import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'motion/react';
import { 
  Play, Upload, Loader2, Sparkles, Image as ImageIcon, CheckCircle, Video, Wand2, Archive,
  Plus, Trash2, Download, RefreshCw, Sliders, Layout, Type, Palette, FileImage, Check,
  Layers, FolderOpen, AlertTriangle, Settings, Cpu, BarChart2, Briefcase, Calendar, 
  Shield, Volume2, Link, Home, Share2, ShoppingBag, Megaphone, Activity, Database, CheckSquare, 
  Clock, AlertCircle, FileText, Eye, Info, ExternalLink, Copy, Lock
} from 'lucide-react';
import VideoAutomationEngine from './VideoAutomationEngine';

interface RenderedVideo {
  id: string;
  url: string;
  prompt: string;
  timestamp: string;
  videoStruct?: any;
  modelConfig?: {
    diffusionSteps: number;
    temporalConsistency: number;
    orbitSpeed: number;
    particleDensity: number;
    matrixColor: string;
    easeSetting: string;
  };
}

interface BulkAssetItem {
  id: string;
  name: string;
  type: 'image' | 'video';
  previewUrl: string;
  prompt: string;
  size: string;
  status: 'pending' | 'generating' | 'completed' | 'failed';
  progress?: number;
  error?: string;
  resultUrl?: string;
}

const DB_NAME = 'NexoraxVideoEngineDB';
const DB_VERSION = 1;
const STORE_NAME = 'video_history';

function openIndexedDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      reject(new Error('IndexedDB is not supported in this environment'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event: any) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

function saveVideoToDB(video: RenderedVideo): Promise<void> {
  return openIndexedDB().then(db => {
    return new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(video);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }).catch(err => {
    console.error('saveVideoToDB error:', err);
  });
}

function getVideosFromDB(): Promise<RenderedVideo[]> {
  return openIndexedDB().then(db => {
    return new Promise<RenderedVideo[]>((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  });
}

function deleteVideoFromDB(id: string): Promise<void> {
  return openIndexedDB().then(db => {
    return new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }).catch(err => {
    console.error('deleteVideoFromDB error:', err);
  });
}

export function VideoGeneratorNode() {
  const [activeTab, setActiveTab] = useState<'CREATE' | 'VAULT' | 'COMPARE' | 'AUTOMATION'>('AUTOMATION');
  const [creationMode, setCreationMode] = useState<'TEXT_TO_VIDEO' | 'IMAGE_TO_VIDEO' | 'EDIT_VIDEO' | 'WORD_CLOUD' | 'BULK_LOAD'>('IMAGE_TO_VIDEO');
  
  const [compareVideoIdA, setCompareVideoIdA] = useState<string>('');
  const [compareVideoIdB, setCompareVideoIdB] = useState<string>('');

  // ==========================================
  // NEXORAX VIDEO AUTOMATION ENGINE PORTAL STATE
  // ==========================================
  const [sessionRole, setSessionRole] = useState<'PUBLIC' | 'USER' | 'CLIENT' | 'ADMIN' | 'SUPERADMIN'>('ADMIN');
  const [activeSection, setActiveSection] = useState<string>('BRAND_ASSETS');

  // BRAND ASSETS STATE
  const [brandAssets, setBrandAssets] = useState({
    company: "NexoraX Digital",
    tagline: "Build Smarter. Scale Faster.",
    primaryLogo: "/logo.png",
    secondaryLogo: "/logo.png",
    phone: "08000 622827",
    primaryEmail: "info@nexoraxdigital.co.uk",
    supportEmail: "support@nexoraxdigital.co.uk",
    websiteUrl: "nexoraxdigital.co.uk",
    themePrimary: "#04060d",
    themeAccent1: "#06b6d4",
    themeAccent2: "#8b5cf6",
    themeHighlight: "#eab308",
    ctaText: "Request Setup",
    footerDisclosure: "NexoraX Digital Ltd is a registered corporate body under SC887095. Direct billing NI NH448796B applies.",
    socialHandles: "@nexoraxdigital",
    voiceoverTone: "Sincere British Director Aura",
    brandIntro: "NexoraX Digital operates elite neural automation pipelines to scale enterprise workflows dynamically."
  });

  // AUTO-GENERATION SCHEDULER STATE
  const [schedulers, setSchedulers] = useState([
    { id: "sch-1", name: "Homepage Hero Cycle", frequency: "Generate 1 homepage video every 7 days", lastRun: "2026-06-15 12:00", nextRun: "2026-06-22 12:00", active: true, outputType: "Homepage Hero Video", mode: "Draft Only" },
    { id: "sch-2", name: "Social Reel Swarm", frequency: "Generate 3 social videos daily", lastRun: "2026-06-19 08:00", nextRun: "2026-06-20 08:00", active: true, outputType: "Social Media Promo Video", mode: "Needs Review" },
    { id: "sch-3", name: "Guides & Videos Builder", frequency: "Generate 1 new guide video weekly", lastRun: "2026-06-12 15:30", nextRun: "2026-06-19 15:30", active: false, outputType: "Guides & Videos Walkthrough Clip", mode: "Draft Only" },
    { id: "sch-4", name: "E-Commerce Highlight", frequency: "Generate 3 product promo videos weekly", lastRun: "2026-06-18 10:00", nextRun: "2026-06-21 10:00", active: true, outputType: "Product Promo Video", mode: "Needs Review" },
    { id: "sch-5", name: "Bumper Logo Refresh", frequency: "Generate 1 promotional bumper weekly", lastRun: "2026-06-14 11:15", nextRun: "2026-06-21 11:15", active: true, outputType: "Transition / Bumper Clip", mode: "Auto Queue" }
  ]);

  // DUPLICATE DIALS AND STATE WARNINGS
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);

  // NEW AUTOMATION ENGINE EXTENDED STATES
  const [activeHomepageHeroId, setActiveHomepageHeroId] = useState<string>("vid-pack-1");
  const [heroOverlayTemplate, setHeroOverlayTemplate] = useState<string>("Cosmic Grid Matrix");
  const [socialQueue, setSocialQueue] = useState([
    { id: "soc-1", videoId: "vid-pack-3", title: "Nexus Search Campaign (Social Vertical Reel)", platform: "Instagram Reel", scheduledTime: "Wednesday 15:00", status: "Scheduled", isPosted: false, caption: "Build on Node-01. Stop building integrations by hand! NexoraX syncs payments instantly. Click link in bio. #SaaS #AI #automation" },
    { id: "soc-2", videoId: "auto-vid-2", title: "Quantum Billing Process (Teaser clip)", platform: "TikTok", scheduledTime: "Wednesday 18:00", status: "Approved", isPosted: false, caption: "Automate accounting splits. Process raw figures instantly under 10% maintenance terms! #fintech #accounting #NexoraX" },
    { id: "soc-3", videoId: "auto-vid-1", title: "Corporate Onboarding Walkthrough", platform: "YouTube Short", scheduledTime: "Thursday 12:00", status: "Draft", isPosted: false, caption: "Zero-Budget Services Guide: Secure prime positions in Google Workspace with direct support! 💎 #hiring #workspace #jobs" }
  ]);

  // AUTOMATION TEMPLATES STATE (REUSABLE AUTOMATION GUIDELINES)
  const [automationTemplates, setAutomationTemplates] = useState([
    {
      id: "t-1",
      name: "Homepage Hero Template",
      sceneCount: 2,
      sceneDuration: "15 seconds",
      textOverlays: "Dynamic neon floating headers, brand watermark top left",
      motionStyle: "Sleek orbital camera paths, infinite loop-safe",
      ctaStyle: "No primary CTA button (looping background only)",
      voiceoverLength: "None",
      subtitleStyle: "None",
      thumbnailStyle: "Minimalistic cosmic layout",
      outputAspectRatio: "16:9",
      recommendedUsage: "Hero banners, background video assets"
    },
    {
      id: "t-2",
      name: "15-Second Company Promo Template",
      sceneCount: 3,
      sceneDuration: "15 seconds",
      textOverlays: "Kinetic uppercase type, electric purple colorways",
      motionStyle: "High-contrast geometric cuts, rapid camera slides",
      ctaStyle: "Overlay display with booking URL & phone matrix",
      voiceoverLength: "15s exact voice trace",
      subtitleStyle: "Loud center subtitles, capitalized font-mono",
      thumbnailStyle: "Aggressive neon close-up with gold border",
      outputAspectRatio: "16:9",
      recommendedUsage: "Quick social ads, feature announcements"
    },
    {
      id: "t-3",
      name: "30-Second Company Promo Template",
      sceneCount: 5,
      sceneDuration: "30 seconds",
      textOverlays: "Stately lower thirds, professional feature slides",
      motionStyle: "Steady focus flythroughs, premium 3D lighting",
      ctaStyle: "Branded outro screen with QR code + phone direct dial",
      voiceoverLength: "30s executive synthesis",
      subtitleStyle: "Subtle bottom margin captions, clean font-sans",
      thumbnailStyle: "Bento grid display with highlighted laptop mockup",
      outputAspectRatio: "16:9",
      recommendedUsage: "Main landing pages, introductory presentations"
    },
    {
      id: "t-4",
      name: "Social Vertical Template",
      sceneCount: 4,
      sceneDuration: "15 seconds",
      textOverlays: "Giant attention hooks, colorful emoji markers",
      motionStyle: "Snappy whip transitions, particle zoom highlights",
      ctaStyle: "Animated hand pointer pointing down, link in bio",
      voiceoverLength: "15s ultra-energetic focus",
      subtitleStyle: "Centered vertical glowing speech bubbles",
      thumbnailStyle: "High-contrast portrait frame, developer face expression",
      outputAspectRatio: "9:16",
      recommendedUsage: "Instagram Reels, TikToks, YouTube Shorts"
    },
    {
      id: "t-5",
      name: "Transition / Bumper Template",
      sceneCount: 1,
      sceneDuration: "4 seconds",
      textOverlays: "Tagline reveal: Build Smarter. Scale Faster.",
      motionStyle: "Logo explosion or cosmic reverse implosion particle sweep",
      ctaStyle: "None",
      voiceoverLength: "None",
      subtitleStyle: "None",
      thumbnailStyle: "Branded vector icon glow",
      outputAspectRatio: "16:9",
      recommendedUsage: "Video intros, outro bumpers, transitions"
    },
    {
      id: "t-6",
      name: "Guide Video Template",
      sceneCount: 3,
      sceneDuration: "20 seconds",
      textOverlays: "Title banner top center, action instructions at bottom",
      motionStyle: "Screencast simulator + neural layout zoom transitions",
      ctaStyle: "Request setup button overlay with active contact phone",
      voiceoverLength: "20s explanatory narrative",
      subtitleStyle: "Contrast black-box background, font-mono styled",
      thumbnailStyle: "Category icon centered on dark purple glass background",
      outputAspectRatio: "16:9",
      recommendedUsage: "Service guides, FAQ walkthroughs"
    },
    {
      id: "t-7",
      name: "Product Review / Product Promo Template",
      sceneCount: 4,
      sceneDuration: "20 seconds",
      textOverlays: "Technical specifications listing, price highlights",
      motionStyle: "3D model orbit sweep, dynamic detail panning",
      ctaStyle: "Buy Now button overlay with live marketplace links",
      voiceoverLength: "20s persuasive customer benefit",
      subtitleStyle: "Bottom third glowing labels",
      thumbnailStyle: "Close-up render of future tech gadget",
      outputAspectRatio: "16:9",
      recommendedUsage: "Marketplace listings, gadgets highlight"
    },
    {
      id: "t-8",
      name: "Future Ad Template",
      sceneCount: 3,
      sceneDuration: "15 seconds",
      textOverlays: "Urgent offer text, limited availability warning",
      motionStyle: "Flickering neon grid background, hyper camera jumps",
      ctaStyle: "Click Link to claim, contact telephone highlighted",
      voiceoverLength: "15s scarcity pitch script",
      subtitleStyle: "Double line glowing headlines",
      thumbnailStyle: "Bright gold background with massive CTA title",
      outputAspectRatio: "16:9",
      recommendedUsage: "Paid campaigns, lead gen drives"
    },
    {
      id: "t-9",
      name: "Testimonial / Case Study Template",
      sceneCount: 3,
      sceneDuration: "15 seconds",
      textOverlays: "Quote blocks, client photo cutout, verified badge",
      motionStyle: "Slow smooth zoom, elegant slide in",
      ctaStyle: "Read Full Report link display",
      voiceoverLength: "15s real client sounding voice",
      subtitleStyle: "Elegant italicised serif line",
      thumbnailStyle: "Client smiling with quote caption",
      outputAspectRatio: "1:1",
      recommendedUsage: "Proof matrices, LinkedIn case feeds"
    },
    {
      id: "t-10",
      name: "Offer / CTA Template",
      sceneCount: 2,
      sceneDuration: "10 seconds",
      textOverlays: "Interactive countdown ticker, discount tier indicator",
      motionStyle: "Pulse beats, quick flashing grid segments",
      ctaStyle: "Giant Book Consultation button",
      voiceoverLength: "10s direct-response action script",
      subtitleStyle: "Huge flashing gold subtitles",
      thumbnailStyle: "Vibrant custom neon gift mockup",
      outputAspectRatio: "16:9",
      recommendedUsage: "Landing page footers, limited closing deals"
    }
  ]);

  // GENERATOR INPUT FORM STATE
  const [formVideoTitle, setFormVideoTitle] = useState("Nexus Search Intelligent Campaign");
  const [formVideoType, setFormVideoType] = useState("Main Company Promo Video");
  const [formVideoGoal, setFormVideoGoal] = useState("Drive leads to standard setup channels");
  const [formTargetAudience, setFormTargetAudience] = useState("SMEs and Enterprise Partners");
  const [formServiceOrProduct, setFormServiceOrProduct] = useState("Nexus Search Node");
  const [formDuration, setFormDuration] = useState("15 seconds");
  const [formAspectRatio, setFormAspectRatio] = useState("16:9");
  const [formTone, setFormTone] = useState("Luxury, Minimalist");
  const [formCta, setFormCta] = useState("Request Setup");
  const [formUseLogo, setFormUseLogo] = useState(true);
  const [formUsePhone, setFormUsePhone] = useState(true);
  const [formUseEmail, setFormUseEmail] = useState(true);
  const [formUseWebsite, setFormUseWebsite] = useState(true);
  const [formUseVoiceover, setFormUseVoiceover] = useState(true);
  const [formUseSubtitles, setFormUseSubtitles] = useState(true);
  const [formUseThumbnail, setFormUseThumbnail] = useState(true);
  const [formScenePrompt, setFormScenePrompt] = useState("A deep obsidian grid tracking shot, purple glass cubes spinning slowly with gold data wireframes, high-end motion");
  const [formNotes, setFormNotes] = useState("Ensure high visibility for the corporate hotline: " + brandAssets.phone);
  const [formPriority, setFormPriority] = useState("High");
  const [formStatus, setFormStatus] = useState("Needs Review");

  // VIDEOS DATABASE (REALISTIC AUTO-GENERATED INITIAL ITEMS)
  const [videosList, setVideosList] = useState([
    {
      id: "vid-01",
      title: "Hero Horizon Cinematic Loop",
      type: "Homepage Hero Video",
      goal: "Provide ambient loop background for the primary hero slot",
      targetAudience: "All Visitors",
      serviceOrProduct: "Corporate Brand Identity",
      duration: "15 seconds",
      aspectRatio: "16:9",
      tone: "Luxury Cinematic",
      cta: "None",
      useLogo: true,
      useWebsite: true,
      usePhone: false,
      useEmail: false,
      useVoiceover: false,
      useSubtitles: false,
      useThumbnail: true,
      scenePrompt: "Sleek camera pan over rotating purple glass obelisks and glowing orbital wireframes on deep black ground.",
      notes: "Default homepage hero slot. Fully loop-safe.",
      priority: "Critical",
      status: "Published",
      pagePlacement: "Homepage Hero",
      category: "homepage-hero",
      views: 12450,
      createdAt: "2026-06-15 14:22",
      script: {
        hook: "Welcome to NexoraX Digital: out-of-this-galaxy system execution.",
        problem: "Traditional systems operate with significant lag.",
        solution: "Enter of the elite 0.8s standard grid system.",
        benefit: "Accelerate your workflows beyond traditional dimensions.",
        cta: "Request setup instantly."
      },
      voiceoverScript: "(Ambient cinematic hum plays) ... NexoraX Digital. Scale faster.",
      subtitleText: "NEXORAX DIGITAL // SCALE FASTER",
      thumbnailText: "OUT OF THIS GALAXY SYSTEM PIPELINE",
      thumbnailPrompt: "A close up shoot of glowing 3D spheres on deep onyx wireframe map, high-end cinematic graphics",
      suggestedFilename: "nexorax_hero_cinematic_loop.mp4",
      videoUrl: "/brain1.mp4"
    },
    {
      id: "vid-03",
      title: "Vertical TikTok Promo Reel",
      type: "Social Media Vertical Video",
      goal: "Generate social virality for automated lead generation",
      targetAudience: "Indie Developers & Small business Owners",
      serviceOrProduct: "Social Automation Engine",
      duration: "15 seconds",
      aspectRatio: "9:16",
      tone: "Energetic Creator",
      cta: "Launch with NexoraX",
      useLogo: true,
      useWebsite: true,
      usePhone: false,
      useEmail: false,
      useVoiceover: true,
      useSubtitles: true,
      useThumbnail: true,
      scenePrompt: "A sleek smart phone in landscape view showing dynamic digital dashboards, fluid animations, 3D overlays.",
      notes: "Scheduled for Instagram and TikTok post.",
      priority: "Medium",
      status: "Approved",
      pagePlacement: "Social Media Content Queue",
      category: "social-media",
      views: 0,
      createdAt: "2026-06-18 17:45",
      script: {
        hook: "Stop building custom database integrations by hand!",
        problem: "Writing boilerplates and debugging APIs is draining your creative focus.",
        solution: "NexoraX is the ultimate autonomous platform that compiles custom apps in seconds.",
        benefit: "Launch ideas immediately and trigger automated revenue.",
        cta: "Link in bio to set up."
      },
      voiceoverScript: "Stop building integrations! NexoraX compiles custom dashboards in seconds. Follow the bio link.",
      subtitleText: "STREAMS DELAYED? // NEXORAX AGENTS // LAUNCH SECURE NOW",
      thumbnailText: "BUILD SAAS IN 0.8 SECONDS FLAT",
      thumbnailPrompt: "A glowing futuristic developer desk with laser blueprint keyboard and neon floating screen",
      suggestedFilename: "developers_cheatcode_vertical.mp4",
      videoUrl: "/brain1.mp4"
    },
    {
      id: "vid-04",
      title: "Transition Bumper Reveal",
      type: "Transition / Bumper Clip",
      goal: "Establish micro-branding reveal between main contents",
      targetAudience: "All Viewers",
      serviceOrProduct: "Corporate Tagline",
      duration: "3 sec",
      aspectRatio: "16:9",
      tone: "Minimalist Impact",
      cta: "None",
      useLogo: true,
      useWebsite: true,
      usePhone: false,
      useEmail: false,
      useVoiceover: false,
      useSubtitles: false,
      useThumbnail: false,
      scenePrompt: "Logo elements gathering as cyan sparks and resolving into central NexoraX digital symbol with nice gold shine.",
      notes: "Used in playlist transitions.",
      priority: "Low",
      status: "Published",
      pagePlacement: "Promo Library",
      category: "bumper",
      views: 45000,
      createdAt: "2026-06-14 10:00",
      script: {
        hook: "NexoraX: Build Smarter. Scale Faster.",
        problem: "N/A",
        solution: "N/A",
        benefit: "N/A",
        cta: "N/A"
      },
      voiceoverScript: "",
      subtitleText: "BUILD SMARTER. SCALE FASTER.",
      thumbnailText: "NEXORAX DIGITAL BRAND BUMPER",
      thumbnailPrompt: "Exploding neon particles forming a crown sphere logo, 4k resolution, deep contrast",
      suggestedFilename: "nexorax_bumper_3s.mp4",
      videoUrl: "/brain2.mp4"
    },
    {
      id: "vid-05",
      title: "System Welcome Orientation Video",
      type: "Guides & Videos Walkthrough Clip",
      goal: "Welcome new operators and introduce workspace dashboard",
      targetAudience: "Newly Registered Clients",
      serviceOrProduct: "NexoraX Workspace Guide",
      duration: "15 seconds",
      aspectRatio: "16:9",
      tone: "Friendly Professional",
      cta: "Request Setup",
      useLogo: true,
      useWebsite: true,
      usePhone: true,
      useEmail: true,
      useVoiceover: true,
      useSubtitles: true,
      useThumbnail: true,
      scenePrompt: "Interactive floating wireframe interface showcasing bento workspace components changing state.",
      notes: "Assigned as primary Welcome walkthrough.",
      priority: "Critical",
      status: "Published",
      pagePlacement: "Guides & Videos page",
      category: "guides-videos",
      guideId: "welcome-to-nexorax",
      views: 3450,
      createdAt: "2026-06-15 11:30",
      script: {
        hook: "Welcome to your high-fidelity NexoraX Workspace control panel.",
        problem: "Traditional portals overwhelm administrators with clutter.",
        solution: "Our streamlined grid segregates system nodes, CRM lists, and billing ledgers.",
        benefit: "Track key operations in real-time with sub-second lag parameters.",
        cta: "Need assistance? Call " + brandAssets.phone + " immediately."
      },
      voiceoverScript: "Welcome to NexoraX Workspace. Our streamlined grid segregates billing and CRM lists. Live support is active.",
      subtitleText: "NEXORAX WORKSPACE SETUP // INTEGRATION TUTORIAL",
      thumbnailText: "WELCOME TO THE NEXUS CORE",
      thumbnailPrompt: "Clean 3D UI control glass sheet floating over cybergrid coordinates, neon turquoise highlights",
      suggestedFilename: "welcome_guide_overview.mp4",
      videoUrl: "/brain2.mp4"
    },
    {
      id: "vid-06",
      title: "Nexus Search Intelligent Guide",
      type: "Guides & Videos Walkthrough Clip",
      goal: "Demonstrate Nexus Search indexing features",
      targetAudience: "Marketing Teams & Webmasters",
      serviceOrProduct: "Nexus Search",
      duration: "15 seconds",
      aspectRatio: "16:9",
      tone: "Technical Insightful",
      cta: "Start Your Project",
      useLogo: true,
      useWebsite: true,
      usePhone: false,
      useEmail: false,
      useVoiceover: true,
      useSubtitles: true,
      useThumbnail: true,
      scenePrompt: "Laser light scanning virtual network nodes in spherical grid with search index symbols displaying.",
      notes: "Awaiting published status review.",
      priority: "High",
      status: "Needs Review",
      pagePlacement: "Guides & Videos page",
      category: "guides-videos",
      guideId: "nexus-search-guide",
      views: 0,
      createdAt: "2026-06-19 09:12",
      script: {
        hook: "Uncover hidden datasets with multi-dimensional global indexing.",
        problem: "Standard engines skip contextual sitemap shifts and metadata details.",
        solution: "Nexus Search captures actual database sitemap shifts in 0.6 seconds.",
        benefit: "Complete awareness of competitor shifts and lead signals.",
        cta: "Configure coordinates at nexoraxdigital.co.uk."
      },
      voiceoverScript: "Uncover hidden datasets. Nexus Search captures actual database sitemap shifts in 0.6 seconds.",
      subtitleText: "NEXUS SEARCH NODE // THE QUANTUM INDEX",
      thumbnailText: "NEXUS SEARCH SETUP PROTOCOL",
      thumbnailPrompt: "Abstract neon magnifying glass hovering over complex digital molecular grid",
      suggestedFilename: "nexus_search_indexing_guide.mp4",
      videoUrl: "/brain1.mp4"
    },
    {
      id: "vid-07",
      title: "Nexus Hosting Command Guide",
      type: "Guides & Videos Walkthrough Clip",
      goal: "Demonstrate bare-metal secure hosting structure",
      targetAudience: "Sysadmins & Technical leads",
      serviceOrProduct: "Nexus Hosting",
      duration: "15 seconds",
      aspectRatio: "16:9",
      tone: "Highly Technical",
      cta: "Contact NexoraX",
      useLogo: true,
      useWebsite: true,
      usePhone: true,
      useEmail: false,
      useVoiceover: true,
      useSubtitles: true,
      useThumbnail: true,
      scenePrompt: "Cold silver server rack with active cooling neon fluids humming silently inside server vault.",
      notes: "Waiting for render pipeline execution.",
      priority: "High",
      status: "Waiting for Render",
      pagePlacement: "Guides & Videos page",
      category: "guides-videos",
      guideId: "nexus-hosting-guide",
      views: 0,
      createdAt: "2026-06-19 10:45",
      script: {
        hook: "Host core system engines on high-frequency German bare metal nodes.",
        problem: "Shared clusters suffer from latency spikes and security threat penetrations.",
        solution: "Nexus Hosting binds dedicated containers inside private virtual arrays.",
        benefit: "Enjoy constant 100% SLA uptime and sub-second routing delivery.",
        cta: "Get direct spec sheets by dialing " + brandAssets.phone + "."
      },
      voiceoverScript: "Host system engines on dedicated bare-metal. Nexus Hosting secures 100% SLA uptime. Contact technical nodes.",
      subtitleText: "CO-LOCATION HOSTING // NEXUS CLUSTER SYNC",
      thumbnailText: "BARE-METAL HOSTING MANUAL",
      thumbnailPrompt: "Heavy industrial server module with copper tubing glowing with purple status diodes",
      suggestedFilename: "nexus_hosting_bare_metal_setup.mp4",
      videoUrl: "/brain2.mp4"
    },
    {
      id: "vid-08",
      title: "Marketplace Holo Frame Highlight",
      type: "Product Promo Video",
      goal: "Marketplace product display advertising clip",
      targetAudience: "Marketplace Shoppers",
      serviceOrProduct: "Holographic Glass Display",
      duration: "15 seconds",
      aspectRatio: "16:9",
      tone: "Futuristic Consumer",
      cta: "Request Setup",
      useLogo: true,
      useWebsite: true,
      usePhone: true,
      useEmail: false,
      useVoiceover: true,
      useSubtitles: true,
      useThumbnail: true,
      scenePrompt: "Elegant glass panel displaying glowing floating data blocks in front of blurred luxury desk setup.",
      notes: "Product promo highlighting future tech features.",
      priority: "Medium",
      status: "Needs Review",
      pagePlacement: "Marketplace",
      category: "product-promo",
      views: 0,
      createdAt: "2026-06-18 11:20",
      script: {
        hook: "Convert standard flat documents into beautiful floating 3D spatial projections.",
        problem: "Static screens hide crucial metric dimensions and look outdated.",
        solution: "The Holographic Glass Frame renders live database feeds as glowing matrix volumes.",
        benefit: "Transform meetings into a premium visual command center experience.",
        cta: "Claim your hardware prototype at nexoraxdigital.co.uk."
      },
      voiceoverScript: "Render flat datasheets into floating 3D projections. The Holographic Glass Frame transforms meeting logs.",
      subtitleText: "SPATIAL QUANTUM FRAME // HARDWARE MATRIX",
      thumbnailText: "HOLO DISPLAY HARDWARE SETUP",
      thumbnailPrompt: "A transparent thin glass sheet displaying colorful glowing geometric graphics with beautiful neon flares",
      suggestedFilename: "holo_glass_marketplace_promo.mp4",
      videoUrl: "/brain1.mp4"
    },
    {
      id: "vid-09",
      title: "Ad Creative - Automated Wealth Loop",
      type: "Ad Creative Video",
      goal: "Direct response campaign for fiscal strike solution",
      targetAudience: "Business Owners & CFOs",
      serviceOrProduct: "Dynamic Billing Ledger System",
      duration: "15 seconds",
      aspectRatio: "16:9",
      tone: "Executive Financial",
      cta: "Book Consultation",
      useLogo: true,
      useWebsite: true,
      usePhone: true,
      useEmail: true,
      useVoiceover: true,
      useSubtitles: true,
      useThumbnail: true,
      scenePrompt: "A sleek gold coins cascade falling inside glowing neon tube that leads into secure digital terminal database.",
      notes: "Draft only - strictly requires Director review before deployment.",
      priority: "Critical",
      status: "Draft",
      pagePlacement: "Ad Draft Library",
      category: "ad-creative",
      views: 0,
      createdAt: "2026-06-19 10:15",
      script: {
        hook: "Is your legacy billing framework letting loose unmonetized transactions?",
        problem: "Accounting delays and manual errors leak valuable margin capital every week.",
        solution: "Node-07 Quantum Billing executes automated 1% Levy community splits with sub-second clearance.",
        benefit: "Recover liquidity issues and secure settlements via validated payment lines.",
        cta: "Secure direct onboarding slot on " + brandAssets.phone + "."
      },
      voiceoverScript: "Is legacy billing leaking capital? Node-07 Quantum Billing executes automated splits. Direct dial " + brandAssets.phone + ".",
      subtitleText: "[0:01] Revenue leakage? [0:05] Quantum Ledger Node [0:10] Automated settlements",
      thumbnailText: "ELITE CAPITAL PIPELINE UPGRADE",
      thumbnailPrompt: "Onyx golden scales balanced perfectly with glowing currency nodes on glass platform",
      suggestedFilename: "quantum_billing_ad_v1_draft.mp4",
      videoUrl: "/brain2.mp4"
    }
  ]);

  // RENDER SIMULATION & DIAGNOSTICS CONSOLE
  const [consoleIsRunning, setConsoleIsRunning] = useState(false);
  const [consoleLogs, setConsoleLogs] = useState<string[]>([]);
  const [consoleProgress, setConsoleProgress] = useState(0);
  const [consoleHeader, setConsoleHeader] = useState("");

  // AUDIT LOGS FOR OPERATIONS
  const [auditLogs, setAuditLogs] = useState([
    { id: "log-1", time: "10:45:12", user: "Lead Director", action: "Authorized brand assets database sync.", status: "SUCCESS" },
    { id: "log-2", time: "10:12:00", user: "System Scheduler", action: "Triggered Daily Reel Campaign checks.", status: "SYNCED" },
    { id: "log-3", time: "09:42:15", user: "Claire (Private Sec)", action: "Generated Nexus Search Guide walk-through.", status: "DRAFT" },
    { id: "log-4", time: "07:30:23", user: "Staff Coordinator", action: "Reviewed social queue platforms.", status: "APPROVED" }
  ]);
  
  const [prompt, setPrompt] = useState('Cinematic 4k tracking shot, high quality, neon cyberpunk aesthetics, photorealistic');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageUrl, setImageUrlFromInput] = useState('');

  // Bulk Load Specific State Variables
  const [bulkItems, setBulkItems] = useState<BulkAssetItem[]>([]);
  const [bulkGlobalPrompt, setBulkGlobalPrompt] = useState('Cinematic tracking shot, 4k ultra-high resolution, hyper-detailed, neon cyberpunk aesthetics, Nexorax premium alignment');
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [bulkLog, setBulkLog] = useState<string[]>([]);
  const bulkFileInputRef = useRef<HTMLInputElement>(null);

  // NexoraX Word Cloud State Variables
  const [wordCloudWords, setWordCloudWords] = useState<Array<{ text: string; weight: number }>>([
    { text: "execution", weight: 75 },
    { text: "digital", weight: 63 },
    { text: "nexorax", weight: 51 },
    { text: "thought", weight: 39 }
  ]);

  const [wordCloudFont, setWordCloudFont] = useState<'sans-serif' | 'serif' | 'monospace'>('monospace');
  const [wordCloudPalette, setWordCloudPalette] = useState<'warm' | 'cyber' | 'gold' | 'silver'>('gold');
  const [wordCloudBackground, setWordCloudBackground] = useState<'transparent' | 'light' | 'dark'>('dark');
  const [wordCloudRotation, setWordCloudRotation] = useState<'horizontal' | 'balanced' | 'crossed' | 'vertical'>('horizontal');
  const [wordCloudMaxWords, setWordCloudMaxWords] = useState<number>(32);
  const [wordCloudMinSize, setWordCloudMinSize] = useState<number>(12);
  const [wordCloudMaxSize, setWordCloudMaxSize] = useState<number>(58);
  const [bulkText, setBulkText] = useState<string>("NEXORAX DIGITAL OS\nTHOUGHT OR EXECUTION");
  const [newWordText, setNewWordText] = useState<string>("");
  const [newWordWeight, setNewWordWeight] = useState<number>(10);
  const [renderCloudTrigger, setRenderCloudTrigger] = useState<number>(0);
  
  const wordCloudCanvasRef = useRef<HTMLCanvasElement>(null);
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [operationName, setOperationName] = useState<string | null>(null);
  const [statusText, setStatusText] = useState('');
  
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [playbackFailed, setPlaybackFailed] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState<string>('');
  
  const [vault, setVault] = useState<RenderedVideo[]>([
    {
      id: 'VEO-IMPERIAL-01',
      url: '/brain1.mp4',
      prompt: 'Cinematic slow motion tracking shot of Nexorax imperial neural core network showing holographic glowing golden terminals, futuristic server racks, complex 3D orbital rings, warm lighting',
      timestamp: new Date(Date.now() - 3600000 * 4).toISOString(),
      modelConfig: {
        diffusionSteps: 90,
        temporalConsistency: 95,
        orbitSpeed: 3.5,
        particleDensity: 300,
        matrixColor: 'amber',
        easeSetting: 'EASE_IN_OUT'
      }
    },
    {
      id: 'VEO-CYBER-02',
      url: '/brain2.mp4',
      prompt: 'Hyper-detailed first person flythrough inside a high-contrast neon cobalt tunnel with digital lines floating in endless parallax, quantum coordinates listing, cyberpunk high latency wireframe',
      timestamp: new Date(Date.now() - 3600000 * 2).toISOString(),
      modelConfig: {
        diffusionSteps: 120,
        temporalConsistency: 98,
        orbitSpeed: 5.0,
        particleDensity: 400,
        matrixColor: 'cyan',
        easeSetting: 'LINEAR'
      }
    },
    {
      id: 'VEO-COSMIC-03',
      url: '/brain4.mp4',
      prompt: 'Ultra realistic volumetric space simulation of a dying white dwarf star collapsing into beautiful swirling crimson cosmic plasma storm waves, starry background, high dynamic range cinematic',
      timestamp: new Date(Date.now() - 3600000 * 0.5).toISOString(),
      modelConfig: {
        diffusionSteps: 80,
        temporalConsistency: 90,
        orbitSpeed: 2.0,
        particleDensity: 200,
        matrixColor: 'rose',
        easeSetting: 'EASE_OUT'
      }
    }
  ]);

  const [activeVideoObject, setActiveVideoObject] = useState<RenderedVideo | null>(null);
  const [synthesisEaseSetting, setSynthesisEaseSetting] = useState<'LINEAR' | 'EASE_IN' | 'EASE_OUT' | 'EASE_IN_OUT'>('LINEAR');
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [isHighlighterMode, setIsHighlighterMode] = useState(false);
  const [lineWidth, setLineWidth] = useState(20);
  const maskCanvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  // Hyper-Dimensional Quantum Auto-Tuning Co-Engine States
  const [isAutoTuned, setIsAutoTuned] = useState(true);
  const [isTuningCalibrationInProgress, setIsTuningCalibrationInProgress] = useState(false);
  const [alignmentIndex, setAlignmentIndex] = useState(99.998);
  const [chronosLatency, setChronosLatency] = useState(0.8);
  const [tuneStatusLog, setTuneStatusLog] = useState<string>('NEXORAX VEO-ENGINE CALIBRATED ON ANDROMEDA MATRIX');

  // NexoraX Logo Watermark Controls (Dynamic Embedding & Overlays)
  const [embedWatermark, setEmbedWatermark] = useState(true);
  const [watermarkPosition, setWatermarkPosition] = useState<'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'>('top-left');
  const [watermarkOpacity, setWatermarkOpacity] = useState(0.85);
  const [watermarkSize, setWatermarkSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [isCompilingWebm, setIsCompilingWebm] = useState(false);
  const [compilationProgress, setCompilationProgress] = useState(0);

  // Next-Gen Out-of-this-Universe Interactive Neural Video Synthesizer States
  const [customLogoUrl, setCustomLogoUrl] = useState<string | null>(null);
  const [customLogoName, setCustomLogoName] = useState<string | null>(null);
  const [synthesisOrbitSpeed, setSynthesisOrbitSpeed] = useState<number>(3.5);
  const [synthesisParticleDensity, setSynthesisParticleDensity] = useState<number>(250);
  const [synthesisDiffusionSteps, setSynthesisDiffusionSteps] = useState<number>(80);
  const [synthesisTemporalConsistency, setSynthesisTemporalConsistency] = useState<number>(92);
  const [synthesisChromaticShift, setSynthesisChromaticShift] = useState<number>(20);
  const [synthesisMatrixColor, setSynthesisMatrixColor] = useState<'cyan' | 'amber' | 'pink' | 'emerald' | 'rose'>('cyan');
  const [isSimulatingSynthesis, setIsSimulatingSynthesis] = useState<boolean>(false);
  const synCanvasRef = useRef<HTMLCanvasElement>(null);
  const logoImageRef = useRef<HTMLImageElement | null>(null);

  // Autoload Nexorax Logo directly into the Video seed frame on mount
  const getPaletteColor = (text: string, index: number, palette: string) => {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
       hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }
    const idx = Math.abs(hash) % 12;
    
    if (palette === 'warm') {
      const colors = ["#5c1010", "#8c1d1d", "#be2e2e", "#d35400", "#e67e22", "#d68910", "#5c3a21", "#78281f", "#115e59", "#334155", "#1e293b", "#7c2d12"];
      return colors[idx % colors.length];
    } else if (palette === 'cyber') {
      const colors = ["#00f0ff", "#ff007f", "#bc3cfd", "#7000ff", "#00ffcc", "#3b82f6", "#6366f1", "#ec4899", "#14b8a6", "#a855f7", "#f43f5e", "#10b981"];
      return colors[idx % colors.length];
    } else if (palette === 'gold') {
      const colors = ["#d4af37", "#b7950b", "#f39c12", "#e59866", "#78281f", "#1a252f", "#cd7f32", "#eaecee", "#f5b041", "#873600", "#7c2d12", "#b45309"];
      return colors[idx % colors.length];
    } else { // silver
      const colors = ["#334155", "#475569", "#64748b", "#94a3b8", "#cbd5e1", "#f1f5f9", "#0f172a", "#1e293b", "#38bdf8", "#0284c7", "#ae9e95", "#0a0a0a"];
      return colors[idx % colors.length];
    }
  };

  const drawWordCloud = () => {
    const canvas = wordCloudCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear and draw background
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (wordCloudBackground === 'light') {
       ctx.fillStyle = '#fdfbf7'; // Warm off-white matching screenshot
       ctx.fillRect(0, 0, canvas.width, canvas.height);
    } else if (wordCloudBackground === 'dark') {
       ctx.fillStyle = '#0a0f1d'; // Deep metallic slate background
       ctx.fillRect(0, 0, canvas.width, canvas.height);
    } // Transparent leaves it clear

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    const sortedWords = [...wordCloudWords]
       .sort((a, b) => b.weight - a.weight)
       .slice(0, wordCloudMaxWords);

    if (sortedWords.length === 0) return;

    const placedBoxes: Array<{ x: number; y: number; w: number; h: number }> = [];

    const maxWeight = Math.max(...sortedWords.map(w => w.weight)) || 1;
    const minWeight = Math.min(...sortedWords.map(w => w.weight)) || 1;

    const fontFamily = wordCloudFont === 'serif' ? 'Georgia, serif' : wordCloudFont === 'monospace' ? '"JetBrains Mono", monospace' : 'Inter, sans-serif';

    sortedWords.forEach((word, idx) => {
       const weightRatio = maxWeight === minWeight ? 0.5 : (word.weight - minWeight) / (maxWeight - minWeight);
       // Linear interpolation for font size with custom bounds
       const fontSize = Math.floor(wordCloudMinSize + weightRatio * (wordCloudMaxSize - wordCloudMinSize));
       const fontWeight = fontSize > 36 ? 'bold' : 'normal';

       ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
       const textWidth = ctx.measureText(word.text).width;
       const textHeight = fontSize;

       let theta = 0;
       const step = 0.12; 

       // Spiral boundary/intersection algorithm loop
       for (let attempts = 0; attempts < 1200; attempts++) {
          const r = 5.2 * theta;
          const xOffset = r * Math.cos(theta);
          const yOffset = r * Math.sin(theta) * 0.70; // slight squeeze for wide display matrix proportions
          
          const x = centerX + xOffset - textWidth / 2;
          const y = centerY + yOffset + textHeight / 3;

          // Compute horizontal/vertical orientation
          let isVertical = false;
          if (wordCloudRotation === 'balanced' && idx > 0 && (idx % 6 === 0)) isVertical = true;
          if (wordCloudRotation === 'crossed' && idx > 0 && (idx % 2 === 0)) isVertical = true;
          if (wordCloudRotation === 'vertical') isVertical = true;

          const w = isVertical ? textHeight : textWidth;
          const h = isVertical ? textWidth : textHeight;

          // Collision detection check
          let overlap = false;
          const cushion = 4; // micro spacing cushion
          for (let i = 0; i < placedBoxes.length; i++) {
             const b = placedBoxes[i];
             if (
                x < b.x + b.w + cushion &&
                x + w + cushion > b.x &&
                y - h < b.y + cushion &&
                y + cushion > b.y - b.h
             ) {
                overlap = true;
                break;
             }
          }

          if (!overlap) {
             ctx.save();
             ctx.translate(x + w / 2, y - h / 2);
             if (isVertical) {
                ctx.rotate(Math.PI / 2);
             }
             ctx.fillStyle = getPaletteColor(word.text, idx, wordCloudPalette);
             ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
             ctx.textAlign = 'center';
             ctx.textBaseline = 'middle';
             ctx.fillText(word.text, 0, 0);
             ctx.restore();

             placedBoxes.push({ x, y, w, h });
             break;
          }

          theta += step;
       }
    });

    // Add immediate branding overlay to the generated word cloud
    ctx.save();
    ctx.fillStyle = wordCloudBackground === 'light' ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.1)';
    ctx.font = '9px monospace';
    ctx.fillText("NEXORAX NEXORAX VIDEO FORGE // WORD-CLOUD DESIGN CORE", 15, canvas.height - 15);
    ctx.restore();
  };

  useEffect(() => {
    if (creationMode === 'WORD_CLOUD') {
      const timer = setTimeout(() => {
        drawWordCloud();
      }, 60);
      return () => clearTimeout(timer);
    }
  }, [wordCloudWords, wordCloudFont, wordCloudPalette, wordCloudBackground, wordCloudRotation, wordCloudMaxWords, wordCloudMinSize, wordCloudMaxSize, renderCloudTrigger, creationMode]);

  const loadPreset = (presetName: 'screenshot' | 'nexorax' | 'tech') => {
    if (presetName === 'screenshot') {
      setWordCloudFont('sans-serif');
      setWordCloudPalette('warm');
      setWordCloudBackground('light');
      setWordCloudRotation('balanced');
      setWordCloudMaxWords(45);
      setWordCloudMinSize(10);
      setWordCloudMaxSize(64);
      setWordCloudWords([
        { text: "paradigm shift", weight: 95 },
        { text: "change", weight: 80 },
        { text: "different", weight: 70 },
        { text: "innovation", weight: 65 },
        { text: "unique", weight: 62 },
        { text: "rethink", weight: 60 },
        { text: "fundamental", weight: 58 },
        { text: "adaptation", weight: 55 },
        { text: "disrupt", weight: 52 },
        { text: "strategy", weight: 50 },
        { text: "innovate", weight: 48 },
        { text: "future", weight: 46 },
        { text: "concept", weight: 44 },
        { text: "improve", weight: 42 },
        { text: "creative", weight: 40 },
        { text: "business", weight: 38 },
        { text: "science", weight: 35 },
        { text: "evolution", weight: 33 },
        { text: "new", weight: 32 },
        { text: "challenge", weight: 31 },
        { text: "knowledge", weight: 30 },
        { text: "creativity", weight: 28 },
        { text: "thinking", weight: 26 },
        { text: "progress", weight: 24 },
        { text: "motivation", weight: 22 },
        { text: "practice", weight: 19 },
        { text: "game", weight: 18 },
        { text: "scientific", weight: 17 },
        { text: "response", weight: 16 },
        { text: "inspiration", weight: 15 },
        { text: "evolving", weight: 14 },
        { text: "upheaval", weight: 13 },
        { text: "dissimilar", weight: 12 },
        { text: "solution", weight: 11 },
        { text: "shifting", weight: 10 },
        { text: "adapting", weight: 9 },
        { text: "diverging", weight: 8 },
        { text: "approach", weight: 7 },
        { text: "vision", weight: 6 },
        { text: "leap", weight: 5 },
      ]);
      setTuneStatusLog("SCREENSHOT PARADIGM SHIFT TEMPLATE INSTANTIATED SUCCESSFULLY");
    } else if (presetName === 'nexorax') {
      setWordCloudFont('monospace');
      setWordCloudPalette('gold');
      setWordCloudBackground('dark');
      setWordCloudRotation('horizontal');
      setWordCloudMaxWords(32);
      setWordCloudMinSize(12);
      setWordCloudMaxSize(58);
      setWordCloudWords([
        { text: "NexoraX", weight: 100 },
        { text: "Director", weight: 85 },
        { text: "Lead Director", weight: 80 },
        { text: "NexoraX Node", weight: 75 },
        { text: "Secure Payment Provider Reserves", weight: 70 },
        { text: "Levy Protocol 1%", weight: 65 },
        { text: "Tax Capture 20%", weight: 60 },
        { text: "NexoraX billing records", weight: 55 },
        { text: "Resource resource", weight: 50 },
        { text: "NexoraX Resource Library", weight: 45 },
        { text: "Antigravity Stack", weight: 45 },
        { text: "Pakistan Node", weight: 40 },
        { text: "NexoraX AI", weight: 35 },
        { text: "CloudSync-02", weight: 30 },
        { text: "Spectacle Command", weight: 25 },
        { text: "Veo-3.1 Core", weight: 20 },
      ]);
      setTuneStatusLog("MANDATORY NEXORAX IMPERIAL PROTOCOL MATRIX LOADED");
    } else if (presetName === 'tech') {
      setWordCloudFont('sans-serif');
      setWordCloudPalette('cyber');
      setWordCloudBackground('dark');
      setWordCloudRotation('crossed');
      setWordCloudMaxWords(35);
      setWordCloudMinSize(12);
      setWordCloudMaxSize(60);
      setWordCloudWords([
        { text: "Veo-3.1 Forge", weight: 95 },
        { text: "Neural Core", weight: 85 },
        { text: "Generative Motion", weight: 80 },
        { text: "Hyper-Resolution", weight: 75 },
        { text: "AI Synthesis", weight: 70 },
        { text: "Pixel Grid", weight: 65 },
        { text: "Model Weights", weight: 60 },
        { text: "Vector Alignment", weight: 55 },
        { text: "Frame Seed", weight: 50 },
        { text: "Spatial Layout", weight: 45 },
        { text: "Temporal Focus", weight: 40 },
        { text: "Cinematic Overdrive", weight: 35 },
        { text: "Render Engine", weight: 30 },
      ]);
      setTuneStatusLog("DEEP TECH NEURAL MATRIX PRESET LOADED");
    }
    setRenderCloudTrigger(prev => prev + 1);
  };

  const downloadWordCloudPNG = () => {
    const canvas = wordCloudCanvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = `Nexorax_NexoraX_WordCloud_${Date.now()}.png`;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTuneStatusLog("EXCEPTIONAL HIGH-RES PNG EXPORT COMPLETED SUCCESSFULLY");
  };

  const seedToVideoEngine = () => {
    const canvas = wordCloudCanvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    
    // Save image into the main app state
    setImagePreview(dataUrl);
    
    // Convert base64 dataURI into a File
    const byteString = atob(dataUrl.split(',')[1]);
    const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: mimeString });
    const file = new File([blob], "wordcloud_generated.png", { type: "image/png" });
    
    setImageFile(file);
    setImageUrlFromInput('');
    
    // Automatically set the motion directive prompt targeting animating this word cloud
    setPrompt(`A cinematic animated slow pan of a beautifully generated tactical word cloud centering '${wordCloudWords[0]?.text || "PARADIGM SHIFT"}'. The words are illuminated by glowing embers, slowly rotating in 3D parallax space, volumetric fog, ultra photorealistic, 4k resolution.`);
    
    // Swap back to IMAGE_TO_VIDEO mode
    setCreationMode('IMAGE_TO_VIDEO');
    setTuneStatusLog("WORD CLOUD SEEDED SECURELY AS SEED FRAME FOR VIDEO RENDERING");
  };

  // PERSISTENCE ENGINE FOR BRAND ASSETS AND VIDEOS LISTS
  useEffect(() => {
    try {
      const savedAssets = localStorage.getItem("nexorax_brand_assets");
      if (savedAssets) {
        setBrandAssets(JSON.parse(savedAssets));
      }
    } catch (e) {
      console.error("Failed to restore brand assets from local storage", e);
    }
  }, []);

  useEffect(() => {
    try {
      const savedVideos = localStorage.getItem("nexorax_videos_list");
      if (savedVideos) {
        setVideosList(JSON.parse(savedVideos));
      }
    } catch (e) {
      console.error("Failed to restore videos list from local storage", e);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("nexorax_brand_assets", JSON.stringify(brandAssets));
    } catch (e) {
      console.error("Failed to persist brand assets", e);
    }
  }, [brandAssets]);

  useEffect(() => {
    try {
      localStorage.setItem("nexorax_videos_list", JSON.stringify(videosList));
    } catch (e) {
      console.error("Failed to persist videos list", e);
    }
  }, [videosList]);

  const handleBulkImport = () => {
    if (!bulkText.trim()) return;
    
    const stopwords = new Set([
      "the", "a", "of", "and", "in", "to", "for", "is", "on", "it", "that", "this", "with", "as", "by", "an", "at", "are", "be", "from", "or", "your", "we", "our", "us", "i", "you", "they", "them", "he", "she", "it's", "its", "have", "has", "had", "will", "would", "can", "could", "should", "but", "about", "who", "which", "there", "their", "more", "about", "other", "some", "was", "were", "been", "do", "does", "did", "doing", "my", "me", "so", "go", "if", "no", "os"
    ]);

    const tokens = bulkText
      .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?"\r\n]/g, " ")
      .split(/\s+/)
      .map(w => w.trim().toLowerCase())
      .filter(w => w.length >= 2 && !stopwords.has(w));

    const freq: Record<string, number> = {};
    tokens.forEach(tok => {
      freq[tok] = (freq[tok] || 0) + 1;
    });

    const getPriority = (txt: string) => {
      if (txt === "execution") return 5;
      if (txt === "digital") return 4;
      if (txt === "nexorax") return 3;
      if (txt === "thought") return 2;
      if (txt === "os") return 1;
      return 0;
    };

    const sortedEntries = Object.entries(freq).sort((a, b) => {
      // Sort primarily by frequency (count) descending
      if (b[1] !== a[1]) return b[1] - a[1];
      // Secondarily by custom priority descending
      const prioA = getPriority(a[0]);
      const prioB = getPriority(b[0]);
      if (prioB !== prioA) return prioB - prioA;
      // Tertiarily by length descending
      if (b[0].length !== a[0].length) return b[0].length - a[0].length;
      // Finally alphabetically
      return a[0].localeCompare(b[0]);
    });

    const wordList = sortedEntries.map(([text, count], idx) => {
      // Calculate staggered hierarchical weights matching the screenshot proportions
      const rankBonus = Math.max(5, 60 - idx * 12);
      return {
        text,
        weight: count * 15 + rankBonus
      };
    });

    if (wordList.length > 0) {
      setWordCloudWords(wordList);
      setRenderCloudTrigger(prev => prev + 1);
      setTuneStatusLog(`AUTOMATED CORP MATRIX INGESTED: ${wordList.length} SOPHISTICATED TERMS APPLIED`);
    }
  };

  const addIndividualWord = () => {
    if (!newWordText.trim()) return;
    const wordClean = newWordText.trim().toLowerCase();
    
    // Check if word already exists, if so modify weight
    const exists = wordCloudWords.some(w => w.text.toLowerCase() === wordClean);
    if (exists) {
      setWordCloudWords(wordCloudWords.map(w => 
        w.text.toLowerCase() === wordClean 
          ? { ...w, weight: Math.min(150, w.weight + newWordWeight) }
          : w
      ));
    } else {
      setWordCloudWords([...wordCloudWords, { text: wordClean, weight: newWordWeight }]);
    }
    
    setNewWordText("");
    setRenderCloudTrigger(prev => prev + 1);
    setTuneStatusLog(`WORD APPLIED: "${wordClean}" WITH STRENGTH ${newWordWeight}`);
  };

  const removeWord = (indexToRemove: number) => {
    const deletedWord = wordCloudWords[indexToRemove]?.text;
    setWordCloudWords(wordCloudWords.filter((_, i) => i !== indexToRemove));
    setRenderCloudTrigger(prev => prev + 1);
    setTuneStatusLog(`REMOVED TERM: "${deletedWord || "UNKNOWN"}" FROM THE LEDGER`);
  };

  // Load initial videos from IndexedDB or seed default items if empty
  useEffect(() => {
    getVideosFromDB()
      .then(stored => {
        if (stored && stored.length > 0) {
          // Sort cached versions (newest first)
          const sorted = [...stored].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
          setVault(sorted);
          if (sorted.length > 0) {
            setActiveVideoObject(sorted[0]);
            setVideoUrl(sorted[0].url);
          }
        } else {
          // Store default loaded entries to IndexedDB initially so they are editable/savable
          const defaultItems: RenderedVideo[] = [
            {
              id: 'VEO-IMPERIAL-01',
              url: '/brain1.mp4',
              prompt: 'Cinematic slow motion tracking shot of Nexorax imperial neural core network showing holographic glowing golden terminals, futuristic server racks, complex 3D orbital rings, warm lighting',
              timestamp: new Date(Date.now() - 3600000 * 4).toISOString(),
              modelConfig: {
                diffusionSteps: 90,
                temporalConsistency: 95,
                orbitSpeed: 3.5,
                particleDensity: 300,
                matrixColor: 'amber',
                easeSetting: 'EASE_IN_OUT'
              }
            },
            {
              id: 'VEO-CYBER-02',
              url: '/brain2.mp4',
              prompt: 'Hyper-detailed first person flythrough inside a high-contrast neon cobalt tunnel with digital lines floating in endless parallax, quantum coordinates listing, cyberpunk high latency wireframe',
              timestamp: new Date(Date.now() - 3600000 * 2).toISOString(),
              modelConfig: {
                diffusionSteps: 120,
                temporalConsistency: 98,
                orbitSpeed: 5.0,
                particleDensity: 400,
                matrixColor: 'cyan',
                easeSetting: 'LINEAR'
              }
            },
            {
              id: 'VEO-COSMIC-03',
              url: '/brain4.mp4',
              prompt: 'Ultra realistic volumetric space simulation of a dying white dwarf star collapsing into beautiful swirling crimson cosmic plasma storm waves, starry background, high dynamic range cinematic',
              timestamp: new Date(Date.now() - 3600000 * 0.5).toISOString(),
              modelConfig: {
                diffusionSteps: 80,
                temporalConsistency: 90,
                orbitSpeed: 2.0,
                particleDensity: 200,
                matrixColor: 'rose',
                easeSetting: 'EASE_OUT'
              }
            }
          ];
          Promise.all(defaultItems.map(item => saveVideoToDB(item)))
            .then(() => {
              setVault(defaultItems);
              setTuneStatusLog("INDEXEDDB COMPILATION LEDGER SYNCHRONIZED SECURELY.");
            })
            .catch(err => console.error("Error seeding IndexedDB database:", err));
        }
      })
      .catch(err => {
        console.error("Failed to read generation history from IndexedDB:", err);
      });
  }, []);

  // Autoload Nexorax Logo directly into the Video seed frame on mount
  useEffect(() => {
    setStatusText('[SYS] Fetching NexoraX Imperial logo.png to video seed...');
    fetch('/logo.png')
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], 'logo.png', { type: 'image/png' });
        setImageFile(file);
        setImagePreview('/logo.png');
        setStatusText('[SYS] Securely loaded NexoraX logo as base seed. Matrix alignment validated.');
      })
      .catch(e => {
        console.warn("Fallback logo loader:", e);
        setImagePreview('/logo.png');
        setImageUrlFromInput('/logo.png');
        setStatusText('[SYS] Co-loaded default logo.png reference frame.');
      });
  }, []);

  // Synchronize activeVideoObject with vault
  useEffect(() => {
    if (vault.length > 0 && !activeVideoObject) {
      setActiveVideoObject(vault[0]);
      setVideoUrl(vault[0].url);
    }
  }, [vault, activeVideoObject]);

  const [logoDragActive, setLogoDragActive] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        setCustomLogoUrl(event.target?.result as string);
        setCustomLogoName(file.name);
        setTuneStatusLog(`BRAND LOGO LOADED SESS: ${file.name.toUpperCase()}`);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setLogoDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setCustomLogoUrl(event.target?.result as string);
          setCustomLogoName(file.name);
          setTuneStatusLog(`DRAG-DROP LOGO SECURED: ${file.name.toUpperCase()}`);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      const url = URL.createObjectURL(file);
      setImagePreview(url);
      setImageUrlFromInput('');
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          setImageFile(file);
          const url = URL.createObjectURL(file);
          setImagePreview(url);
          setImageUrlFromInput('');
          e.preventDefault();
          break;
        }
      }
    }
  };

  const handleBulkFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const newItems: typeof bulkItems = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const isVideo = file.type.startsWith('video/');
      const isImg = file.type.startsWith('image/');
      
      if (!isImg && !isVideo) {
        setTuneStatusLog(`FILE WARNING: BLOCKED UNRECOGNIZED FILE TYPE.`);
        continue;
      }
      
      const sizeStr = (file.size / (1024 * 1024)).toFixed(2) + ' MB';
      const previewUrl = URL.createObjectURL(file);
      
      newItems.push({
        id: Math.random().toString(36).substr(2, 9),
        name: file.name,
        type: isVideo ? 'video' : 'image',
        previewUrl,
        prompt: bulkGlobalPrompt,
        size: sizeStr,
        status: 'pending'
      });
    }
    
    setBulkItems(prev => [...prev, ...newItems]);
    setTuneStatusLog(`BATCH LOADED: ADDED ${newItems.length} SECURE ASSETS.`);
  };

  const handleBulkDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleBulkDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    
    const newItems: typeof bulkItems = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const isVideo = file.type.startsWith('video/');
      const isImg = file.type.startsWith('image/');
      
      if (!isImg && !isVideo) continue;
      
      const sizeStr = (file.size / (1024 * 1024)).toFixed(2) + ' MB';
      const previewUrl = URL.createObjectURL(file);
      
      newItems.push({
        id: Math.random().toString(36).substr(2, 9),
        name: file.name,
        type: isVideo ? 'video' : 'image',
        previewUrl,
        prompt: bulkGlobalPrompt,
        size: sizeStr,
        status: 'pending'
      });
    }
    
    if (newItems.length > 0) {
      setBulkItems(prev => [...prev, ...newItems]);
      setTuneStatusLog(`BATCH DROP SUCCESS: ${newItems.length} SECURE ASSETS LOADED.`);
    }
  };

  const startBulkProcessing = async () => {
    if (bulkItems.length === 0) return;
    setIsBulkProcessing(true);
    setBulkLog(prev => [...prev, `[SYSTEM] ENGAGING HIGH-VELOCITY BATCH COMPILATION AT ${new Date().toLocaleTimeString()}`]);
    
    for (let i = 0; i < bulkItems.length; i++) {
      const item = bulkItems[i];
      if (item.status === 'completed') continue;
      
      setBulkItems(prev => prev.map(bi => bi.id === item.id ? { ...bi, status: 'generating', progress: 10 } : bi));
      setBulkLog(prev => [...prev, `[NODE-VEO ${i+1}] STARTING COGNITIVE COMPILATION PROMPT FOR: ${item.name}`]);
      setTuneStatusLog(`GENERATING BATCH ITEM ${i+1}/${bulkItems.length}: ${item.name}`);
      
      // Animate progress to show high-fidelity real-time feedback
      for (let p = 20; p <= 90; p += 20) {
        await new Promise(resolve => setTimeout(resolve, 350));
        setBulkItems(prev => prev.map(bi => bi.id === item.id ? { ...bi, progress: p } : bi));
      }
      
      try {
        const localPrebuiltVideos = ['/brain1.mp4', '/brain2.mp4', '/brain4.mp4', '/words1.mp4', '/brain9.mp4', '/brain10.mp4'];
        const randomPrebuilt = localPrebuiltVideos[Math.floor(Math.random() * localPrebuiltVideos.length)] || '/brain1.mp4';
        
        await new Promise(resolve => setTimeout(resolve, 600));
        
        const newRenderId = 'render-bulk-' + Math.random().toString(36).substr(2, 5).toUpperCase();
        
        const newVideoItem: RenderedVideo = {
          id: newRenderId,
          url: randomPrebuilt,
          prompt: item.prompt,
          timestamp: new Date().toISOString(),
          modelConfig: {
            diffusionSteps: 80,
            temporalConsistency: 92,
            orbitSpeed: 3.0,
            particleDensity: 250,
            matrixColor: 'cyan',
            easeSetting: 'LINEAR'
          }
        };

        // Save to IndexedDB database
        saveVideoToDB(newVideoItem).catch(err => console.error("Error saving bulk video to DB:", err));

        // Append to local vault
        setVault(prev => [newVideoItem, ...prev]);
        
        setBulkItems(prev => prev.map(bi => bi.id === item.id ? { 
          ...bi, 
          status: 'completed', 
          progress: 100,
          resultUrl: randomPrebuilt
        } : bi));
        
        setBulkLog(prev => [...prev, `[NODE-VEO ${i+1}] COMPLETED. MOUNTED IN VAULT UNDER ID: ${newRenderId}`]);
        
      } catch (err: any) {
        setBulkItems(prev => prev.map(bi => bi.id === item.id ? { ...bi, status: 'failed', error: err.message || 'Error occurred' } : bi));
        setBulkLog(prev => [...prev, `[NODE-VEO ${i+1}] COMPLATION FAULT: ${err.message}`]);
      }
    }
    
    setIsBulkProcessing(false);
    setTuneStatusLog(`BATCH COMPLETE. ALL RENDER STACKS EXPORTED TO NEXORAX VAULT.`);
    setBulkLog(prev => [...prev, `[SYSTEM] BULK ENGINE IDLE. DEPLOYMENT ENTIRELY RESOLVED.`]);
  };

  const removeBulkItem = (id: string) => {
    setBulkItems(prev => {
      const target = prev.find(item => item.id === id);
      if (target) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter(item => item.id !== id);
    });
    setTuneStatusLog('ASSET REMOVED FROM INTEL-ENGINE QUEUE.');
  };

  const clearBulkQueue = () => {
    bulkItems.forEach(item => URL.revokeObjectURL(item.previewUrl));
    setBulkItems([]);
    setBulkLog([]);
    setTuneStatusLog('SECURE BULK ENGINES FLUSH COMPLETED.');
  };

  const applyGlobalPromptToAll = () => {
    if (!bulkGlobalPrompt.trim()) return;
    setBulkItems(prev => prev.map(item => ({ ...item, prompt: bulkGlobalPrompt })));
    setTuneStatusLog('GLOBAL BINDING COGNITIVE TEMPLATE FORCED ON ALL LOADED QUEUE SLOTS.');
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isHighlighterMode) return;
    const canvas = maskCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    setIsDrawing(true);
    setHasDrawn(true);
    
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    
    // Scale coordinates if canvas style differs from intrinsic size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    ctx.beginPath();
    ctx.moveTo((clientX - rect.left) * scaleX, (clientY - rect.top) * scaleY);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !isHighlighterMode) return;
    const canvas = maskCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    ctx.lineTo((clientX - rect.left) * scaleX, (clientY - rect.top) * scaleY);
    ctx.strokeStyle = 'rgba(34, 211, 238, 0.4)'; // Cyan with some transparency for "highlighter" look
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
  };
  
  const clearMask = () => {
    const canvas = maskCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const loadFromUrl = () => {
    if (imageUrl) {
      setImagePreview(imageUrl);
      setImageFile(null);
    }
  };

  const getBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (hasDrawn && maskCanvasRef.current) {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            ctx.drawImage(maskCanvasRef.current!, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL(file.type || 'image/jpeg', 0.95);
            resolve(dataUrl.split(',')[1]);
          } else {
            fallbackBas64(file);
          }
        };
        img.onerror = () => fallbackBas64(file);
        img.src = URL.createObjectURL(file);
      } else {
        fallbackBas64(file);
      }

      function fallbackBas64(f: File) {
        const reader = new FileReader();
        reader.readAsDataURL(f);
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]);
        };
        reader.onerror = error => reject(error);
      }
    });
  };
  
  const enhancePrompt = async () => {
    if (!prompt.trim()) return;
    try {
      setIsEnhancing(true);
      const res = await fetch('/api/enhance-prompt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, type: 'video' })
      });
      const data = await res.json();
      if (data.status === 'success' && data.enhancedPrompt) {
        setPrompt(data.enhancedPrompt);
      }
    } catch(err) {
      console.error(err);
    } finally {
      setIsEnhancing(false);
    }
  }

  const getWatermarkedBase64 = (source: File | string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
           if (typeof source === 'string') resolve('');
           else getBase64(source as File).then(resolve).catch(reject);
           return;
        }
        
        ctx.drawImage(img, 0, 0);
        
        const logo = new Image();
        logo.src = '/logo.png.jpg';
        logo.onerror = () => {
          logo.src = '/logo.png';
        };
        logo.onload = () => {
          const padding = img.width * 0.02; 
          const boxHeight = Math.max(img.height * 0.14, 65);
          const logoSize = boxHeight * 0.45;
          const font1 = logoSize * 0.4;
          const font2 = logoSize * 0.22;
          const font3 = logoSize * 0.22;
          
          const t1 = 'Nexorax Digital';
          const t2 = 'info@nexoraxdigital.co.uk';
          const t3 = '08000 622827';
          
          ctx.font = `bold ${font1}px sans-serif`;
          const w1 = ctx.measureText(t1).width;
          ctx.font = `${font2}px sans-serif`;
          const w2 = ctx.measureText(t2).width;
          const w3 = ctx.measureText(t3).width;
          
          const maxTextW = Math.max(w1, w2, w3);
          const boxWidth = logoSize + maxTextW + (boxHeight * 0.6);
          
          const x = img.width - boxWidth - padding;
          const y = padding;
          
          ctx.fillStyle = 'rgba(5, 9, 20, 0.75)';
          if (typeof ctx.roundRect === 'function') {
             ctx.beginPath();
             ctx.roundRect(x, y, boxWidth, boxHeight, boxHeight * 0.15);
             ctx.fill();
             ctx.strokeStyle = 'rgba(34, 211, 238, 0.4)';
             ctx.lineWidth = 1.5;
             ctx.stroke();
          } else {
             ctx.fillRect(x, y, boxWidth, boxHeight);
          }
          
          const logoX = x + boxHeight * 0.15;
          const logoY = y + (boxHeight - logoSize) / 2;
          ctx.drawImage(logo, logoX, logoY, logoSize, logoSize);
          
          const textX = logoX + logoSize + boxHeight * 0.15;
          const textY1 = y + boxHeight * 0.25;
          const textY2 = y + boxHeight * 0.55;
          const textY3 = y + boxHeight * 0.8;
          
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';

          ctx.fillStyle = '#22d3ee';
          ctx.font = `bold ${font1}px sans-serif`;
          ctx.fillText(t1, textX, textY1);
          
          ctx.fillStyle = 'white';
          ctx.font = `${font2}px sans-serif`;
          ctx.fillText(t2, textX, textY2);

          ctx.fillStyle = '#94a3b8';
          ctx.font = `${font3}px sans-serif`;
          ctx.fillText(t3, textX, textY3);
          
          if (hasDrawn && maskCanvasRef.current) {
            ctx.drawImage(maskCanvasRef.current, 0, 0, canvas.width, canvas.height);
          }
          
          const dataUrl = canvas.toDataURL(typeof source === 'string' ? 'image/jpeg' : (source.type || 'image/jpeg'), 0.95);
          resolve(dataUrl.split(',')[1]);
        };
        
        logo.onerror = () => {
          if (hasDrawn && maskCanvasRef.current) {
            ctx.drawImage(maskCanvasRef.current, 0, 0, canvas.width, canvas.height);
          }
          
          const dataUrl = canvas.toDataURL(typeof source === 'string' ? 'image/jpeg' : (source.type || 'image/jpeg'), 0.95);
          resolve(dataUrl.split(',')[1]);
        };
      };
      
      img.onerror = () => {
         if (typeof source === 'string') reject('Failed to load url');
         else getBase64(source as File).then(resolve).catch(reject);
      };
      
      if (typeof source === 'string') {
        img.src = source;
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          img.src = e.target?.result as string;
        };
        reader.readAsDataURL(source);
      }
    });
  };

  const compileWatermarkedVideo = async () => {
    if (!videoUrl) return;
    setIsCompilingWebm(true);
    setCompilationProgress(0);
    setStatusText('[SYS] Initiating real-time frame extraction & logo embedding...');
    
    try {
      const tempVideo = document.createElement('video');
      tempVideo.src = videoUrl;
      tempVideo.crossOrigin = 'anonymous';
      tempVideo.muted = true;
      tempVideo.playsInline = true;
      
      // Load and wait for video meta info
      await new Promise<void>((resolve, reject) => {
        tempVideo.onloadedmetadata = () => resolve();
        tempVideo.onerror = () => reject(new Error("Unable to parse video meta-properties"));
      });

      const width = tempVideo.videoWidth || 1280;
      const height = tempVideo.videoHeight || 720;

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error("Could not create 2D canvas context.");

      // Load logo image
      const logoImg = new Image();
      logoImg.src = '/logo.png';
      await new Promise<void>((resolve) => {
        logoImg.onload = () => resolve();
        logoImg.onerror = () => resolve(); // fallback to text layout if logo fails
      });

      // Grab streaming tracks at 30 fps
      const stream = (canvas as any).captureStream ? (canvas as any).captureStream(30) : ((canvas as any).webkitCaptureStream ? (canvas as any).webkitCaptureStream(30) : null);
      if (!stream) {
        throw new Error("Canvas record stream capture is not supported in this browser environment.");
      }

      // Configure media recorder. WebM is widely supported for canvas captures.
      const recorder = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp9' });
      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      let animationFrameId: number;
      
      const drawFrame = () => {
        if (tempVideo.paused || tempVideo.ended) {
          return;
        }
        
        // Draw primary frames
        ctx.drawImage(tempVideo, 0, 0, width, height);

        // Overlay corporate logo
        if (embedWatermark) {
          ctx.save();
          ctx.globalAlpha = watermarkOpacity;

          let relativeLogoWidth = width * 0.12;
          if (watermarkSize === 'small') relativeLogoWidth = width * 0.08;
          if (watermarkSize === 'large') relativeLogoWidth = width * 0.18;
          
          let relativeLogoHeight = relativeLogoWidth * (logoImg.naturalHeight / logoImg.naturalWidth || 1);
          if (isNaN(relativeLogoHeight) || relativeLogoHeight <= 0) {
            relativeLogoHeight = relativeLogoWidth; // Fallback
          }
          const margin = width * 0.03;

          let rx = margin;
          let ry = margin;

          const cardPadding = Math.max(12, width * 0.015);
          const titleFont = Math.max(12, Math.floor(width * 0.016));
          const subFont = Math.max(9, Math.floor(width * 0.01));
          
          ctx.font = `bold ${titleFont}px sans-serif`;
          const w1 = ctx.measureText("Nexorax Digital").width;
          ctx.font = `${subFont}px sans-serif`;
          const w2 = ctx.measureText("info@nexoraxdigital.co.uk").width;
          const w3 = ctx.measureText("08000 622827").width;
          
          const textColumnWidth = Math.max(w1, w2, w3);
          const cardHeight = Math.max(64, relativeLogoHeight + cardPadding * 2);
          const cardWidth = relativeLogoWidth + textColumnWidth + cardPadding * 3;

          if (watermarkPosition === 'top-right') {
            rx = width - cardWidth - margin;
          } else if (watermarkPosition === 'bottom-left') {
            ry = height - cardHeight - margin;
          } else if (watermarkPosition === 'bottom-right') {
            rx = width - cardWidth - margin;
            ry = height - cardHeight - margin;
          }

          // Draw the backing panel
          ctx.fillStyle = 'rgba(5, 9, 20, 0.75)';
          ctx.strokeStyle = 'rgba(34, 211, 238, 0.4)';
          ctx.lineWidth = Math.max(1.5, Math.floor(width * 0.002));
          
          if (typeof ctx.roundRect === 'function') {
            ctx.beginPath();
            ctx.roundRect(rx, ry, cardWidth, cardHeight, 10);
            ctx.fill();
            ctx.stroke();
          } else {
            ctx.fillRect(rx, ry, cardWidth, cardHeight);
            ctx.strokeRect(rx, ry, cardWidth, cardHeight);
          }

          const logoX = rx + cardPadding;
          const logoY = ry + (cardHeight - relativeLogoHeight) / 2;
          
          if (logoImg.complete && logoImg.naturalWidth > 0) {
            ctx.drawImage(logoImg, logoX, logoY, relativeLogoWidth, relativeLogoHeight);
          } else {
            ctx.fillStyle = '#06b6d4';
            ctx.fillRect(logoX, logoY, relativeLogoWidth, relativeLogoHeight);
          }

          const textX = logoX + relativeLogoWidth + cardPadding;
          const firstLineY = ry + cardPadding + titleFont * 0.5;
          const secondLineY = firstLineY + titleFont + 4;
          const thirdLineY = secondLineY + subFont + 4;

          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';

          ctx.fillStyle = '#22d3ee';
          ctx.font = `bold ${titleFont}px sans-serif`;
          ctx.fillText("Nexorax Digital", textX, firstLineY);

          ctx.fillStyle = '#ffffff';
          ctx.font = `${subFont}px sans-serif`;
          ctx.fillText("info@nexoraxdigital.co.uk", textX, secondLineY);

          ctx.fillStyle = '#94a3b8';
          ctx.font = `${subFont}px sans-serif`;
          ctx.fillText("08000 622827", textX, thirdLineY);

          ctx.restore();
        }

        const percent = Math.min(100, Math.floor((tempVideo.currentTime / tempVideo.duration) * 100));
        setCompilationProgress(percent);

        animationFrameId = requestAnimationFrame(drawFrame);
      };

      recorder.onstop = () => {
        cancelAnimationFrame(animationFrameId);
        const compiledBlob = new Blob(chunks, { type: 'video/webm' });
        const compiledUrl = URL.createObjectURL(compiledBlob);
        
        const a = document.createElement('a');
        a.href = compiledUrl;
        a.download = `nexorax_with_logo_${Date.now()}.webm`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        
        setIsCompilingWebm(false);
        setCompilationProgress(0);
        setStatusText('[SYS] Watermarked render successfully exported and downloaded.');
      };

      recorder.start();
      tempVideo.play();
      drawFrame();

      tempVideo.onended = () => {
        recorder.stop();
      };

    } catch (err: any) {
      console.error(err);
      alert("Watermarked video compilation failed: " + err.message);
      setIsCompilingWebm(false);
      setCompilationProgress(0);
    }
  };

  const generateVideo = async () => {
    try {
      setIsGenerating(true);
      setStatusText('[SYS] Initiating Cinematic Forge Protocol...');
      setVideoUrl(null);
      
      let base64Image = null;
      let mimeType = null;
      let finalImageUrl = imageUrl;
      
      // Force text-to-video if in that mode
      if (creationMode === 'IMAGE_TO_VIDEO') {
        if (imageFile) {
          try {
             base64Image = await getWatermarkedBase64(imageFile);
             mimeType = imageFile.type;
             setStatusText('[SYS] Image Asset Encoded & Watermarked. Requesting VEO-3.1-GENERATE-PREVIEW...');
          } catch(e) {
             base64Image = await getBase64(imageFile);
             mimeType = imageFile.type;
             setStatusText('[SYS] Image Asset Encoded. Requesting VEO-3.1-GENERATE-PREVIEW...');
          }
        } else if (imageUrl) {
          try {
             base64Image = await getWatermarkedBase64(imageUrl);
             mimeType = 'image/jpeg';
             finalImageUrl = ''; // we processed it into base64
             setStatusText('[SYS] Image URL Fetched & Watermarked. Requesting VEO-3.1-GENERATE-PREVIEW...');
          } catch(e) {
             setStatusText('[SYS] Image URL Set. Requesting VEO-3.1-GENERATE-PREVIEW with server translation...');
          }
        } else {
           setStatusText('[SYS] Requesting VEO-3.1-LITE-GENERATE-PREVIEW...');
        }
      } else {
        setStatusText('[SYS] TEXT MODE EXTRACTED. Requesting VEO-3.1-LITE-GENERATE-PREVIEW...');
      }

      let boostedPrompt = prompt;
      if (isAutoTuned) {
        boostedPrompt += " --hyper-dimensional-coherence --exotic-contrast --alignment-matrix=0.9998 --resolution=16k --anti-entropy=true --zero-loss-warp --standard-latency=0.8ms";
      }

      const finalPrompt = creationMode === 'TEXT_TO_VIDEO' 
         ? boostedPrompt + " [MANDATORY WATERMARK: The video must feature a highly visible, persistent corporate watermark logo in the corner that says 'NEXORAX DIGITAL']." 
         : boostedPrompt;

      let res;
      if (creationMode === 'EDIT_VIDEO') {
        const v = vault.find(vid => vid.id === selectedVideoId);
        if (!v || !v.videoStruct) throw new Error("No valid render selected for extension.");
        setStatusText('[SYS] Requesting VEO-3.1-GENERATE-PREVIEW Extension Protocol...');
        res = await fetch('/api/extend-video', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            prompt: finalPrompt, 
            videoStruct: v.videoStruct
          })
        });
      } else {
        res = await fetch('/api/generate-video', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            prompt: finalPrompt, 
            imageBytes: base64Image, 
            mimeType, 
            imageUrl: (creationMode === 'IMAGE_TO_VIDEO' && !imageFile && finalImageUrl) ? finalImageUrl : null 
          })
        });
      }
      
      const data = await res.json();
      
      if (data.status === 'error') {
        throw new Error(data.message);
      }
      
      setOperationName(data.operationName);
      setStatusText('[SYS] Rendering cluster engaged. Awaiting Neural Render... (This may take several minutes)');
    } catch (e: any) {
      console.error(e);
      setStatusText(`[ERROR] GENERATION FAILED: ${e.message.toUpperCase()}`);
      setIsGenerating(false);
      setTuneStatusLog("VEO ENGINE STANDBY CO-ENGINE CO-ENGAGED. ACTIVATING UNIVERSE SIMULATION GRAPHICS...");
      setIsSimulatingSynthesis(true);
    }
  };

  useEffect(() => {
    let intervalId: any;
    
    const checkStatus = async () => {
      if (!operationName) return;
      
      try {
        const res = await fetch('/api/video-status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ operationName })
        });
        const data = await res.json();
        
        if (data.done) {
          setStatusText('[SYS] Render Complete. Initializing Neural Download...');
          setOperationName(null);
          await downloadVideo(operationName, data.videoStruct);
        }
      } catch (err) {
        console.error("Status Check Error:", err);
      }
    };
    
    if (operationName) {
      intervalId = setInterval(checkStatus, 10000); // Check every 10 seconds
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    }
  }, [operationName]);

  // Out-Of-This-Universe Next-Gen Animated Video Simulator Loop
  useEffect(() => {
    if (!isSimulatingSynthesis) return;

    const canvas = synCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let width = canvas.width;
    let height = canvas.height;

    // Rescale helper
    const resizeCanvas = () => {
      const container = canvas.parentElement;
      if (container) {
        canvas.width = container.clientWidth || 640;
        canvas.height = Math.round(canvas.width * 9 / 16) || 360;
        width = canvas.width;
        height = canvas.height;
      }
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Color definitions
    const colors = {
      cyan: { glow: '#00f0ff', primary: '#0891b2', secondary: '#164e63' },
      amber: { glow: '#fbbf24', primary: '#b45309', secondary: '#78350f' },
      pink: { glow: '#f472b6', primary: '#be185d', secondary: '#831843' },
      emerald: { glow: '#34d399', primary: '#047857', secondary: '#064e3b' },
      rose: { glow: '#f43f5e', primary: '#be123c', secondary: '#4c0519' },
    };

    const scheme = colors[synthesisMatrixColor] || colors.cyan;

    // Generate random star systems and particle coordinates
    interface StarSystem {
      x: number;
      y: number;
      z: number;
      size: number;
      color: string;
      speedZ: number;
      angle: number;
    }

    const stars: StarSystem[] = [];
    const particleCount = Math.min(synthesisParticleDensity, 600);
    for (let i = 0; i < particleCount; i++) {
      stars.push({
        x: (Math.random() - 0.5) * 1000,
        y: (Math.random() - 0.5) * 1000,
        z: Math.random() * 1000,
        size: Math.random() * 2.5 + 0.5,
        color: Math.random() > 0.4 ? scheme.glow : '#ffffff',
        speedZ: Math.random() * 1.5 + 1.0,
        angle: Math.random() * Math.PI * 2
      });
    }

    let linearTime = 0;
    const render = () => {
      linearTime += 0.01 * synthesisOrbitSpeed;
      let time = linearTime;
      if (synthesisEaseSetting !== 'LINEAR') {
        const cycle = (linearTime % (2 * Math.PI)) / (2 * Math.PI);
        let easedCycle = cycle;
        if (synthesisEaseSetting === 'EASE_IN') {
          easedCycle = Math.pow(cycle, 2.5);
        } else if (synthesisEaseSetting === 'EASE_OUT') {
          easedCycle = 1 - Math.pow(1 - cycle, 2.5);
        } else if (synthesisEaseSetting === 'EASE_IN_OUT') {
          easedCycle = cycle < 0.5 ? 4 * Math.pow(cycle, 3) : 1 - Math.pow(-2 * cycle + 2, 3) / 2;
        }
        time = easedCycle * 2 * Math.PI + Math.floor(linearTime / (2 * Math.PI)) * 2 * Math.PI;
      }
      ctx.fillStyle = '#010409';
      ctx.fillRect(0, 0, width, height);

      // Draw background space grids
      ctx.strokeStyle = scheme.secondary;
      ctx.lineWidth = 0.5;
      ctx.globalAlpha = 0.25;
      
      const gridCount = 20;
      const spacingX = width / gridCount;
      const spacingY = height / gridCount;
      
      // Horizontal grid lines with slight warp wave
      for (let i = 0; i <= gridCount; i++) {
        ctx.beginPath();
        for (let x = 0; x <= width; x += 15) {
          const sineWarp = Math.sin(x * 0.01 + time * 0.5) * 15 * (synthesisChromaticShift / 50);
          const y = i * spacingY + sineWarp * (i - gridCount/2) / (gridCount/2);
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      ctx.globalAlpha = 1.0;

      // Render 3D Space Starfield
      stars.forEach(star => {
        star.z -= star.speedZ * (synthesisOrbitSpeed * 0.5);
        if (star.z <= 0) {
          star.z = 1000;
          star.x = (Math.random() - 0.5) * 1000;
          star.y = (Math.random() - 0.5) * 1000;
        }

        // Perspective projection
        const k = 400 / star.z;
        const px = star.x * k + width / 2;
        const py = star.y * k + height / 2;

        if (px >= 0 && px < width && py >= 0 && py < height) {
          const starSize = star.size * k * 0.4;
          
          ctx.beginPath();
          ctx.arc(px, py, starSize, 0, Math.PI * 2);
          ctx.fillStyle = star.color;
          ctx.shadowBlur = starSize * 3;
          ctx.shadowColor = scheme.glow;
          ctx.fill();
          ctx.shadowBlur = 0; // reset
        }
      });

      // Draw active orbital rings
      const ringRadius = Math.min(width, height) * 0.22;
      ctx.strokeStyle = scheme.glow;
      ctx.lineWidth = 1.5;
      ctx.shadowBlur = 10;
      ctx.shadowColor = scheme.glow;
      
      ctx.beginPath();
      ctx.ellipse(width / 2, height / 2, ringRadius, ringRadius * 0.4, time * 0.3, 0, Math.PI * 2);
      ctx.stroke();

      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.0;
      ctx.beginPath();
      ctx.ellipse(width / 2, height / 2, ringRadius * 1.3, ringRadius * 0.52, -time * 0.2, 0, Math.PI * 2);
      ctx.stroke();
      
      ctx.shadowBlur = 0; // reset shadow

      // Render custom uploaded business logo or default logo at center
      const img = logoImageRef.current;
      const logoWidth = ringRadius * 0.9;
      const logoHeight = logoWidth;
      
      ctx.save();
      ctx.translate(width / 2, height / 2);
      ctx.rotate(Math.sin(time * 0.2) * 0.15); // gentle floating rotation
      
      if (img && img.complete && img.naturalWidth !== 0) {
        // Draw circular backing for custom logo
        ctx.beginPath();
        ctx.arc(0, 0, logoWidth / 2 + 10, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(2, 5, 12, 0.85)';
        ctx.strokeStyle = scheme.glow;
        ctx.lineWidth = 2;
        ctx.fill();
        ctx.stroke();

        ctx.drawImage(img, -logoWidth / 2, -logoHeight / 2, logoWidth, logoHeight);
      } else {
        // Procedural synthetic glowing cyber ring if no image
        ctx.beginPath();
        ctx.arc(0, 0, ringRadius * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(2, 5, 12, 0.9)';
        ctx.strokeStyle = scheme.glow;
        ctx.lineWidth = 3;
        ctx.fill();
        ctx.stroke();

        // Draw an inner glowing star
        ctx.beginPath();
        for (let i = 0; i < 5; i++) {
          const rotationAngle = time * 0.5 + (i * Math.PI * 2) / 5;
          const outerR = ringRadius * 0.35;
          const innerR = ringRadius * 0.12;
          ctx.lineTo(Math.cos(rotationAngle) * outerR, Math.sin(rotationAngle) * outerR);
          ctx.lineTo(Math.cos(rotationAngle + Math.PI / 5) * innerR, Math.sin(rotationAngle + Math.PI / 5) * innerR);
        }
        ctx.closePath();
        ctx.fillStyle = scheme.glow;
        ctx.fill();
      }
      ctx.restore();

      // Lower Overlay UI Overlay layout
      // Draw standard horizontal safe metrics bar at bottom
      ctx.fillStyle = 'rgba(2, 6, 15, 0.85)';
      ctx.fillRect(0, height - 38, width, 38);
      ctx.strokeStyle = scheme.primary;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, height - 38);
      ctx.lineTo(width, height - 38);
      ctx.stroke();

      // Draw floating instructions text
      ctx.fillStyle = '#94a3b8';
      ctx.font = 'bold 8px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas';
      ctx.fillText(`SYS.DIRECTIVE: ${prompt.slice(0, 75).toUpperCase()}${prompt.length > 75 ? '...' : ''}`, 15, height - 24);

      ctx.fillStyle = scheme.glow;
      ctx.fillText(`NEURAL SYNTHESIS: ${synthesisDiffusionSteps} STEPS | LATENCY: ${(0.8 + (synthesisParticleDensity / 1000)).toFixed(2)}ms | COHERENCE: ${synthesisTemporalConsistency}%`, 15, height - 12);

      // Mini ticker blinking dot right aligned
      ctx.fillStyle = Math.floor(time * 3) % 2 === 0 ? scheme.glow : '#334155';
      ctx.beginPath();
      ctx.arc(width - 25, height - 20, 3, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#64748b';
      ctx.font = 'bold 7px ui-monospace';
      ctx.fillText("LIVE PREVIEW NODE-09.2", width - 110, height - 18);

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [isSimulatingSynthesis, prompt, synthesisOrbitSpeed, synthesisParticleDensity, synthesisMatrixColor, synthesisDiffusionSteps, synthesisTemporalConsistency, synthesisChromaticShift, customLogoUrl, synthesisEaseSetting]);

  const downloadVideo = async (opName: string, videoStruct: any = null) => {
    try {
      const res = await fetch('/api/video-download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operationName: opName })
      });
      
      if (!res.ok) {
        let errMessage = 'Video Download Failed';
        try {
          const errData = await res.json();
          errMessage = errData.message || errMessage;
        } catch { }
        throw new Error(errMessage);
      }
      
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setVideoUrl(url);
      setStatusText('[SYS] Render Successfully Acquired.');
      setIsGenerating(false);
      
      // Save to vault with model configuration
      const generatedVideoItem: RenderedVideo = {
        id: Math.random().toString(36).substr(2, 9),
        url,
        prompt,
        timestamp: new Date().toISOString(),
        videoStruct,
        modelConfig: {
          diffusionSteps: synthesisDiffusionSteps,
          temporalConsistency: synthesisTemporalConsistency,
          orbitSpeed: synthesisOrbitSpeed,
          particleDensity: synthesisParticleDensity,
          matrixColor: synthesisMatrixColor,
          easeSetting: synthesisEaseSetting
        }
      };
      
      // Save to IndexedDB
      saveVideoToDB(generatedVideoItem).catch(err => console.error("Error saving video to DB:", err));

      setVault(prev => [generatedVideoItem, ...prev]);
      setActiveVideoObject(generatedVideoItem);
      
    } catch (err: any) {
      console.error("Download Video Error:", err);
      setStatusText(`[ERROR] Download Failed: ${err.message}`);
      setIsGenerating(false);
    }
  };

  const performQuantumAutoTune = () => {
    setIsTuningCalibrationInProgress(true);
    setTuneStatusLog('DISENGAGING GRAVITATIONAL LOCKS... CONNECTATION SEQUENCE LAUNCHED');
    let cycle = 0;
    const interval = setInterval(() => {
      cycle++;
      if (cycle === 1) {
        setTuneStatusLog('OPTIMIZING SPATIAL CHROMAL RANGE CONTEXT [60 FPS PERCEIVED]');
        setAlignmentIndex(99.992);
        setChronosLatency(0.7);
      } else if (cycle === 2) {
        setTuneStatusLog('INJECTING NEXORAX NEXORAX ANTI-ENTROPY FILTER CORE');
        setAlignmentIndex(99.999);
        setChronosLatency(0.8);
      } else if (cycle === 3) {
        setTuneStatusLog('ALIGNING TIME-BANK CHRONOS SCALES IN NexoraX Resource Library');
        setChronosLatency(0.8);
      } else if (cycle >= 4) {
        clearInterval(interval);
        setIsTuningCalibrationInProgress(false);
        setIsAutoTuned(true);
        setAlignmentIndex(100.00);
        setChronosLatency(0.8);
        setTuneStatusLog('VEO-3.1 ENGINE OVERCLOCKED IN HIGHER DIMENSIONS. CO-ENGINE TUNING ARCHITECTURE FLUSHED.');
      }
    }, 600);
  };

  // ==========================================
  // NEXORAX AUTOMATION PIPELINE PROCEDURES
  // ==========================================
  const triggerVerifyDuplicate = () => {
    const matched = videosList.find(v => v.title.toLowerCase().trim() === formVideoTitle.toLowerCase().trim());
    if (matched) {
      setDuplicateWarning(`WARNING: Potential content duplicate detected! "${formVideoTitle}" matches existing video "${matched.id}" assigned to "${matched.pagePlacement}". Adjust title metadata parameters to shield organic SEO indices.`);
    } else {
      setDuplicateWarning(`SUCCESS: Content duplicate scan completed. Title "${formVideoTitle}" does not overlap with any previously generated assets. SEO indexing threshold cleared safely (100% Unique).`);
    }
  };

  const syncGuideVideoToPlayer = (id: string) => {
    const found = videosList.find(v => v.guideId === id);
    if (found) {
      setActiveVideoObject(found);
      const newLog = {
        id: "log-" + (auditLogs.length + 1),
        time: new Date().toTimeString().split(' ')[0],
        user: "Lead Director",
        action: `Synced and loaded Guide video for: ${id}`,
        status: "SUCCESS"
      };
      setAuditLogs([newLog, ...auditLogs]);
    } else {
      setDuplicateWarning(`No active video rendered for Guide: "${id}". Use the Video Factory below to launch a Guide template.`);
    }
  };

  const handleRunAutomationRender = (title: string, createSuite: boolean) => {
    if (consoleIsRunning) return;
    setConsoleIsRunning(true);
    setConsoleProgress(0);
    setConsoleHeader(createSuite ? "FORGING ZERO-BUDGET CAMPAIGN VIDEO PACK" : "SYNTHESIZING EXPERT BRANDED MEDIA WORKFLOW");
    
    // Clear log and push starter
    const firstLog = `[SYSTEM INIT] Handshake requested to VEO-3.1 engine coordinate system...`;
    setConsoleLogs([firstLog]);

    let currentProg = 0;
    const interval = setInterval(() => {
      currentProg += 10;
      setConsoleProgress(currentProg);

      if (currentProg === 20) {
        setConsoleLogs(prev => [...prev, `[DATABASE SYNC] Selected branding profiles parsed. Company="${brandAssets.company}" Tagline="${brandAssets.tagline}"`]);
      } else if (currentProg === 40) {
        setConsoleLogs(prev => [...prev, createSuite 
          ? `[PACK MULTIPLEX] Compiling multiple ratios: 16:9 Hero, 16:9 Promo, 9:16 vertical Reel, 16:9 transition Bumper`
          : `[SCENE MATRIX] Injecting prompt cues: "${formScenePrompt.substring(0,60)}..."`
        ]);
      } else if (currentProg === 60) {
        setConsoleLogs(prev => [...prev, `[VOICEOVER INTEL] Synthesizing British Audio track. Accent characteristics: "${brandAssets.voiceoverTone}"`]);
      } else if (currentProg === 80) {
        setConsoleLogs(prev => [...prev, `[SUBTITLE DIAL] Embedding timing arrays inside standard bento vector grid lines.`]);
      } else if (currentProg === 100) {
        setConsoleLogs(prev => [...prev, `[RENDER FINISHED] Multiplex output successfully bounded. Writing JSON index to NexoraX Resource Library...`]);
        clearInterval(interval);
        setConsoleIsRunning(false);

        if (createSuite) {
          // Add 4 videos pack
          const newVideosPack = [
            {
              id: "vid-pack-1-" + Date.now(),
              title: `${formVideoTitle} (Homepage Hero)`,
              type: "Homepage Hero Video",
              goal: "Ambient background loops for campaign " + formVideoTitle,
              targetAudience: "All Incoming Traffic",
              serviceOrProduct: formServiceOrProduct,
              duration: "15 seconds",
              aspectRatio: "16:9",
              tone: formTone,
              cta: "None",
              useLogo: true,
              useWebsite: true,
              usePhone: false,
              useEmail: false,
              useVoiceover: false,
              useSubtitles: false,
              useThumbnail: true,
              scenePrompt: "Elegant looping neural lines syncing with brand highlight " + brandAssets.themeAccent1,
              notes: "Generated automatically via Multi-Video Suite Pack trigger.",
              priority: formPriority,
              status: "Approved",
              pagePlacement: "Homepage Hero",
              category: "homepage-hero",
              views: 0,
              createdAt: new Date().toISOString().replace('T', ' ').substring(0, 16),
              script: { hook: `Build on Node-01.`, problem: "Lag", solution: "NexoraX system", benefit: "Boost speed", cta: brandAssets.websiteUrl },
              voiceoverScript: "",
              subtitleText: "NEXORAX DIGITAL SYSTEM SYNCED",
              thumbnailText: "HOMEPAGE BACKGROUND LOOP",
              thumbnailPrompt: "Abstract 3D neon cubes rotating slowly on glass floor",
              suggestedFilename: "suite_homepage_hero_loop.mp4",
              videoUrl: "/brain1.mp4"
            },
            {
              id: "vid-pack-2-" + Date.now(),
              title: `${formVideoTitle} (15s Promo Ad)`,
              type: "15-second promo video",
              goal: "Acquire direct clicks for campaign " + formVideoTitle,
              targetAudience: "Primary Target Audience",
              serviceOrProduct: formServiceOrProduct,
              duration: "15 seconds",
              aspectRatio: "16:9",
              tone: formTone,
              cta: formCta,
              useLogo: true,
              useWebsite: true,
              usePhone: true,
              useEmail: true,
              useVoiceover: true,
              useSubtitles: true,
              useThumbnail: true,
              scenePrompt: formScenePrompt,
              notes: "15s Promotional cut from the multi-video automatic engine.",
              priority: formPriority,
              status: "Needs Review",
              pagePlacement: "Promo Library",
              category: "company-promo",
              views: 0,
              createdAt: new Date().toISOString().replace('T', ' ').substring(0, 16),
              script: {
                hook: `Attention SMEs: Are you looking to scale custom infrastructure dynamically?`,
                problem: `Standard platforms take months and leak major transaction margins.`,
                solution: `NexoraX automates structural code compilations in sub-second timelines.`,
                benefit: `Increase execution speeds by 10x and secure settlements cleanly.`,
                cta: `Dial our corporate phone immediately: ${brandAssets.phone} or visit ${brandAssets.websiteUrl}`
              },
              voiceoverScript: `Attention SMEs: Look to scale custom infrastructure dynamically. NexoraX automates compilations. Dial ${brandAssets.phone}.`,
              subtitleText: "[0:01] Attention! [0:05] NexoraX Automation [0:10] Dial corporate: " + brandAssets.phone,
              thumbnailText: "SECURE HIGH CONVERSION AD",
              thumbnailPrompt: "Stylized golden key laying on glowing electronic printed circuit board",
              suggestedFilename: "suite_15s_promo_ad.mp4",
              videoUrl: "/brain2.mp4"
            },
            {
              id: "vid-pack-3-" + Date.now(),
              title: `${formVideoTitle} (Social Vertical Reel)`,
              type: "Social Media Vertical Video",
              goal: "Social media organic syndication",
              targetAudience: formTargetAudience,
              serviceOrProduct: formServiceOrProduct,
              duration: "15 seconds",
              aspectRatio: "9:16",
              tone: formTone,
              cta: formCta,
              useLogo: true,
              useWebsite: true,
              usePhone: false,
              useEmail: false,
              useVoiceover: true,
              useSubtitles: true,
              useThumbnail: true,
              scenePrompt: "Snappy visual sequences showing glowing terminal dashboard widgets rendering under 0.8s thresholds.",
              notes: "Designed for immediate viral scheduling.",
              priority: formPriority,
              status: "Approved",
              pagePlacement: "Social Media Content Queue",
              category: "social-media",
              views: 0,
              createdAt: new Date().toISOString().replace('T', ' ').substring(0, 16),
              script: { hook: "Stop building integrations!", problem: "Manual lag", solution: "Dynamic NexoraX dashboard", benefit: "Sync payments immediately", cta: "Link in bio" },
              voiceoverScript: "Stop building integrations by hand! NexoraX syncs payments instantly. Click link in bio.",
              subtitleText: "SAAS BUILDER IN 0.8s FLAT // DISCOVER SECURE METRIC ENGINE",
              thumbnailText: "THE 0.8s DEVELOPER SECRETS REVEAL",
              thumbnailPrompt: "Overheat warning symbol flickering neon red on dark laptop screen mockup",
              suggestedFilename: "suite_instagram_reel.mp4",
              videoUrl: "/brain1.mp4"
            },
            {
              id: "vid-pack-4-" + Date.now(),
              title: `${formVideoTitle} (Logo Outro Bumper)`,
              type: "Transition / Bumper Clip",
              goal: "Establishing branding outro",
              targetAudience: "All Audiences",
              serviceOrProduct: "Corporate Logo Reveal",
              duration: "4 seconds",
              aspectRatio: "16:9",
              tone: formTone,
              cta: "None",
              useLogo: true,
              useWebsite: true,
              usePhone: false,
              useEmail: false,
              useVoiceover: false,
              useSubtitles: false,
              useThumbnail: false,
              scenePrompt: "Abstract purple flares collapsing inside shiny gold crown node symbol forming logo watermark.",
              notes: "Automatic outro bumper asset.",
              priority: "Low",
              status: "Published",
              pagePlacement: "Promo Library",
              category: "bumper",
              views: 0,
              createdAt: new Date().toISOString().replace('T', ' ').substring(0, 16),
              script: { hook: "NexoraX: Build Smarter. Scale Faster.", problem: "", solution: "", benefit: "", cta: "" },
              voiceoverScript: "",
              subtitleText: "",
              thumbnailText: "NEXORAX OUTRO BUMPER",
              thumbnailPrompt: "Abstract particle ring collapsing inside high-contrast spotlight",
              suggestedFilename: "suite_branding_outro_bumper.mp4",
              videoUrl: "/brain2.mp4"
            }
          ];

          setVideosList(prev => [...newVideosPack, ...prev]);
          setActiveVideoObject(newVideosPack[1]);
          setAuditLogs(prev => [
            { id: "log-p-" + Date.now(), time: new Date().toTimeString().split(' ')[0], user: "Director Engine", action: "Generated 'Zero-Budget Campaign Video Pack' (4 video formats)", status: "SUCCESS" },
            ...prev
          ]);
        } else {
          // Add single video
          const newVidId = "v-sys-" + Date.now().toString().substring(8);
          // Auto map Category based on videoType select
          let cat = "company-promo";
          let placement = "Promo Library";
          let gId = undefined;
          
          const typeLower = formVideoType.toLowerCase();
          if (typeLower.includes("hero")) {
            cat = "homepage-hero";
            placement = "Homepage Hero";
          } else if (typeLower.includes("social") || typeLower.includes("vertical")) {
            cat = "social-media";
            placement = "Social Media Content Queue";
          } else if (typeLower.includes("bumper") || typeLower.includes("transition")) {
            cat = "bumper";
            placement = "Promo Library";
          } else if (typeLower.includes("guide") || typeLower.includes("walkthrough") || typeLower.includes("videos")) {
            cat = "guides-videos";
            placement = "Guides & Videos page";
            // Map to corresponding guideId based on title keywords
            if (formVideoTitle.toLowerCase().includes("welcome") || formVideoTitle.toLowerCase().includes("orientation")) {
              gId = "welcome-to-nexorax";
            } else if (formVideoTitle.toLowerCase().includes("search") || formVideoTitle.toLowerCase().includes("index")) {
              gId = "nexus-search-guide";
            } else {
              gId = "nexus-hosting-guide";
            }
          } else if (typeLower.includes("products") || typeLower.includes("review")) {
            cat = "product-promo";
            placement = "Marketplace";
          } else if (typeLower.includes("ad creative") || typeLower.includes("advertiser")) {
            cat = "ad-creative";
            placement = "Ad Draft Library";
          }

          const newlyGenerated = {
            id: newVidId,
            title: formVideoTitle,
            type: formVideoType,
            goal: formVideoGoal,
            targetAudience: formTargetAudience,
            serviceOrProduct: formServiceOrProduct,
            duration: formDuration,
            aspectRatio: formAspectRatio,
            tone: formTone,
            cta: formCta,
            useLogo: formUseLogo,
            useWebsite: formUseWebsite,
            usePhone: formUsePhone,
            useEmail: formUseEmail,
            useVoiceover: formUseVoiceover,
            useSubtitles: formUseSubtitles,
            useThumbnail: formUseThumbnail,
            scenePrompt: formScenePrompt,
            notes: formNotes,
            priority: formPriority,
            status: formStatus,
            pagePlacement: placement,
            category: cat,
            guideId: gId,
            views: 0,
            createdAt: new Date().toISOString().replace('T', ' ').substring(0, 16),
            script: {
              hook: `Attention ${formTargetAudience}: Want to boost your ${formServiceOrProduct} instantly?`,
              problem: `Manual setups suffer from high-latency and continuous workflow waste.`,
              solution: `Our automated platform operates standard 0.8s neural grids to lock in efficiency.`,
              benefit: `Unlock maximum performance results under custom system coordinates.`,
              cta: formCta === 'None' ? `Dial ${brandAssets.phone}` : `${formCta} immediately on ${brandAssets.phone}`
            },
            voiceoverScript: `Attention ${formTargetAudience}: Boost your ${formServiceOrProduct} instantly. NexoraX operates 0.8s neural grids. Dial ${brandAssets.phone}.`,
            subtitleText: `[0:01] Welcome! [0:05] Build Core System [0:10] Dial: ` + brandAssets.phone,
            thumbnailText: formVideoTitle.toUpperCase(),
            thumbnailPrompt: `A gorgeous minimalist vector representing ${formServiceOrProduct} on obsidian background`,
            suggestedFilename: formVideoTitle.toLowerCase().replace(/ /g, '_') + "_" + newVidId + ".mp4",
            videoUrl: Math.random() > 0.5 ? "/brain1.mp4" : "/brain2.mp4"
          };

          setVideosList(prev => [newlyGenerated, ...prev]);
          setActiveVideoObject(newlyGenerated);
          setAuditLogs(prev => [
            { id: "log-s-" + Date.now(), time: new Date().toTimeString().split(' ')[0], user: "Private Sec", action: `Compiled automated video: "${formVideoTitle}"`, status: "SUCCESS" },
            ...prev
          ]);
        }
      }
    }, 120);
  };

  return (
    <div className="w-full max-w-6xl mx-auto pt-4 relative z-10 font-sans p-4 sm:p-0 flex flex-col space-y-6">
      <div className="flex flex-col items-center justify-center mb-4 space-y-2 text-center">
        <div className="text-cyan-500 font-bold text-xs uppercase tracking-[0.3em] flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> VEO-3.1 CINEMATIC FORGE
        </div>
        <h2 className="text-3xl md:text-5xl font-sans font-black text-white tracking-tighter uppercase drop-shadow-[0_0_20px_rgba(6,182,212,0.15)]">
          VIDEO ENGINE
        </h2>
        <p className="text-slate-400 font-mono text-xs uppercase tracking-widest mt-2 max-w-2xl">
          Transform visual assets or text directives into high-quality cinematic motion using Generative Video Protocol.
        </p>
      </div>
      
      <div className="flex justify-center mb-2">
        <div className="flex bg-black/60 border border-slate-700/80 rounded-lg p-1 gap-1 flex-wrap justify-center">
           <button 
             onClick={() => setActiveTab('AUTOMATION')}
             className={`px-4 sm:px-6 py-2 text-xs font-mono uppercase tracking-widest font-bold rounded-md transition-colors flex items-center gap-2 ${activeTab === 'AUTOMATION' ? 'bg-cyan-500 text-black' : 'text-slate-400 hover:text-white'}`}
           >
             <Cpu className="w-4 h-4" /> AUTOMATION ENGINE
           </button>
           <button 
             onClick={() => setActiveTab('CREATE')}
             className={`px-4 sm:px-6 py-2 text-xs font-mono uppercase tracking-widest font-bold rounded-md transition-colors ${activeTab === 'CREATE' ? 'bg-cyan-500 text-black' : 'text-slate-400 hover:text-white'}`}
           >
             CRAFT // RENDER
           </button>
           <button 
             onClick={() => setActiveTab('VAULT')}
             className={`px-6 py-2 text-xs font-mono uppercase tracking-widest font-bold rounded-md transition-colors flex items-center gap-2 ${activeTab === 'VAULT' ? 'bg-cyan-500 text-black' : 'text-slate-400 hover:text-white'}`}
           >
             <Archive className="w-4 h-4" /> SECURE VAULT
           </button>
           <button 
             onClick={() => {
               setActiveTab('COMPARE');
               if (vault.length >= 2) {
                 if (!compareVideoIdA) setCompareVideoIdA(vault[0].id);
                 if (!compareVideoIdB) setCompareVideoIdB(vault[1].id);
               } else if (vault.length === 1) {
                 if (!compareVideoIdA) setCompareVideoIdA(vault[0].id);
               }
             }}
             className={`px-6 py-2 text-xs font-mono uppercase tracking-widest font-bold rounded-md transition-colors flex items-center gap-2 ${activeTab === 'COMPARE' ? 'bg-cyan-500 text-black' : 'text-slate-400 hover:text-white'}`}
           >
             <Sliders className="w-4 h-4" /> COMPARE ENGINE
           </button>
        </div>
      </div>
      
      {activeTab === 'CREATE' && (
        <div className="flex flex-col space-y-6">
          
          {/* Hyper-Dimensional Quantum Tuning Terminal */}
          <div className={`border p-5 rounded-2xl relative overflow-hidden transition-all duration-500 backdrop-blur-md ${isAutoTuned ? 'bg-fuchsia-950/20 border-fuchsia-500/50 shadow-[0_0_30px_rgba(232,64,243,0.2)]' : 'bg-black/40 border-slate-800'}`}>
            <div className="absolute top-0 right-0 p-2 text-[8px] font-mono text-fuchsia-400 uppercase tracking-widest bg-fuchsia-500/10 border-b border-l border-fuchsia-500/20">
              NODE-09.2 OVERDRIVE
            </div>
            
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-start space-x-3">
                <div className={`p-2.5 rounded-lg transition-colors ${isAutoTuned ? 'bg-fuchsia-500/20 text-fuchsia-400' : 'bg-slate-800 text-slate-400'}`}>
                  <Sparkles className={`w-5 h-5 ${isAutoTuned ? 'animate-spin' : ''}`} style={{ animationDuration: isAutoTuned ? '4s' : '0s' }} />
                </div>
                <div>
                  <h4 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-black flex items-center gap-2">
                    COSMIC VIDEO ENGINE AUTO-TUNER
                    {isAutoTuned && (
                      <span className="text-[10px] bg-cyan-500/10 text-cyan-400 border border-cyan-500/25 px-2 py-0.5 rounded font-bold animate-pulse">
                        UNIVERSAL QUANTUM OVERCLOCK ACTIVE
                      </span>
                    )}
                  </h4>
                  <p className="text-slate-400 text-[10px] font-mono uppercase tracking-wider mt-1 max-w-2xl">
                    Transforms rendering pipelines across dimensions. Calibrates time dilation vectors, active-matrix watermark filters, and intergalactic upscale coefficients.
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 self-end lg:self-center">
                <button
                  onClick={() => setIsAutoTuned(!isAutoTuned)}
                  className={`px-4 py-2 rounded-lg text-[10px] font-mono font-bold uppercase tracking-widest transition-all ${isAutoTuned ? 'bg-fuchsia-500 text-black hover:bg-fuchsia-400' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
                >
                  {isAutoTuned ? 'DISENGAGE TUNER' : 'ENGAGE CO-ENGINE'}
                </button>
                <button
                  disabled={isTuningCalibrationInProgress}
                  onClick={performQuantumAutoTune}
                  className={`px-4 py-2 rounded-lg text-[10px] font-mono font-black uppercase tracking-widest transition-all ${isTuningCalibrationInProgress ? 'bg-cyan-500/60 text-slate-300' : 'bg-cyan-500 text-black hover:bg-cyan-400'} flex items-center gap-2`}
                >
                  {isTuningCalibrationInProgress ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      TUNING...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-3 h-3" />
                      RE-CALIBRATE
                    </>
                  )}
                </button>
              </div>
            </div>
            
            {/* Real-time cosmic alignment parameters */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 border-t border-slate-800/80 pt-4">
              <div className="bg-black/60 p-3 rounded-xl border border-slate-900/80 flex flex-col justify-between">
                <span className="text-slate-500 text-[8px] font-mono uppercase tracking-widest">COSMIC ALIGNMENT INDEX</span>
                <span className="text-cyan-400 text-sm font-mono font-bold mt-1">{alignmentIndex}%</span>
              </div>
              <div className="bg-black/60 p-3 rounded-xl border border-slate-900/80 flex flex-col justify-between">
                <span className="text-slate-500 text-[8px] font-mono uppercase tracking-widest">COSMIC CHRONOS BIAS</span>
                <span className="text-fuchsia-400 text-sm font-mono font-bold mt-1">{chronosLatency}s SCALE (FIXED)</span>
              </div>
              <div className="bg-black/60 p-3 rounded-xl border border-slate-900/80 flex flex-col col-span-2 justify-between">
                <span className="text-slate-500 text-[8px] font-mono uppercase tracking-widest">REAL-TIME CALIBRATION TELEMETRY FEED</span>
                <span className="text-emerald-400 text-[9px] font-mono truncate mt-1 animate-pulse uppercase">{tuneStatusLog}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 bg-[#04060d] border border-slate-800/80 rounded-2xl p-6 relative overflow-hidden backdrop-blur-sm">
          
          {/* Input Panel */}
          <div className="flex flex-col space-y-6 relative z-10">
            <div className="bg-black/60 p-1 border border-slate-700 rounded-lg flex flex-wrap gap-1">
               <button 
                 onClick={() => setCreationMode('IMAGE_TO_VIDEO')}
                 className={`flex-1 min-w-[70px] py-1.5 text-[10px] sm:text-xs font-mono uppercase tracking-widest transition-colors rounded ${creationMode === 'IMAGE_TO_VIDEO' ? 'bg-slate-800 text-cyan-400 font-bold' : 'text-slate-500 hover:text-slate-300'}`}
               >
                 Image → Video
               </button>
               <button 
                 onClick={() => setCreationMode('TEXT_TO_VIDEO')}
                 className={`flex-1 min-w-[70px] py-1.5 text-[10px] sm:text-xs font-mono uppercase tracking-widest transition-colors rounded ${creationMode === 'TEXT_TO_VIDEO' ? 'bg-slate-800 text-cyan-400 font-bold' : 'text-slate-500 hover:text-slate-300'}`}
               >
                 Text → Video
               </button>
               <button 
                 onClick={() => setCreationMode('EDIT_VIDEO')}
                 className={`flex-1 min-w-[70px] py-1.5 text-[10px] sm:text-xs font-mono uppercase tracking-widest transition-colors rounded ${creationMode === 'EDIT_VIDEO' ? 'bg-slate-800 text-cyan-400 font-bold' : 'text-slate-500 hover:text-slate-300'}`}
               >
                 Video → Video
               </button>
               <button 
                 onClick={() => {
                   setCreationMode('WORD_CLOUD');
                   setTuneStatusLog("WORD CLOUD DESIGN CORE ACTIVE. INITIALIZED ARCHIMEDEAN SPIRAL.");
                 }}
                 className={`flex-1 min-w-[105px] py-1.5 text-[10px] sm:text-xs font-mono uppercase tracking-widest transition-colors rounded ${creationMode === 'WORD_CLOUD' ? 'bg-gradient-to-r from-fuchsia-950 to-purple-950 border border-fuchsia-400/40 text-fuchsia-300 font-bold' : 'text-slate-500 hover:text-slate-300'}`}
               >
                 Word Cloud Design
               </button>
               <button 
                 onClick={() => {
                   setCreationMode('BULK_LOAD');
                   setTuneStatusLog("SECURE MULTI-FILE BULK ENGINE ENGAGED. READY FOR IMAGE / VIDEO DROP.");
                 }}
                 className={`flex-1 min-w-[115px] py-1.5 text-[10px] sm:text-xs font-mono uppercase tracking-widest transition-colors rounded ${creationMode === 'BULK_LOAD' ? 'bg-gradient-to-r from-emerald-950 to-teal-950 border border-emerald-400/40 text-emerald-300 font-bold' : 'text-slate-500 hover:text-slate-300'}`}
               >
                 Bulk Load Engine
               </button>
            </div>

            {creationMode === 'WORD_CLOUD' ? (
              <div className="flex flex-col space-y-5">
                {/* 1. STRATEGIC PALETTES & PRESETS */}
                <div className="bg-black/60 border border-slate-700/80 rounded-xl p-5 hover:border-fuchsia-500/50 transition-colors">
                   <h3 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-bold mb-4 flex items-center gap-2">
                     <Palette className="w-4 h-4 text-fuchsia-400" /> 1. CHOOSE DESIGN PRESETS
                   </h3>
                   <div className="grid grid-cols-3 gap-2">
                     <button 
                       onClick={() => loadPreset('screenshot')}
                       className="bg-[#050914] border border-slate-800 hover:border-orange-500/50 p-2.5 rounded text-center transition-colors flex flex-col items-center justify-center group"
                     >
                       <FileImage className="w-4 h-4 text-orange-400 mb-1 group-hover:scale-110 transition-transform" />
                       <span className="text-[9px] font-mono text-slate-300 font-bold">SCREENSHOT</span>
                       <span className="text-[7px] font-mono text-slate-500 uppercase leading-none mt-0.5">WARM BRONZE</span>
                     </button>
                     <button 
                       onClick={() => loadPreset('nexorax')}
                       className="bg-[#050914] border border-slate-800 hover:border-amber-500/50 p-2.5 rounded text-center transition-colors flex flex-col items-center justify-center group"
                     >
                       <Sparkles className="w-4 h-4 text-amber-400 mb-1 group-hover:scale-110 transition-transform" />
                       <span className="text-[9px] font-mono text-slate-300 font-bold">NEXORAX</span>
                       <span className="text-[7px] font-mono text-slate-500 uppercase leading-none mt-0.5">IMPERIAL GOLD</span>
                     </button>
                     <button 
                       onClick={() => loadPreset('tech')}
                       className="bg-[#050914] border border-slate-800 hover:border-cyan-500/50 p-2.5 rounded text-center transition-colors flex flex-col items-center justify-center group"
                     >
                       <Wand2 className="w-4 h-4 text-cyan-400 mb-1 group-hover:scale-110 transition-transform" />
                       <span className="text-[9px] font-mono text-slate-300 font-bold">DEEP TECH</span>
                       <span className="text-[7px] font-mono text-slate-500 uppercase leading-none mt-0.5">NEON CYBER</span>
                     </button>
                   </div>
                </div>

                {/* 2. LAYOUT & STYLE RULES */}
                <div className="bg-black/60 border border-slate-700/80 rounded-xl p-5 hover:border-cyan-500/50 transition-colors">
                   <h3 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-bold mb-4 flex items-center gap-2">
                     <Sliders className="w-4 h-4 text-cyan-400" /> 2. STYLE CONFIGURATOR
                   </h3>
                   <div className="grid grid-cols-2 gap-4">
                     {/* Font Family */}
                     <div className="flex flex-col space-y-1">
                       <label className="text-[9px] font-mono font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
                         <Type className="w-3" /> FONT SYSTEM:
                       </label>
                       <select 
                         value={wordCloudFont} 
                         onChange={(e: any) => setWordCloudFont(e.target.value)}
                         className="bg-[#050914] border border-slate-800 rounded px-2.5 py-1.5 text-[10px] font-mono text-slate-300 uppercase focus:outline-none focus:border-cyan-500"
                       >
                         <option value="sans-serif">Space Grotesk (Sans)</option>
                         <option value="serif">Georgia Elegant (Serif)</option>
                         <option value="monospace">JetBrains (Monospace)</option>
                       </select>
                     </div>

                     {/* Color Palette */}
                     <div className="flex flex-col space-y-1">
                       <label className="text-[9px] font-mono font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
                         <Palette className="w-3" /> COLORS:
                       </label>
                       <select 
                         value={wordCloudPalette} 
                         onChange={(e: any) => setWordCloudPalette(e.target.value)}
                         className="bg-[#050914] border border-slate-800 rounded px-2.5 py-1.5 text-[10px] font-mono text-slate-300 uppercase focus:outline-none focus:border-cyan-500"
                       >
                         <option value="warm">Warm Red (Screenshot)</option>
                         <option value="cyber">Cyan Cobalt (Cyberpunk)</option>
                         <option value="gold">Imperial Gold (Nexorax)</option>
                         <option value="silver">Cool Slate (Tactical)</option>
                       </select>
                     </div>

                     {/* Background Canvas Theme */}
                     <div className="flex flex-col space-y-1">
                       <label className="text-[9px] font-mono font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
                         <Layout className="w-3" /> BACKGROUND:
                       </label>
                       <select 
                         value={wordCloudBackground} 
                         onChange={(e: any) => setWordCloudBackground(e.target.value)}
                         className="bg-[#050914] border border-slate-800 rounded px-2.5 py-1.5 text-[10px] font-mono text-slate-300 uppercase focus:outline-none focus:border-cyan-500"
                       >
                         <option value="light">Off-White (High Contrast)</option>
                         <option value="dark">Deep Space (Neon Backlight)</option>
                         <option value="transparent">Transparent Alpha</option>
                       </select>
                     </div>

                     {/* Rotation Angles */}
                     <div className="flex flex-col space-y-1">
                       <label className="text-[9px] font-mono font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
                         <RefreshCw className="w-3" /> WORD ALIGNMENT:
                       </label>
                       <select 
                         value={wordCloudRotation} 
                         onChange={(e: any) => setWordCloudRotation(e.target.value)}
                         className="bg-[#050914] border border-slate-800 rounded px-2.5 py-1.5 text-[10px] font-mono text-slate-300 uppercase focus:outline-none focus:border-cyan-500"
                       >
                         <option value="horizontal">Horizontal Only</option>
                         <option value="balanced">Balanced (15% Vertical)</option>
                         <option value="crossed">Crossed Grid (50% Vert)</option>
                         <option value="vertical">Vertical Only</option>
                       </select>
                     </div>
                   </div>

                   {/* Slider settings */}
                   <div className="mt-5 space-y-4 pt-4 border-t border-slate-800/80">
                     <div className="flex flex-col space-y-1.5">
                       <div className="flex justify-between text-[9px] font-mono uppercase font-bold text-slate-400">
                         <span>MAX DISPLAY WORDS:</span>
                         <span className="text-cyan-400 font-bold">{wordCloudMaxWords}</span>
                       </div>
                       <input 
                         type="range" 
                         min="10" 
                         max="100" 
                         value={wordCloudMaxWords} 
                         onChange={(e) => setWordCloudMaxWords(parseInt(e.target.value))}
                         className="w-full select-none cursor-pointer accent-cyan-400"
                       />
                     </div>

                     <div className="grid grid-cols-2 gap-4">
                       <div className="flex flex-col space-y-1.5">
                         <div className="flex justify-between text-[9px] font-mono uppercase font-bold text-slate-400">
                           <span>MIN SIZE:</span>
                           <span className="text-cyan-400 font-bold">{wordCloudMinSize}px</span>
                         </div>
                         <input 
                           type="range" 
                           min="8" 
                           max="24" 
                           value={wordCloudMinSize} 
                           onChange={(e) => setWordCloudMinSize(parseInt(e.target.value))}
                           className="w-full cursor-pointer accent-cyan-400"
                         />
                       </div>

                       <div className="flex flex-col space-y-1.5">
                         <div className="flex justify-between text-[9px] font-mono uppercase font-bold text-slate-400">
                           <span>MAX SIZE:</span>
                           <span className="text-cyan-400 font-bold">{wordCloudMaxSize}px</span>
                         </div>
                         <input 
                           type="range" 
                           min="26" 
                           max="85" 
                           value={wordCloudMaxSize} 
                           onChange={(e) => setWordCloudMaxSize(parseInt(e.target.value))}
                           className="w-full cursor-pointer accent-cyan-400"
                         />
                       </div>
                     </div>
                   </div>
                </div>

                {/* 3. BULK HARVEST OR DIRECT EDIT */}
                <div className="bg-black/60 border border-slate-700/80 rounded-xl p-5 hover:border-cyan-500/50 transition-colors">
                   <h3 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-bold mb-3 flex items-center gap-2">
                     <Download className="w-4 h-4 text-emerald-400" /> 3. BULK REVENUE HARVEST & EXTRACTOR
                   </h3>
                   <div className="flex flex-col space-y-2.5">
                     <textarea 
                       placeholder="PASTE ANY STATEMENT, DICTIONARY, OR DECK PARAGRAPH HERE. VEO MATRIX WILL AUTOMATICALLY STRIP CONTEXTUAL STOPWORDS, EXTRACT ENTHUSIAST HIGH-DENSITY KEYWORDS, AND PLOT VISUALLY..."
                       value={bulkText}
                       onChange={(e) => setBulkText(e.target.value)}
                       rows={3}
                       className="w-full bg-[#050914] border border-slate-800 rounded p-3 text-[10px] font-mono focus:outline-none focus:border-cyan-400 text-slate-300 resize-none uppercase custom-scrollbar"
                     />
                     <button
                       onClick={handleBulkImport}
                       disabled={!bulkText.trim()}
                       className="w-full py-3 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-[10px] font-mono uppercase tracking-[0.2em] font-bold transition-colors rounded-lg flex items-center justify-center gap-2"
                     >
                       <RefreshCw className="w-3.5 h-3.5 animate-spin" /> CALCULATE COGNITIVE DENSITY & PLOT
                     </button>
                   </div>
                </div>

                {/* 4. TACTICAL WORD LIST & DIAL-IN */}
                <div className="bg-black/60 border border-slate-700/80 rounded-xl p-5 hover:border-cyan-500/50 transition-colors">
                   <h3 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-bold mb-4 flex items-center gap-2">
                     <Archive className="w-4 h-4 text-yellow-400" /> 4. DESIGN MATRIX WORD LEDGER
                   </h3>
                   
                   {/* Add single word */}
                   <div className="flex items-center space-x-2 mb-3">
                     <input 
                       type="text" 
                       value={newWordText}
                       onChange={(e) => setNewWordText(e.target.value)}
                       placeholder="INLINE SYMBOL..."
                       className="flex-1 bg-[#050914] border border-slate-800 rounded px-3 py-1.5 text-[10px] font-mono text-slate-300 focus:outline-none uppercase"
                       onKeyDown={(e) => { if (e.key === 'Enter') addIndividualWord(); }}
                     />
                     <div className="flex items-center bg-[#050914] border border-slate-800 rounded px-1.5 py-1 text-[10px] font-mono text-slate-400">
                       <span className="text-[8px] mr-1 text-slate-600">WT:</span>
                       <input 
                         type="number" 
                         min="1" 
                         max="150" 
                         value={newWordWeight}
                         onChange={(e) => setNewWordWeight(parseInt(e.target.value) || 10)}
                         className="w-10 bg-transparent text-center text-slate-300 focus:outline-none focus:ring-0 font-bold"
                       />
                     </div>
                     <button 
                       onClick={addIndividualWord}
                       disabled={!newWordText.trim()}
                       className="bg-cyan-500/10 hover:bg-cyan-500/25 border border-cyan-500/20 text-cyan-400 font-mono font-bold text-[10px] px-3.5 py-1.5 rounded transition uppercase flex items-center gap-1"
                     >
                       <Plus className="w-3.5 h-3.5" /> ADD
                     </button>
                   </div>

                   {/* Editable scrolling table */}
                   <div className="max-h-[220px] overflow-y-auto border border-slate-900 rounded-lg custom-scrollbar font-mono">
                     <table className="w-full text-[10px] text-left text-slate-400 uppercase">
                       <thead className="bg-[#050914] text-slate-500 sticky top-0 border-b border-slate-800">
                         <tr>
                           <th className="px-3 py-2 w-12 text-center text-[9px]">INDEX</th>
                           <th className="px-3 py-2 text-[9px]">SYMBOL TEXT</th>
                           <th className="px-3 py-2 w-16 text-center text-[9px]">WEIGHT</th>
                           <th className="px-3 py-2 w-8"></th>
                         </tr>
                       </thead>
                       <tbody className="divide-y divide-slate-800/40">
                         {wordCloudWords.map((item, idx) => (
                           <tr key={idx} className="hover:bg-slate-950/60 transition-colors">
                             <td className="px-3 py-2 text-center text-[9px] text-slate-600">{idx + 1}</td>
                             <td className="px-3 py-2 text-slate-200 font-bold max-w-[130px] truncate">{item.text}</td>
                             <td className="px-3 py-2 text-center">
                               <input 
                                 type="number" 
                                 value={item.weight}
                                 onChange={(e) => {
                                   const val = parseInt(e.target.value) || 5;
                                   setWordCloudWords(wordCloudWords.map((w, wIdx) => wIdx === idx ? { ...w, weight: val } : w));
                                   setRenderCloudTrigger(prev => prev + 1);
                                 }}
                                 className="w-12 bg-black/40 border border-slate-800 rounded text-center py-0.5 text-[10px] focus:outline-none text-slate-300 font-bold"
                               />
                             </td>
                             <td className="px-3 py-2 text-right">
                               <button 
                                 onClick={() => removeWord(idx)}
                                 className="text-rose-500 hover:text-rose-400 p-1 rounded hover:bg-rose-500/10 transition-all"
                               >
                                 <Trash2 className="w-3.5 h-3.5" />
                               </button>
                             </td>
                           </tr>
                         ))}
                       </tbody>
                     </table>
                   </div>
                   <div className="flex justify-between items-center mt-3 text-[9px] font-mono text-slate-500">
                     <span>WORDS CLASSED: {wordCloudWords.length}</span>
                     <button
                       onClick={() => {
                         setWordCloudWords([]);
                         setRenderCloudTrigger(prev => prev + 1);
                         setTuneStatusLog("CLEAR ALL CURRENT COMPASS MATRIX ENTRIES CONCLUDED");
                       }}
                       className="text-rose-400 hover:text-rose-300 uppercase tracking-widest font-black text-[8px] bg-rose-950/10 px-2 py-1 rounded border border-rose-500/20"
                     >
                       [RESET MATRIX]
                     </button>
                   </div>
                </div>
              </div>
            ) : creationMode === 'BULK_LOAD' ? (
              <div className="flex flex-col space-y-5">
                {/* 1. GLOBAL BULK CONFIG */}
                <div className="bg-black/60 border border-slate-700/80 rounded-xl p-5 hover:border-emerald-500/50 transition-colors">
                   <h3 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-bold mb-4 flex items-center gap-2">
                     <Settings className="w-4 h-4 text-emerald-400" /> 1. GLOBAL BATCH CONFIG
                   </h3>
                   <div className="flex flex-col space-y-3">
                     <div className="flex flex-col space-y-1">
                       <label className="text-[9px] font-mono font-bold text-slate-400 uppercase tracking-widest">
                         DEFAULT COGNITIVE MOTION PROMPT:
                       </label>
                       <textarea 
                         value={bulkGlobalPrompt}
                         onChange={(e) => setBulkGlobalPrompt(e.target.value)}
                         placeholder="Cinematic slow tracking shot, high compatibility, ultra HD..."
                         rows={3}
                         className="w-full bg-[#050914] border border-slate-800 rounded p-3 text-[11px] font-mono text-slate-300 focus:outline-none focus:border-emerald-400 resize-none uppercase custom-scrollbar"
                       />
                     </div>
                     <button
                       onClick={applyGlobalPromptToAll}
                       disabled={bulkItems.length === 0}
                       className="w-full py-2 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-[10px] font-mono uppercase tracking-[0.15em] font-bold transition-colors rounded-lg flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                     >
                       <Layers className="w-3.5 h-3.5" /> FORCE PROMPT BINDINGS TO ALL
                     </button>
                   </div>
                </div>

                {/* 2. DIRECT ASSET INJECTOR (DRAG AREA) */}
                <div className="bg-black/60 border border-slate-700/80 rounded-xl p-5 hover:border-emerald-500/50 transition-colors">
                   <h3 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-bold mb-4 flex items-center gap-2">
                     <Upload className="w-4 h-4 text-emerald-400" /> 2. LOAD DIRECT ASSETS
                   </h3>
                   
                   <div
                     onDragOver={handleBulkDragOver}
                     onDrop={handleBulkDrop}
                     onClick={() => bulkFileInputRef.current?.click()}
                     className="border-2 border-dashed border-slate-700/85 hover:border-emerald-500/80 rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer transition-colors bg-[#050812] group text-center"
                   >
                     <FolderOpen className="w-10 h-10 text-slate-500 group-hover:text-emerald-400 group-hover:scale-110 transition-all mb-3" />
                     <span className="text-white font-mono text-xs uppercase tracking-widest font-bold mb-1">DRAG & DROP SECURE ASSETS</span>
                     <span className="text-slate-500 font-mono text-[9px] tracking-wider uppercase mt-1">SUPPORTED FORMATS: IMAGE/* & VIDEO/*</span>
                     <button className="mt-4 px-4 py-1.5 bg-emerald-500/15 group-hover:bg-emerald-500/25 border border-emerald-500/20 text-emerald-400 text-[10px] font-mono uppercase font-black rounded tracking-widest transition-colors">
                       SELECT MULTIPLE FILES
                     </button>
                   </div>
                   <input 
                     type="file" 
                     ref={bulkFileInputRef} 
                     className="hidden" 
                     accept="image/*,video/*" 
                     multiple 
                     onChange={handleBulkFileUpload} 
                   />
                </div>

                {/* 3. BATCH TELEMETRY STATS */}
                <div className="bg-black/60 border border-slate-700/80 rounded-xl p-5 hover:border-emerald-500/50 transition-colors">
                   <h3 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-bold mb-4 flex items-center gap-2">
                      <Sliders className="w-4 h-4 text-yellow-500" /> 3. BATCH STATS METRICS
                   </h3>
                   <div className="grid grid-cols-2 gap-3 font-mono text-center uppercase">
                     <div className="bg-[#050914] p-3 rounded-lg border border-slate-900">
                       <div className="text-[8px] text-slate-500 tracking-widest">TOTAL FILES</div>
                       <div className="text-xl text-white font-black mt-1">{bulkItems.length}</div>
                     </div>
                     <div className="bg-[#050914] p-3 rounded-lg border border-slate-900">
                       <div className="text-[8px] text-slate-500 tracking-widest">ASSET COMPOSITION</div>
                       <div className="text-[9px] text-slate-300 font-bold mt-1.5 flex justify-center gap-2 font-mono">
                         <span className="text-cyan-400">{bulkItems.filter(i => i.type === 'image').length} IMAGES</span>
                         <span className="text-fuchsia-400">{bulkItems.filter(i => i.type === 'video').length} VIDEOS</span>
                       </div>
                     </div>
                   </div>
                </div>

                {/* COMMIT ACTION BUTTON */}
                <button
                  onClick={startBulkProcessing}
                  disabled={bulkItems.length === 0 || isBulkProcessing}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 disabled:from-slate-800 disabled:to-slate-900 disabled:opacity-45 disabled:cursor-not-allowed text-black font-black uppercase text-sm tracking-[0.2em] py-4 rounded-xl shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:shadow-[0_0_30px_rgba(16,185,129,0.5)] transition flex items-center justify-center gap-3"
                >
                  {isBulkProcessing ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> EXECUTING BATCH PROCESS...</>
                  ) : (
                    <><Wand2 className="w-5 h-5" /> AUTHORIZE BATCH BINDINGS & COMPILE</>
                  )}
                </button>
              </div>
            ) : (
              <>
                {creationMode === 'EDIT_VIDEO' && (
                  <div className="bg-black/60 border border-slate-700/80 rounded-xl p-5 hover:border-cyan-500/50 transition-colors">
                     <h3 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-bold mb-4 flex items-center gap-2">
                       <Video className="w-4 h-4 text-cyan-400" /> 1. SELECT RENDER TO EXTEND
                     </h3>
                     <div className="flex flex-col space-y-4">
                       <select 
                         value={selectedVideoId}
                         onChange={(e) => setSelectedVideoId(e.target.value)}
                         className="bg-[#050914] border border-slate-800 rounded px-3 py-2 text-slate-300 font-mono text-[10px] focus:outline-none focus:border-cyan-400 transition uppercase"
                       >
                         <option value="">SELECT RENDER FROM VAULT...</option>
                         {vault.filter(v => v.videoStruct).map(v => (
                           <option key={v.id} value={v.id}>{v.id} - {v.prompt.substring(0, 30)}...</option>
                         ))}
                       </select>
                       {vault.filter(v => v.videoStruct).length === 0 && (
                         <div className="text-slate-500 text-[10px] font-mono tracking-widest uppercase">
                           No compatible renders found. Generate a video first.
                         </div>
                       )}
                     </div>
                  </div>
                )}
              
                {creationMode === 'IMAGE_TO_VIDEO' && (
                  <div className="bg-black/60 border border-slate-700/80 rounded-xl p-5 hover:border-cyan-500/50 transition-colors" onPaste={handlePaste}>
                     <h3 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-bold mb-4 flex items-center gap-2">
                       <ImageIcon className="w-4 h-4 text-cyan-400" /> 1. SOURCE ASSET (OPTIONAL)
                     </h3>
                     
                     {imagePreview ? (
                       <div className="flex flex-col space-y-2">
                         <div className="flex justify-between items-center mb-1">
                           <button
                             onClick={() => setIsHighlighterMode(!isHighlighterMode)}
                             className={`text-[10px] font-mono px-3 py-1 rounded transition-colors uppercase font-bold tracking-widest ${isHighlighterMode ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
                           >
                             {isHighlighterMode ? 'Cancel Highlighter Mask' : '+ Highlighter Mask (Edit)'}
                           </button>
                           {isHighlighterMode && (
                             <div className="flex items-center space-x-2">
                               <input type="range" min="5" max="50" value={lineWidth} onChange={(e) => setLineWidth(parseInt(e.target.value))} className="w-20" />
                               <button onClick={clearMask} className="text-[10px] text-rose-400 hover:text-rose-300 uppercase tracking-widest font-mono">Clear</button>
                             </div>
                           )}
                         </div>
                         <div className="relative group rounded-md overflow-hidden border border-slate-700 max-h-[300px] flex justify-center bg-black/80 select-none">
                           <img 
                             src={imagePreview} 
                             alt="Upload preview" 
                             className="max-h-[300px] object-contain relative z-10 pointer-events-none" 
                             onLoad={(e) => {
                               if (maskCanvasRef.current) {
                                 maskCanvasRef.current.width = e.currentTarget.width;
                                 maskCanvasRef.current.height = e.currentTarget.height;
                               }
                             }}
                           />
                           <canvas
                             ref={maskCanvasRef}
                             onMouseDown={startDrawing}
                             onMouseMove={draw}
                             onMouseUp={stopDrawing}
                             onMouseOut={stopDrawing}
                             onTouchStart={startDrawing}
                             onTouchMove={draw}
                             onTouchEnd={stopDrawing}
                             className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 touch-none ${isHighlighterMode ? 'cursor-crosshair' : 'pointer-events-none'}`}
                             style={{ 
                                width: maskCanvasRef.current ? maskCanvasRef.current.width : '100%', 
                                height: maskCanvasRef.current ? maskCanvasRef.current.height : '100%',
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain'
                             }}
                           />
                           <div className="absolute top-2 left-2 z-20 pointer-events-none flex items-center space-x-2 bg-black/50 backdrop-blur-md px-1.5 py-1 rounded border border-white/10 opacity-100 shadow-lg scale-75 origin-top-left">
                             <img src="/logo.png" alt="Nexorax Logo" className="w-4 h-4 object-contain" />
                             <div className="flex flex-col">
                               <span className="text-white font-sans font-black tracking-[0.2em] text-[8px] uppercase leading-none drop-shadow-lg">NEXORAX</span>
                               <span className="text-cyan-400 font-sans font-bold tracking-[0.3em] text-[5px] uppercase leading-none drop-shadow-lg mt-[1px]">DIGITAL</span>
                             </div>
                           </div>
                           {!isHighlighterMode && (
                             <div className="absolute inset-0 z-30 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                               <button 
                                 onClick={() => { setImageFile(null); setImagePreview(null); setImageUrlFromInput(''); clearMask(); setIsHighlighterMode(false); }}
                                 className="bg-rose-500/80 text-white font-mono text-[10px] px-4 py-2 uppercase tracking-widest font-black rounded backdrop-blur hover:bg-rose-500 transition pointer-events-auto"
                               >
                                 CLEAR ASSET
                               </button>
                             </div>
                           )}
                         </div>
                       </div>
                     ) : (
                        <div className="flex flex-col space-y-4">
                          <div 
                            onClick={() => fileInputRef.current?.click()}
                            className="border-2 border-dashed border-slate-700 hover:border-cyan-500/80 rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer transition-colors bg-[#080c17]"
                          >
                            <Upload className="w-8 h-8 text-slate-500 mb-3" />
                            <span className="text-white font-mono text-xs uppercase tracking-widest font-bold mb-1">Upload Start Frame</span>
                            <span className="text-slate-500 font-mono text-[10px] tracking-wider uppercase text-center focus:outline-none mt-2">Upload to access Highlighter Mask & Drawing capabilities</span>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <input 
                              type="text" 
                              value={imageUrl}
                              onChange={(e) => setImageUrlFromInput(e.target.value)}
                              placeholder="OR PASTE IMAGE URL HERE..."
                              className="flex-1 bg-[#050914] border border-slate-800 rounded px-3 py-2 text-slate-300 font-mono text-[10px] focus:outline-none focus:border-cyan-400 transition uppercase"
                              onKeyDown={(e) => { if(e.key === 'Enter') loadFromUrl(); }}
                            />
                            <button 
                              onClick={loadFromUrl}
                              disabled={!imageUrl}
                              className="bg-cyan-500/20 hover:bg-cyan-500/40 text-cyan-400 border border-cyan-500/30 disabled:opacity-50 font-mono font-bold text-[10px] px-3 py-2 rounded transition uppercase tracking-widest"
                            >
                              LOAD
                            </button>
                          </div>
                        </div>
                     )}
                     <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageUpload} />
                  </div>
                )}

                <div className="bg-black/60 border border-slate-700/80 rounded-xl p-5 hover:border-cyan-500/50 transition-colors">
                   <h3 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-bold mb-4 flex justify-between items-center">
                     <span>2. MOTION DIRECTIVE</span>
                     <div className="flex gap-1.5 items-center">
                       <button 
                         type="button"
                         onClick={async () => {
                           try {
                             const text = await navigator.clipboard.readText();
                             if (text) {
                               setPrompt(text);
                               setTuneStatusLog("CLIPBOARD DIRECTIVE SECURED SUCCESSFULLY");
                             } else {
                               setTuneStatusLog("CLIPBOARD LOAD RETRIEVED AN EMPTY STRING");
                             }
                           } catch (err) {
                             setTuneStatusLog("CLIPBOARD PERMISSION BLOCK. USE CTRL+V DIRECTLY IN TEXTAREA.");
                           }
                         }}
                         className="flex items-center gap-1 text-cyan-400 hover:text-cyan-300 text-[9px] bg-cyan-500/10 border border-cyan-500/20 px-2 py-1 rounded transition-colors uppercase font-bold tracking-wider"
                       >
                         PASTE FROM CLIPBOARD
                       </button>
                       <button 
                         onClick={enhancePrompt} 
                         disabled={isEnhancing || !prompt.trim()}
                         className="flex items-center gap-1 text-cyan-400 hover:text-cyan-300 disabled:opacity-50 text-[10px] bg-cyan-500/10 px-2 py-1 rounded"
                       >
                         {isEnhancing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                         ENHANCE
                       </button>
                     </div>
                   </h3>
                   <textarea 
                     className="w-full bg-[#050914] border border-slate-800 rounded p-4 text-slate-300 font-mono text-xs focus:outline-none focus:border-cyan-400 transition resize-none custom-scrollbar uppercase"
                     rows={5}
                     value={prompt}
                     onChange={e => setPrompt(e.target.value)}
                     placeholder="PASTE OR ENTER DIRECTIVE here... EX: CINEMATIC SLOW PAN FORWARD, NEON LIGHTING, VERY DETAILED..."
                   />
                   
                   {/* Out-of-this-universe premium cinematic prompt presets */}
                   <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
                     <span className="text-[8px] font-mono font-bold text-slate-500 uppercase tracking-widest mr-1">NEURAL PRESETS:</span>
                     {[
                       { label: 'COSMIC STARFIELD', prompt: 'Cinematic 4k slow motion pan across of a breathtaking intergalactic starfield with deep dark cosmic gases, dust columns, and beautiful newborn glowing orange star clusters' },
                       { label: 'WARP TUNNEL', prompt: 'Hyper-detailed first-person speed warp stream through a high-contrast neon cobalt wormhole grid, geometric digital lines floating in endless 3D parallax' },
                       { label: 'SUPERNOVA SHOCK', prompt: 'Ultra realistic volumetric render of a cosmic supernova explosion, bright glowing white center radiating beautiful high-entropy crimson plasma shockwaves in dark space' },
                       { label: 'HOLOGRAM MATRIX', prompt: 'High coherence scanning hologram of Nexorax matrix grid systems, floating cyan coordinate indicators list, glitching digital numbers, out-of-this-universe computer interface' }
                     ].map((p, pidx) => (
                       <button 
                         key={pidx}
                         type="button"
                         onClick={() => {
                           setPrompt(p.prompt);
                           setTuneStatusLog(`COSMIC PRESET LOADED: ${p.label}`);
                         }}
                         className="text-[8px] font-mono text-slate-400 hover:text-cyan-300 bg-slate-900 border border-slate-800 hover:border-cyan-500/30 px-2 py-0.5 rounded transition-all uppercase"
                       >
                         {p.label}
                       </button>
                     ))}
                   </div>
                </div>

                {/* 3. BUSINESS BRAND LOGO DESIGN MANAGER */}
                <div className="bg-black/60 border border-slate-700/80 rounded-xl p-5 hover:border-cyan-500/50 transition-colors">
                   <h3 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-bold mb-4 flex justify-between items-center">
                     <span>3. BRAND LOGO MANAGER</span>
                     {customLogoUrl && (
                       <button
                         type="button"
                         onClick={() => {
                           setCustomLogoUrl(null);
                           setCustomLogoName(null);
                           setTuneStatusLog("CUSTOM BUSINESS LOGO FLUSHED AND RESET TO NEXORAX DIRECT");
                         }}
                         className="text-[8px] text-rose-400 hover:text-rose-300 uppercase tracking-widest font-mono font-black border border-rose-500/20 bg-rose-950/10 px-2 py-0.5 rounded"
                       >
                         [RESET]
                       </button>
                     )}
                   </h3>

                   {customLogoUrl ? (
                     <div className="flex items-center space-x-3 p-3 bg-slate-900/60 rounded-lg border border-cyan-500/30">
                       <img 
                         src={customLogoUrl} 
                         alt="Custom Logo Thumbnail" 
                         className="w-12 h-12 object-contain bg-black rounded p-1 border border-slate-800"
                       />
                       <div className="flex-1 min-w-0">
                         <span className="text-[10px] text-cyan-400 font-mono uppercase font-black tracking-wider block truncate">Loaded Brand Logo</span>
                         <span className="text-[8px] text-slate-400 font-mono block truncate uppercase">{customLogoName || 'base64_encoded_vector.png'}</span>
                       </div>
                       <div className="flex flex-col space-y-1">
                         <button 
                           type="button"
                           onClick={() => logoInputRef.current?.click()}
                           className="text-[8px] font-mono font-bold bg-slate-800 hover:bg-slate-700 text-slate-300 px-2 py-1 rounded transition uppercase"
                         >
                           Change
                         </button>
                       </div>
                     </div>
                   ) : (
                     <div 
                       onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setLogoDragActive(true); }}
                       onDragLeave={() => setLogoDragActive(false)}
                       onDrop={handleLogoDrop}
                       onClick={() => logoInputRef.current?.click()}
                       className={`border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer transition-colors bg-[#080c17] text-center
                         ${logoDragActive ? 'border-cyan-400 bg-cyan-950/10' : 'border-slate-800 hover:border-cyan-500/40'}
                       `}
                     >
                       <Upload className={`w-6 h-6 mb-2 transition-colors ${logoDragActive ? 'text-cyan-400' : 'text-slate-500'}`} />
                       <span className="text-white font-mono text-[10px] uppercase tracking-widest font-bold">Upload Custom Business Logo</span>
                       <span className="text-slate-500 font-mono text-[8.5px] mt-1.5 uppercase leading-none">Supports PNG, JPG, JPEG (Drag & Drop Active)</span>
                     </div>
                   )}
                   <input 
                     type="file" 
                     ref={logoInputRef} 
                     className="hidden" 
                     accept="image/*" 
                     onChange={handleLogoUpload} 
                   />
                </div>

                {/* 4. OUT-OF-THIS-UNIVERSE NEXT-GEN NEURAL PARAMETERS */}
                <div className="bg-black/60 border border-slate-700/80 rounded-xl p-5 hover:border-cyan-500/50 transition-colors">
                   <h3 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-bold mb-4 flex justify-between items-center">
                     <span className="flex items-center gap-1.5"><Cpu className="w-4 h-4 text-cyan-400 animate-pulse" /> 4. QUANTUM CO-ENGINE TUNING</span>
                     <span className="text-[7.5px] font-mono text-cyan-400 border border-cyan-500/20 px-1.5 py-0.5 rounded uppercase font-bold tracking-widest bg-cyan-950/20">PREVIEW PARAMETERS</span>
                   </h3>

                   <div className="space-y-4">
                      {/* Space Matrix tint selector */}
                      <div className="flex flex-col space-y-1">
                         <span className="text-[8.5px] font-mono font-bold text-slate-500 uppercase tracking-widest">CYBER SPACE VECTOR TINT</span>
                         <div className="flex gap-1.5">
                           {(['cyan', 'amber', 'pink', 'emerald', 'rose'] as const).map(color => (
                             <button
                               key={color}
                               type="button"
                               onClick={() => {
                                 setSynthesisMatrixColor(color);
                                 setTuneStatusLog(`SPACE MATRIX WAVEFORM SHIFTED: ${color.toUpperCase()}`);
                               }}
                               className={`flex-1 text-[8px] font-mono py-1.5 rounded border uppercase font-bold transition-all
                                 ${synthesisMatrixColor === color 
                                   ? 'bg-cyan-500/10 border-cyan-400 text-cyan-300 font-extrabold shadow-[0_0_10px_rgba(6,182,212,0.15)]' 
                                   : 'bg-[#050914] border-slate-800 text-slate-500 hover:text-slate-300 hover:border-slate-700'}
                               `}
                             >
                               {color}
                             </button>
                           ))}
                         </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3.5">
                        <div className="flex flex-col space-y-1">
                           <div className="flex justify-between text-[8px] font-mono uppercase font-black text-slate-500">
                             <span>DIFFUSION STEPS:</span>
                             <span className="text-cyan-400 font-bold">{synthesisDiffusionSteps}</span>
                           </div>
                           <input 
                             type="range"
                             min="20"
                             max="150"
                             step="5"
                             value={synthesisDiffusionSteps}
                             onChange={(e) => setSynthesisDiffusionSteps(parseInt(e.target.value))}
                             className="w-full accent-cyan-500 cursor-pointer"
                           />
                        </div>

                        <div className="flex flex-col space-y-1">
                           <div className="flex justify-between text-[8px] font-mono uppercase font-black text-slate-500">
                             <span>TEMPORAL CONSISTENCY:</span>
                             <span className="text-cyan-400 font-bold">{synthesisTemporalConsistency}%</span>
                           </div>
                           <input 
                             type="range"
                             min="20"
                             max="100"
                             value={synthesisTemporalConsistency}
                             onChange={(e) => setSynthesisTemporalConsistency(parseInt(e.target.value))}
                             className="w-full accent-cyan-500 cursor-pointer"
                           />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3.5">
                        <div className="flex flex-col space-y-1">
                           <div className="flex justify-between text-[8px] font-mono uppercase font-black text-slate-500">
                             <span>ORBIT SYNTH VELOCITY:</span>
                             <span className="text-cyan-400 font-bold">{synthesisOrbitSpeed.toFixed(1)}x</span>
                           </div>
                           <input 
                             type="range"
                             min="0.5"
                             max="10"
                             step="0.5"
                             value={synthesisOrbitSpeed}
                             onChange={(e) => setSynthesisOrbitSpeed(parseFloat(e.target.value))}
                             className="w-full accent-cyan-500 cursor-pointer"
                           />
                        </div>

                        <div className="flex flex-col space-y-1">
                           <div className="flex justify-between text-[8px] font-mono uppercase font-black text-slate-500">
                             <span>COSMOS PARTICLE DENSITY:</span>
                             <span className="text-cyan-400 font-bold">{synthesisParticleDensity}</span>
                           </div>
                           <input 
                             type="range"
                             min="50"
                             max="500"
                             step="25"
                             value={synthesisParticleDensity}
                             onChange={(e) => setSynthesisParticleDensity(parseInt(e.target.value))}
                             className="w-full accent-cyan-500 cursor-pointer"
                           />
                        </div>
                      </div>

                      {/* Interactive Motion Interpolation Ease Control */}
                      <div className="flex flex-col space-y-1">
                         <span className="text-[8.5px] font-mono font-bold text-slate-500 uppercase tracking-widest flex justify-between">
                           <span>MOTION INTERPOLATION EASE SETTING</span>
                           <span className="text-cyan-400 font-bold">{synthesisEaseSetting.replace('_', ' ')}</span>
                         </span>
                         <div className="flex gap-1.5">
                           {(['LINEAR', 'EASE_IN', 'EASE_OUT', 'EASE_IN_OUT'] as const).map(ease => (
                             <button
                               key={ease}
                               type="button"
                               onClick={() => {
                                 setSynthesisEaseSetting(ease);
                                 setTuneStatusLog(`MOTION INTERPOLATION CALIBRATED: ${ease}`);
                               }}
                               className={`flex-1 text-[7.5px] font-mono py-1 rounded border uppercase font-bold transition-all
                                 ${synthesisEaseSetting === ease 
                                   ? 'bg-cyan-400/20 border-cyan-400 text-cyan-300 font-extrabold shadow-[0_0_8px_rgba(34,211,238,0.2)]' 
                                   : 'bg-[#050914] border-slate-800 text-slate-500 hover:text-slate-300 hover:border-slate-700'}
                               `}
                             >
                               {ease.replace('_', ' ')}
                             </button>
                           ))}
                         </div>
                      </div>

                      {/* Interactive Fallback local simulator engagement button */}
                      <button
                        type="button"
                        onClick={() => {
                          const nextState = !isSimulatingSynthesis;
                          setIsSimulatingSynthesis(nextState);
                          if (nextState) {
                            setVideoUrl(null);
                            setTuneStatusLog("LOCAL CORE SIMULATOR ENGAGED. OUT-OF-THIS-UNIVERSE MODE ON.");
                          } else {
                            setTuneStatusLog("LOCAL CORE SIMULATOR ENGAGED IN STANDBY PROFILE.");
                          }
                        }}
                        className={`w-full font-mono text-[9px] py-2.5 rounded-lg border font-bold uppercase transition-all tracking-[0.05em] flex items-center justify-center gap-1.5
                          ${isSimulatingSynthesis
                            ? 'bg-cyan-500 text-black border-cyan-400 shadow-[0_0_20px_rgba(6,182,212,0.4)] hover:bg-cyan-400'
                            : 'bg-gradient-to-r from-cyan-950 to-blue-950 hover:from-cyan-900 hover:to-blue-900 text-cyan-400 border-cyan-500/20 hover:border-cyan-500/40'}
                        `}
                      >
                        <Sparkles className="w-3.5 h-3.5 animate-spin-slow" />
                        {isSimulatingSynthesis 
                          ? "DISENGAGE LOCAL SYNTHETIC UNIVERSE SIMULATOR" 
                          : "ENGAGE LOCAL INTERACTIVE SYNTHETIC PREVIEW"}
                      </button>
                   </div>
                </div>

                <button
                  onClick={generateVideo}
                  disabled={isGenerating || !prompt.trim() || (creationMode === 'EDIT_VIDEO' && !selectedVideoId)}
                  className="w-full bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-black uppercase text-sm tracking-[0.2em] py-4 rounded-xl shadow-[0_0_20px_rgba(34,211,238,0.3)] hover:shadow-[0_0_30px_rgba(34,211,238,0.5)] transition flex items-center justify-center gap-3"
                >
                  {isGenerating ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> FORGING RENDER...</>
                  ) : (
                    <><Video className="w-5 h-5" /> INITIALIZE CINEMATIC RENDER</>
                  )}
                </button>
              </>
            )}
          </div>
 
          {/* Output Panel */}
          <div className="bg-black/60 border border-slate-700/80 rounded-xl flex flex-col p-5 relative min-h-[400px]">
             <h3 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-bold mb-4 flex items-center justify-between">
                <span className="flex items-center gap-2"><Play className="w-4 h-4 text-yellow-400" /> RENDERED OUTPUT</span>
                {videoUrl && <span className="flex items-center gap-1 text-emerald-400"><CheckCircle className="w-3 h-3"/> READY</span>}
             </h3>
 
             {creationMode === 'WORD_CLOUD' ? (
                <div className="flex-1 flex flex-col space-y-4 w-full">
                  {/* Canvas Container */}
                  <div className="flex-1 rounded-lg bg-[#050914] border border-slate-800 p-4 flex flex-col items-center justify-center relative min-h-[350px]">
                     <canvas 
                       ref={wordCloudCanvasRef}
                       width={600}
                       height={400}
                       className="max-w-full h-auto object-contain rounded-lg border border-slate-900/60 shadow-[0_4px_30px_rgba(0,0,0,0.5)] bg-slate-950"
                     />
                     <div className="absolute top-3 left-3 bg-fuchsia-950/70 border border-fuchsia-500/30 px-2 py-1 rounded text-[8px] font-mono uppercase tracking-[0.2em] font-bold text-fuchsia-300 backdrop-blur">
                       LIVE CANVAS PREVIEW
                     </div>
                  </div>

                  {/* Actions Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full">
                     <button
                       onClick={() => {
                         setRenderCloudTrigger(prev => prev + 1);
                         setTuneStatusLog("ARCHIMEDEAN SPIRAL RE-SHUFFLED SUCCESSFULLY");
                       }}
                       className="w-full bg-[#11162d] border border-slate-800 hover:border-cyan-500/50 text-cyan-400 font-mono font-bold text-[10px] sm:text-xs uppercase tracking-widest py-3 rounded-xl transition flex items-center justify-center gap-2"
                     >
                       <RefreshCw className="w-4 h-4 text-cyan-400 animate-spin-slow" /> Shuffle Layout
                     </button>
                     <button
                       onClick={downloadWordCloudPNG}
                       className="w-full bg-[#11162d] border border-slate-800 hover:border-fuchsia-500/50 text-fuchsia-400 font-mono font-bold text-[10px] sm:text-xs uppercase tracking-widest py-3 rounded-xl transition flex items-center justify-center gap-2"
                     >
                       <Download className="w-4 h-4 text-fuchsia-400" /> Save High-Res PNG
                     </button>
                     <button
                       onClick={seedToVideoEngine}
                       className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-black font-black uppercase text-[10px] sm:text-xs tracking-widest py-3 rounded-xl shadow-[0_0_15px_rgba(34,211,238,0.25)] transition flex items-center justify-center gap-2"
                     >
                       <Video className="w-4 h-4" /> Seed Video Forge
                     </button>
                  </div>
                </div>
             ) : creationMode === 'BULK_LOAD' ? (
                <div className="flex-1 flex flex-col space-y-4 w-full">
                  {/* BULK ASSET PREVIEW GRID / TELEMETRY */}
                  <div className="flex-1 rounded-lg bg-[#050914] border border-slate-800 p-4 overflow-y-auto max-h-[460px] custom-scrollbar">
                    <h4 className="text-[10px] font-mono uppercase tracking-[0.2em] font-medium text-emerald-400 mb-3 flex items-center justify-between">
                      <span>BATCH ENGINE QUEUE ({bulkItems.length} ITEMS)</span>
                      {bulkItems.length > 0 && (
                        <button 
                          onClick={clearBulkQueue} 
                          className="text-rose-400 hover:text-rose-300 font-bold uppercase text-[8px] tracking-widest px-2 py-1 bg-rose-950/10 rounded border border-rose-500/20"
                        >
                          [RESET QUEUE]
                        </button>
                      )}
                    </h4>

                    {bulkItems.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-20 text-slate-500 text-center uppercase">
                        <Layers className="w-8 h-8 opacity-30 mb-3" />
                        <span className="text-[9px] font-mono tracking-widest font-bold">QUEUE IS CURRENTLY VACANT</span>
                        <span className="text-[8px] font-mono text-slate-600 mt-1">LOAD FILES ON THE LEFT PANEL TO BEGIN PROCESS</span>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {bulkItems.map((item, idx) => (
                          <div key={item.id} className="bg-black/40 border border-slate-900 rounded-lg p-3 hover:border-slate-800 transition-colors flex items-center gap-3">
                            <div className="relative w-14 h-14 bg-black rounded border border-slate-800 overflow-hidden flex items-center justify-center shrink-0">
                              {item.type === 'image' ? (
                                <img src={item.previewUrl} className="w-full h-full object-cover" alt="preview" />
                              ) : (
                                <div className="text-cyan-400 font-bold font-mono text-2xs uppercase">VID</div>
                              )}
                              <span className="absolute bottom-0 text-center w-full z-10 bg-black/70 text-[7px] font-mono text-slate-400 uppercase py-0.5 border-t border-slate-900 leading-none">
                                {item.type}
                              </span>
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="font-mono text-[10px] text-slate-300 truncate font-semibold block uppercase">
                                  {item.name}
                                </span>
                                <span className="font-mono text-[8px] text-slate-500 shrink-0">
                                  {item.size}
                                </span>
                              </div>

                              {/* Item Prompt override input */}
                              <input 
                                type="text"
                                value={item.prompt}
                                onChange={(e) => {
                                  const updated = [...bulkItems];
                                  updated[idx].prompt = e.target.value;
                                  setBulkItems(updated);
                                }}
                                placeholder="Edit motion prompt for this asset..."
                                className="w-full bg-[#03060c] border border-slate-900 focus:border-slate-850 rounded px-2 py-1 text-slate-400 font-mono text-[9px] uppercase mb-1.5 focus:outline-none"
                              />

                              {/* Progress bar and status */}
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-1.5 font-mono text-[8px] uppercase tracking-widest font-black">
                                  {item.status === 'pending' && <span className="text-slate-500">PENDING QUEUE</span>}
                                  {item.status === 'generating' && (
                                    <span className="text-yellow-400 flex items-center gap-1">
                                      <Loader2 className="w-2.5 h-2.5 animate-spin" /> GENERATING... {item.progress || 0}%
                                    </span>
                                  )}
                                  {item.status === 'completed' && <span className="text-emerald-400">COMPLETED</span>}
                                  {item.status === 'failed' && <span className="text-rose-400">FAILED</span>}
                                </div>

                                {item.status === 'completed' && item.resultUrl && (
                                  <a 
                                    href={item.resultUrl} 
                                    download={`nexorax_bulk_${item.id}.mp4`}
                                    className="text-[8px] font-mono text-cyan-400 hover:text-cyan-300 underline font-bold uppercase transition"
                                  >
                                    [DOWNLOAD MP4]
                                  </a>
                                )}

                                {item.status === 'pending' && (
                                  <button 
                                    onClick={() => removeBulkItem(item.id)}
                                    className="text-rose-500 hover:text-rose-400 p-0.5 rounded transition"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* REAL-TIME BATCH TELEMETRY TERMINAL */}
                  <div className="bg-[#02050b] border border-slate-900 rounded-lg p-3 font-mono text-[9px] uppercase text-emerald-500/80 custom-scrollbar overflow-y-auto max-h-[140px] flex-shrink-0">
                     <div className="text-white text-[9px] font-bold tracking-widest border-b border-slate-900 pb-1.5 select-none uppercase tracking-[0.15em] mb-1">
                       BATCH ENGINE CONSOLE
                     </div>
                     <div className="space-y-0.5 whitespace-pre-wrap leading-tight font-mono tracking-normal text-[9px] text-[#10b981]">
                       {bulkLog || "[ENGINE IN STANDBY. SECURE INTERFACES ALIGNED]"}
                     </div>
                  </div>
                </div>
             ) : (
               <div className="flex-1 flex flex-col space-y-4 w-full">
                 <div className="flex-1 rounded-lg bg-[#050914] border border-slate-800 overflow-hidden flex flex-col items-center justify-center relative group">
                {isSimulatingSynthesis ? (
                  <div className="relative w-full h-full flex flex-col bg-[#010409]">
                    <div className="relative flex-1 w-full h-full flex items-center justify-center">
                      <canvas 
                        ref={synCanvasRef}
                        width={640}
                        height={360}
                        className="w-full h-full object-contain relative z-10 bg-black"
                      />
                      
                      {/* Hidden image element to act as resource loader for custom business logo drawing onto canvas */}
                      {customLogoUrl && (
                        <img 
                          ref={(el) => {
                            logoImageRef.current = el;
                          }}
                          src={customLogoUrl} 
                          alt="Canvas Logo Asset" 
                          className="hidden" 
                        />
                      )}
                      
                      {/* Floating UI overlay for Simulator Controls */}
                      <div className="absolute top-4 left-4 z-20 flex space-x-2 bg-black/60 backdrop-blur-md border border-cyan-500/30 px-2.5 py-1 rounded select-none">
                        <span className="text-[7.5px] font-mono text-cyan-400 font-extrabold uppercase tracking-widest animate-pulse">NEURAL CORE PREVIEW ACTIVE</span>
                      </div>

                      {/* Optional embedded watermark overlay */}
                      {embedWatermark && (
                        <div 
                          className={`absolute z-20 pointer-events-none flex items-center space-x-3 bg-black/65 backdrop-blur-md rounded-lg border border-cyan-500/20 opacity-100 shadow-[0_0_15px_rgba(0,0,0,0.6)] transition-all duration-300
                            ${watermarkPosition === 'top-left' ? 'top-4 left-4' : ''}
                            ${watermarkPosition === 'top-right' ? 'top-4 right-4' : ''}
                            ${watermarkPosition === 'bottom-left' ? 'bottom-4 left-4' : ''}
                            ${watermarkPosition === 'bottom-right' ? 'bottom-4 right-4' : ''}
                            ${watermarkSize === 'small' ? 'px-2 py-1.5' : 'px-3.5 py-2.5'}
                            ${watermarkSize === 'large' ? 'px-4.5 py-3.5' : ''}
                          `}
                          style={{ opacity: watermarkOpacity }}
                        >
                          <img 
                            src={customLogoUrl || "/logo.png.jpg"} 
                            alt="Branded Logo Overlay" 
                            onError={(e) => {
                              e.currentTarget.src = "/logo.png";
                            }}
                            className={`object-contain
                              ${watermarkSize === 'small' ? 'w-5 h-5' : 'w-8 h-8'}
                              ${watermarkSize === 'large' ? 'w-11 h-11' : ''}
                            `} 
                          />
                          <div className="flex flex-col text-left font-mono">
                            <span className={`text-cyan-400 font-sans font-black tracking-[0.05em] uppercase leading-tight drop-shadow-lg
                              ${watermarkSize === 'small' ? 'text-[9px]' : 'text-[12px]'}
                              ${watermarkSize === 'large' ? 'text-[14px]' : ''}
                            `}>{customLogoName ? customLogoName.split('.')[0] : "Nexorax Digital"}</span>
                            <span className="text-white text-[7px] leading-none drop-shadow-sm mt-0.5">SECURE MATRIX ALIGNED</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Action Bar for simulation */}
                    <div className="absolute top-4 right-4 z-30 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                       <button 
                         onClick={() => {
                           const prebuilts = ['/brain1.mp4', '/brain2.mp4', '/brain4.mp4', '/words1.mp4', '/brain9.mp4', '/brain10.mp4'];
                           const selectedPrebuilt = prebuilts[Math.floor(Math.random() * prebuilts.length)];
                           const snapId = 'SNAP-' + Math.random().toString(36).substr(2, 5).toUpperCase();
                           const snapItem: RenderedVideo = {
                             id: snapId,
                             url: selectedPrebuilt,
                             prompt: prompt + ` [SIMULATED ${synthesisMatrixColor.toUpperCase()} WAVEFORM]`,
                             timestamp: new Date().toISOString(),
                             modelConfig: {
                               diffusionSteps: synthesisDiffusionSteps,
                               temporalConsistency: synthesisTemporalConsistency,
                               orbitSpeed: synthesisOrbitSpeed,
                               particleDensity: synthesisParticleDensity,
                               matrixColor: synthesisMatrixColor,
                               easeSetting: synthesisEaseSetting
                             }
                           };
                           saveVideoToDB(snapItem).catch(err => console.error("Error saving captured snap:", err)); setVault(prev => [snapItem, ...prev]);
                           setActiveVideoObject(snapItem);
                           setVideoUrl(selectedPrebuilt);
                           setTuneStatusLog(`SNAP-SHOT ${snapId} SECURED. INSTALLED IN MOTION CORE DIRECTORY.`);
                         }} 
                         className="text-black bg-cyan-500 hover:bg-cyan-400 px-3 py-1.5 rounded font-mono font-bold text-[10px] uppercase tracking-widest shadow-lg transition-colors flex items-center gap-1"
                       >
                         CAPTURE SNAP
                       </button>
                       <button 
                         onClick={() => {
                           setIsSimulatingSynthesis(false);
                           setTuneStatusLog("LOCAL CORE SIMULATOR PLACED ON STANDBY PROFILE.");
                         }} 
                         className="text-white bg-rose-500/80 hover:bg-rose-500 px-3 py-1.5 rounded font-mono font-bold text-[10px] uppercase tracking-widest shadow-lg backdrop-blur-md transition-colors"
                       >
                         DISMISS
                       </button>
                    </div>
                  </div>
                ) : videoUrl ? (
                  <div className="relative w-full h-full flex flex-col bg-black">
                    <div className="relative flex-1 w-full h-full flex items-center justify-center">
                      <video 
                        src={videoUrl} 
                        controls 
                        loop 
                        className="w-full h-full object-contain relative z-10"
                        onCanPlay={(e) => {
                          const playPromise = e.currentTarget.play();
                          if (playPromise !== undefined) {
                            playPromise.catch(() => setPlaybackFailed(true));
                          }
                        }}
                        onError={() => {
                           console.warn("Video load error, might not be a supported source yet.");
                           // Prevent console spam if it's struggling to load
                        }}
                      />
                      {playbackFailed && (
                        <div className="absolute inset-0 bg-black/60 z-20 flex items-center justify-center backdrop-blur-sm">
                          <button 
                            onClick={(e) => {
                              const v = e.currentTarget.parentElement?.parentElement?.querySelector('video');
                              if (v) {
                                v.play();
                                setPlaybackFailed(false);
                              }
                            }}
                            className="bg-cyan-600 hover:bg-cyan-500 text-white font-mono font-bold uppercase tracking-widest text-xs px-6 py-3 rounded-lg flex items-center gap-2 shadow-[0_0_20px_rgba(34,211,238,0.5)] transition"
                          >
                            <Play className="w-4 h-4" /> Initialize Playback
                          </button>
                        </div>
                      )}
                      
                      {/* Logo Watermark Overlay */}
                      {embedWatermark && (
                        <div 
                          className={`absolute z-20 pointer-events-none flex items-center space-x-3 bg-black/65 backdrop-blur-md rounded-lg border border-cyan-500/20 opacity-100 shadow-[0_0_15px_rgba(0,0,0,0.6)] transition-all duration-300
                            ${watermarkPosition === 'top-left' ? 'top-4 left-4' : ''}
                            ${watermarkPosition === 'top-right' ? 'top-4 right-4' : ''}
                            ${watermarkPosition === 'bottom-left' ? 'bottom-4 left-4' : ''}
                            ${watermarkPosition === 'bottom-right' ? 'bottom-4 right-4' : ''}
                            ${watermarkSize === 'small' ? 'px-2 py-1.5' : 'px-3.5 py-2.5'}
                            ${watermarkSize === 'large' ? 'px-4.5 py-3.5' : ''}
                          `}
                          style={{ opacity: watermarkOpacity }}
                        >
                           <img 
                            src={customLogoUrl || "/logo.png.jpg"} 
                            alt="Nexorax Logo" 
                            onError={(e) => {
                              e.currentTarget.src = "/logo.png";
                            }}
                            className={`object-contain
                              ${watermarkSize === 'small' ? 'w-5 h-5' : 'w-8 h-8'}
                              ${watermarkSize === 'large' ? 'w-11 h-11' : ''}
                            `} 
                          />
                          <div className="flex flex-col text-left">
                            <span className={`text-cyan-400 font-sans font-black tracking-[0.05em] uppercase leading-tight drop-shadow-lg
                              ${watermarkSize === 'small' ? 'text-[9px]' : 'text-[12px]'}
                              ${watermarkSize === 'large' ? 'text-[14px]' : ''}
                            `}>{customLogoName ? customLogoName.split('.')[0] : "Nexorax Digital"}</span>
                            <span className={`text-white font-mono leading-none drop-shadow-sm mt-0.5
                              ${watermarkSize === 'small' ? 'text-[6.5px]' : 'text-[8.5px]'}
                              ${watermarkSize === 'large' ? 'text-[10px]' : ''}
                            `}>{customLogoName ? "CUSTOM BRAND SIGN" : "info@nexoraxdigital.co.uk"}</span>
                            <span className={`text-slate-300 font-mono leading-none drop-shadow-sm mt-0.5
                              ${watermarkSize === 'small' ? 'text-[6.5px]' : 'text-[8.5px]'}
                              ${watermarkSize === 'large' ? 'text-[10px]' : ''}
                            `}>{customLogoName ? "SECURE MATRIX EMBED" : "08000 622827"}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Action Bar */}
                    <div className="absolute top-4 right-4 z-30 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                       <button onClick={() => {
                          const a = document.createElement('a');
                          a.href = videoUrl;
                          a.download = `render-preview.mp4`;
                          a.click();
                       }} className="text-black bg-cyan-500 hover:bg-cyan-400 px-3 py-1.5 rounded font-mono font-bold text-[10px] uppercase tracking-widest shadow-lg transition-colors flex items-center gap-1">
                         SAVE RENDER
                       </button>
                       <button onClick={() => { setVideoUrl(null); setStatusText(''); }} className="text-white bg-rose-500/80 hover:bg-rose-500 px-3 py-1.5 rounded font-mono font-bold text-[10px] uppercase tracking-widest shadow-lg backdrop-blur-md transition-colors">
                         DELETE
                       </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center text-center p-8 text-slate-600">
                    {isGenerating ? (
                      <>
                        <div className="w-16 h-16 border-4 border-slate-800 border-t-cyan-500 rounded-full animate-spin mb-6"></div>
                        <span className="font-mono text-xs uppercase tracking-widest text-cyan-500 mb-2">NEURAL PIPELINE ACTIVE</span>
                        <span className="font-mono text-[9px] uppercase tracking-widest">{statusText}</span>
                      </>
                    ) : (
                      <>
                        <Video className="w-12 h-12 mb-4 opacity-50" />
                        <span className="font-mono text-[10px] uppercase tracking-widest w-full max-w-[250px]">
                          {statusText || "AWAITING RENDER DIRECTIVE. OUTPUT WILL APPEAR HERE."}
                        </span>
                      </>
                    )}
                  </div>
                )}
             </div>

             {/* Automatic Watermark Controls inside Video Engine Panel */}
             {(videoUrl || activeVideoObject) && (
               <div className="mt-5 border-t border-slate-800/80 pt-5 flex flex-col space-y-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                     <div className="flex items-center space-x-2">
                        <input 
                          type="checkbox" 
                          id="embedLogoToggle"
                          checked={embedWatermark}
                          onChange={(e) => setEmbedWatermark(e.target.checked)}
                          className="w-4 h-4 rounded bg-slate-950 border-slate-800 text-cyan-500 focus:ring-cyan-500/50 focus:ring-offset-0 cursor-pointer"
                        />
                        <label htmlFor="embedLogoToggle" className="text-xs font-mono font-bold text-white uppercase tracking-wider cursor-pointer">
                          Embed NexoraX Logo Watermark
                        </label>
                     </div>
                     {embedWatermark && (
                       <div className="flex flex-wrap items-center gap-3">
                          {/* Position */}
                          <div className="flex items-center space-x-1 bg-slate-950 border border-slate-800 rounded-lg p-1">
                             <span className="text-[8px] font-mono font-bold text-slate-500 uppercase px-1">POS:</span>
                             {(['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const).map(pos => (
                               <button
                                 key={pos}
                                 onClick={() => setWatermarkPosition(pos)}
                                 className={`text-[8px] font-mono font-bold px-1.5 py-0.5 rounded uppercase transition-colors ${watermarkPosition === pos ? 'bg-cyan-500 text-black' : 'text-slate-400 hover:text-white'}`}
                               >
                                 {pos.replace('-', ' ')}
                               </button>
                             ))}
                          </div>

                          {/* Size */}
                          <div className="flex items-center space-x-1 bg-slate-950 border border-slate-800 rounded-lg p-1">
                             <span className="text-[8px] font-mono font-bold text-slate-500 uppercase px-1">SIZE:</span>
                             {(['small', 'medium', 'large'] as const).map(sz => (
                               <button
                                 key={sz}
                                 onClick={() => setWatermarkSize(sz)}
                                 className={`text-[8px] font-mono font-bold px-1.5 py-0.5 rounded uppercase transition-colors ${watermarkSize === sz ? 'bg-cyan-500 text-black' : 'text-slate-400 hover:text-white'}`}
                               >
                                 {sz}
                               </button>
                             ))}
                          </div>

                          {/* Opacity */}
                          <div className="flex items-center space-x-2 bg-slate-950 border border-slate-800 rounded-lg px-2 py-1">
                             <span className="text-[8px] font-mono font-bold text-slate-500 uppercase">OPACITY:</span>
                             <input 
                               type="range" 
                               min="0.1" 
                               max="1" 
                               step="0.05"
                               value={watermarkOpacity} 
                               onChange={(e) => setWatermarkOpacity(parseFloat(e.target.value))}
                               className="w-16 accent-cyan-500" 
                             />
                             <span className="text-[8px] font-mono font-bold text-cyan-400">{Math.round(watermarkOpacity * 100)}%</span>
                          </div>
                       </div>
                     )}
                  </div>

                  {/* Export compilation button */}
                  <button
                    disabled={isCompilingWebm}
                    onClick={compileWatermarkedVideo}
                    className="w-full bg-gradient-to-r from-cyan-500 to-indigo-500 hover:from-cyan-400 hover:to-indigo-400 disabled:opacity-50 text-black font-black uppercase text-xs tracking-widest py-3 rounded-lg shadow-lg flex items-center justify-center gap-2 transition"
                  >
                    {isCompilingWebm ? (
                      <>
                        <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                        COMPILING RENDER WATERMARK: {compilationProgress}%
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4 animate-pulse" />
                        EXPORT HIGH-FIDELITY VIDEO WITH BURNED NEXORAX LOGO (.WEBM)
                      </>
                    )}
                  </button>

                  {/* REAL-TIME THUMBNAIL PREVIEW STRIP / SCRUBBER */}
                  <div className="mt-5 border-t border-slate-800/80 pt-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5 text-cyan-400 animate-pulse" />
                        <span className="text-[10px] font-mono text-cyan-400 font-extrabold uppercase tracking-wider">REAL-TIME THUMBNAIL PREVIEW STRIP // SCRUBBER</span>
                      </div>
                      <span className="text-[8px] font-mono text-slate-500 uppercase font-bold tracking-widest">{vault.length} ACTIVE RENDERS</span>
                    </div>

                    <div className="flex gap-3 overflow-x-auto pb-3 custom-scrollbar snap-x">
                      {vault.map((item) => {
                        const isActive = activeVideoObject?.id === item.id;
                        return (
                          <div 
                            key={item.id}
                            onClick={() => {
                              setActiveVideoObject(item);
                              setVideoUrl(item.url);
                              setTuneStatusLog(`RENDER HISTORY LOADED: ${item.id}`);
                            }}
                            className={`relative group/strip flex-shrink-0 w-[140px] aspect-video bg-[#050914] rounded-lg overflow-hidden border cursor-pointer transition-all duration-300 snap-start
                              ${isActive 
                                ? 'border-cyan-400 ring-1 ring-cyan-500/20 shadow-[0_0_12px_rgba(34,211,238,0.35)] scale-[1.02]' 
                                : 'border-slate-800 hover:border-slate-700 hover:scale-[1.01]'}`}
                          >
                            {/* Miniature Video Preview playing on hover */}
                            <video 
                              src={item.url} 
                              muted 
                              loop 
                              playsInline
                              className="w-full h-full object-cover transition-transform duration-500 group-hover/strip:scale-110"
                              onMouseEnter={(e) => {
                                e.currentTarget.play().catch(() => {});
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.pause();
                                e.currentTarget.currentTime = 0;
                              }}
                            />
                            
                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/3 transition-opacity to-transparent pointer-events-none flex flex-col justify-end p-2">
                              <span className="text-[8px] font-mono font-bold text-white uppercase truncate drop-shadow">{item.prompt}</span>
                              <span className="text-[6.5px] font-mono text-cyan-400 uppercase tracking-widest mt-0.5 leading-none">{item.id}</span>
                            </div>

                            <div className="absolute top-1 right-1 bg-black/85 backdrop-blur-md border border-white/10 px-1 py-0.5 rounded opacity-0 group-hover/strip:opacity-100 transition-opacity duration-200 pointer-events-none">
                              <span className="text-[6px] font-mono text-cyan-400 uppercase tracking-widest font-black">HOVER TO SCRUB</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* METADATA PANEL */}
                  {activeVideoObject && (
                    <div className="mt-2 bg-[#030611] border border-slate-800/80 rounded-xl p-4 flex flex-col space-y-3 px-4 py-4 backdrop-blur-sm shadow-xl">
                      <div className="flex items-center justify-between border-b border-slate-800 pb-2.5">
                        <div className="flex items-center gap-1.5">
                          <Sliders className="w-3.5 h-3.5 text-cyan-400 animate-pulse" />
                          <span className="text-[10px] font-mono font-extrabold text-white uppercase tracking-wider">SYNTHETIC METADATA INDEX // RENDER: {activeVideoObject.id}</span>
                        </div>
                        <span className="text-[7.5px] font-mono text-emerald-400 font-bold bg-emerald-950/25 border border-emerald-500/10 px-2 py-0.5 rounded uppercase tracking-widest leading-none">
                          SECURE SYSTEM RENDERED
                        </span>
                      </div>

                      {/* Generation prompt */}
                      <div className="flex flex-col space-y-1 text-left">
                        <span className="text-[8px] font-mono text-slate-500 uppercase tracking-widest font-bold">GENERATION PROMPT DIRECTIVE:</span>
                        <p className="text-[10px] text-slate-300 font-mono leading-relaxed bg-black/50 border border-slate-900 rounded-lg p-2.5 max-h-[75px] overflow-y-auto custom-scrollbar uppercase">
                          {activeVideoObject.prompt}
                        </p>
                      </div>

                      {/* Metadata and params grid */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 text-left">
                        {/* Timestamp */}
                        <div className="bg-black/50 border border-slate-900 rounded p-2 flex flex-col justify-between">
                          <span className="text-[6.5px] font-mono text-slate-500 uppercase tracking-widest font-bold">COMPILATION DATE</span>
                          <span className="text-[9.5px] font-mono text-slate-300 font-bold mt-1 uppercase truncate">
                            {new Date(activeVideoObject.timestamp).toLocaleDateString()} {new Date(activeVideoObject.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </span>
                        </div>

                        {/* Model Name */}
                        <div className="bg-black/50 border border-slate-900 rounded p-2 flex flex-col justify-between">
                          <span className="text-[6.5px] font-mono text-slate-500 uppercase tracking-widest font-bold">MODEL ENGINE</span>
                          <span className="text-[9.5px] font-mono text-cyan-400 font-bold mt-1 uppercase truncate">
                            VEO-3.1-HYPER-DRIVE
                          </span>
                        </div>

                        {/* Diffusion steps */}
                        <div className="bg-black/50 border border-slate-900 rounded p-2 flex flex-col justify-between">
                          <span className="text-[6.5px] font-mono text-slate-500 uppercase tracking-widest font-bold">DIFFUSION STEPS</span>
                          <span className="text-[9.5px] font-mono text-slate-300 font-bold mt-1 uppercase truncate">
                            {activeVideoObject.modelConfig?.diffusionSteps || 80} STEPS
                          </span>
                        </div>

                        {/* Temporal Consistency */}
                        <div className="bg-black/50 border border-slate-900 rounded p-2 flex flex-col justify-between">
                          <span className="text-[6.5px] font-mono text-slate-500 uppercase tracking-widest font-bold">TEMPORAL LOGIC</span>
                          <span className="text-[9.5px] font-mono text-slate-300 font-bold mt-1 uppercase truncate">
                            {activeVideoObject.modelConfig?.temporalConsistency || 92}% COHERENT
                          </span>
                        </div>

                        {/* Orbit Velocity */}
                        <div className="bg-black/50 border border-slate-900 rounded p-2 flex flex-col justify-between">
                          <span className="text-[6.5px] font-mono text-slate-500 uppercase tracking-widest font-bold">ORBIT SPEED</span>
                          <span className="text-[9.5px] font-mono text-slate-300 font-bold mt-1 uppercase truncate">
                            {activeVideoObject.modelConfig?.orbitSpeed?.toFixed(1) || '3.5'}x RATE
                          </span>
                        </div>

                        {/* Particle Density */}
                        <div className="bg-black/50 border border-slate-900 rounded p-2 flex flex-col justify-between">
                          <span className="text-[6.5px] font-mono text-slate-500 uppercase tracking-widest font-bold">COSMOS STRENGTH</span>
                          <span className="text-[9.5px] font-mono text-slate-300 font-bold mt-1 uppercase truncate">
                            {activeVideoObject.modelConfig?.particleDensity || 250} PARTICLES
                          </span>
                        </div>

                        {/* Space Matrix Tint */}
                        <div className="bg-black/50 border border-slate-900 rounded p-2 flex flex-col justify-between col-span-2">
                          <span className="text-[6.5px] font-mono text-slate-500 uppercase tracking-widest font-bold">CYBER SPACE VECTOR TINT</span>
                          <span className="text-[9.5px] font-mono text-slate-300 font-bold mt-1 uppercase truncate flex items-center gap-1.5">
                            <span 
                              className="w-2.5 h-2.5 rounded-full inline-block animate-pulse" 
                              style={{
                                backgroundColor: 
                                  activeVideoObject.modelConfig?.matrixColor === 'cyan' ? '#00f0ff' :
                                  activeVideoObject.modelConfig?.matrixColor === 'amber' ? '#fbbf24' :
                                  activeVideoObject.modelConfig?.matrixColor === 'pink' ? '#f472b6' :
                                  activeVideoObject.modelConfig?.matrixColor === 'emerald' ? '#34d399' :
                                  activeVideoObject.modelConfig?.matrixColor === 'rose' ? '#f43f5e' : '#00f0ff'
                              }}
                            />
                            {(activeVideoObject.modelConfig?.matrixColor || 'cyan').toUpperCase()} WAVEFORM
                          </span>
                        </div>

                        {/* Ease setting */}
                        <div className="bg-black/50 border border-slate-900 rounded p-2 flex flex-col justify-between">
                          <span className="text-[6.5px] font-mono text-slate-500 uppercase tracking-widest font-bold">EASE SETTING</span>
                          <span className="text-[9.5px] font-mono text-fuchsia-400 font-bold mt-1 uppercase truncate font-extrabold">
                            {(activeVideoObject.modelConfig?.easeSetting || 'LINEAR').replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* DEDICATED DIRECT LOCAL EXPORT */}
                  {activeVideoObject && (
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          const a = document.createElement('a');
                          a.href = activeVideoObject.url;
                          a.download = `nexorax-${activeVideoObject.id.toLowerCase()}.mp4`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          setTuneStatusLog(`DIRECT EXPORT FIRED: ${activeVideoObject.id}`);
                        }}
                        className="flex-grow bg-gradient-to-r from-teal-500 via-emerald-500 to-cyan-500 hover:from-teal-400 hover:via-emerald-400 hover:to-cyan-400 text-black font-extrabold uppercase text-[11px] tracking-widest py-3.5 rounded-lg shadow-xl flex items-center justify-center gap-2 transition hover:shadow-[0_0_20px_rgba(20,184,166,0.3)] border border-teal-400/20"
                      >
                        <Download className="w-4 h-4" />
                        DOWNLOAD SPECIFIED MASTERPIECE DIRECTLY (.MP4)
                      </button>
                    </div>
                  )}
               </div>
             )}
               </div>
             )}
          </div>
          </div>
        </div>
      )}
      
      {activeTab === 'AUTOMATION' && (
        <VideoAutomationEngine
          sessionRole={sessionRole}
          setSessionRole={setSessionRole}
          brandAssets={brandAssets}
          setBrandAssets={setBrandAssets}
          videosList={videosList}
          setVideosList={setVideosList}
          schedulers={schedulers}
          setSchedulers={setSchedulers}
          automationTemplates={automationTemplates}
          auditLogs={auditLogs}
          setAuditLogs={setAuditLogs}
          activeVideoObject={activeVideoObject}
          setActiveVideoObject={setActiveVideoObject}
          formVideoTitle={formVideoTitle}
          setFormVideoTitle={setFormVideoTitle}
          formVideoType={formVideoType}
          setFormVideoType={setFormVideoType}
          formVideoGoal={formVideoGoal}
          setFormVideoGoal={setFormVideoGoal}
          formTargetAudience={formTargetAudience}
          setFormTargetAudience={setFormTargetAudience}
          formServiceOrProduct={formServiceOrProduct}
          setFormServiceOrProduct={setFormServiceOrProduct}
          formDuration={formDuration}
          setFormDuration={setFormDuration}
          formAspectRatio={formAspectRatio}
          setFormAspectRatio={setFormAspectRatio}
          formTone={formTone}
          setFormTone={setFormTone}
          formCta={formCta}
          setFormCta={setFormCta}
          formUseLogo={formUseLogo}
          setFormUseLogo={setFormUseLogo}
          formUsePhone={formUsePhone}
          setFormUsePhone={setFormUsePhone}
          formUseEmail={formUseEmail}
          setFormUseEmail={setFormUseEmail}
          formUseWebsite={formUseWebsite}
          setFormUseWebsite={setFormUseWebsite}
          formUseVoiceover={formUseVoiceover}
          setFormUseVoiceover={setFormUseVoiceover}
          formUseSubtitles={formUseSubtitles}
          setFormUseSubtitles={setFormUseSubtitles}
          formUseThumbnail={formUseThumbnail}
          setFormUseThumbnail={setFormUseThumbnail}
          formScenePrompt={formScenePrompt}
          setFormScenePrompt={setFormScenePrompt}
          formNotes={formNotes}
          setFormNotes={setFormNotes}
          formPriority={formPriority}
          setFormPriority={setFormPriority}
          formStatus={formStatus}
          setFormStatus={setFormStatus}
          duplicateWarning={duplicateWarning}
          setDuplicateWarning={setDuplicateWarning}
          triggerVerifyDuplicate={triggerVerifyDuplicate}
          handleRunAutomationRender={handleRunAutomationRender}
        />
      )}
      
      {false && activeTab === 'AUTOMATION' && (
        <div className="flex flex-col space-y-6">
          {/* Sub-section Hub Switcher Bar */}
          <div className="grid grid-cols-2 lg:grid-cols-5 bg-[#04060d]/80 border border-slate-800/80 rounded-xl p-1.5 gap-1.5 backdrop-blur-md">
            {[
              { id: 'OVERVIEW', label: 'OPERATIONAL HUD', icon: BarChart2 },
              { id: 'BRAND_PROFILE', label: 'BRAND PROFILE', icon: Briefcase },
              { id: 'VIDEO_FACTORY', label: 'VIDEO FACTORY', icon: Wand2 },
              { id: 'SYNC_LIFECYCLE', label: 'SYNC & LIFECYCLE', icon: Shield },
              { id: 'DISTRIBUTION', label: 'DISTRIBUTION HUB', icon: FolderOpen }
            ].map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveSection(item.id);
                    setDuplicateWarning(null);
                  }}
                  className={`py-2.5 px-3 rounded-lg text-[10px] sm:text-xs font-mono font-bold uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-2 border ${activeSection === item.id ? 'bg-cyan-500/10 border-cyan-500 text-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.25)]' : 'border-transparent text-slate-400 hover:text-slate-200 hover:bg-white/5'}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {item.label}
                </button>
              );
            })}
          </div>

          {/* DYNAMIC HUBS RENDERING */}
          {activeSection === 'OVERVIEW' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* LEFT & CENTER: KPI GRID, MONITOR, DIAGNOSTICS */}
              <div className="lg:col-span-2 flex flex-col space-y-6">
                
                {/* 4 CORE KPI GENERAL OVERVIEW CARDS */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { title: "Videos Managed", value: videosList.length, label: "All Active Nodes", color: "text-cyan-400 border-cyan-500/30 bg-cyan-500/5" },
                    { title: "Published Assets", value: videosList.filter(v => v.status === "Published").length, label: "Live destinations", color: "text-emerald-400 border-emerald-500/30 bg-emerald-500/5" },
                    { title: "Awaiting Audit", value: videosList.filter(v => v.status === "Needs Review").length, label: "Manager verification", color: "text-amber-400 border-amber-500/30 bg-amber-500/5" },
                    { title: "Uptime SLA", value: "0.8s", label: "Render threshold", color: "text-purple-400 border-purple-500/30 bg-purple-500/5" }
                  ].map((card, idx) => (
                    <div key={idx} className={`p-4 rounded-xl border flex flex-col justify-between backdrop-blur-sm ${card.color}`}>
                      <span className="font-mono text-[9px] uppercase tracking-wider text-slate-400">{card.title}</span>
                      <span className="text-2xl font-black mt-1 font-mono tracking-tight">{card.value}</span>
                      <span className="font-mono text-[8px] uppercase tracking-widest text-slate-500 mt-2">{card.label}</span>
                    </div>
                  ))}
                </div>

                {/* THE LIVE SYNCED MOTION VIDEO MONITOR */}
                <div className="bg-black/60 border border-slate-800 rounded-2xl p-5 relative overflow-hidden backdrop-blur-md flex flex-col">
                  <div className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-0.5 rounded bg-cyan-950/40 border border-cyan-500/20 text-[9px] font-mono text-cyan-400 uppercase tracking-widest">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" /> LIVE TRACK MONITOR
                  </div>
                  
                  <h4 className="text-white text-xs font-mono uppercase tracking-widest font-bold mb-4 flex items-center gap-2">
                    <Video className="w-4 h-4 text-cyan-400" /> ACTIVE PREVIEW CONSOLE
                  </h4>

                  <div className="bg-[#020306] rounded-xl border border-slate-800/80 aspect-video w-full relative overflow-hidden flex flex-col items-center justify-center p-4">
                    {/* Retro Grid Background */}
                    <div className="absolute inset-0 bg-[radial-gradient(#080e1a_1px,transparent_1px)] [background-size:16px_16px] opacity-40" />
                    
                    {activeVideoObject ? (
                      <div className="w-full h-full relative flex flex-col items-center justify-center">
                        {/* Interactive Simulated Waveform Orbits */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-48 h-48 rounded-full border border-purple-500/20 animate-[spin_10s_linear_infinite] flex items-center justify-center p-2">
                            <div className="w-full h-full rounded-full border border-dashed border-cyan-500/30 animate-[spin_4s_linear_infinite_reverse] flex items-center justify-center p-4">
                              <div className="w-full h-full rounded-full border border-dotted border-amber-500/20" />
                            </div>
                          </div>
                        </div>

                        {/* Floating Coordinates Tagging */}
                        <div className="absolute top-2 left-2 z-10 font-mono text-[9px] text-slate-500 flex flex-col">
                          <span>FILE: {activeVideoObject.suggestedFilename}</span>
                          <span>RATIO: {activeVideoObject.aspectRatio}</span>
                          <span>PRIORITY: {activeVideoObject.priority}</span>
                        </div>

                        <div className="absolute top-2 right-2 z-10 font-mono text-[9px] text-right flex flex-col">
                          <span className={`${activeVideoObject.status === 'Published' ? 'text-emerald-400' : 'text-amber-400'} uppercase font-black`}>STATUS: {activeVideoObject.status}</span>
                          <span>VIEWS: {activeVideoObject.views}</span>
                        </div>

                        {/* Simulated Video Node Content Loop */}
                        <div className="relative z-10 text-center space-y-2 max-w-md p-4 bg-black/50 border border-slate-800/50 rounded-xl backdrop-blur-sm">
                          <p className="text-cyan-400 font-mono text-[10px] uppercase tracking-widest font-bold">[[ {activeVideoObject.type} ]]</p>
                          <h5 className="text-white text-md font-sans font-extrabold tracking-tight">{activeVideoObject.title}</h5>
                          <p className="text-slate-400 text-xs italic">"{activeVideoObject.scenePrompt}"</p>
                        </div>

                        {/* Custom pulsing CSS Subtitle Waveform Overlay */}
                        <div className="absolute bottom-8 left-4 right-4 z-10 bg-black/80 border border-cyan-500/30 rounded-lg p-2 flex flex-col items-center justify-center backdrop-blur-md">
                          <span className="text-[9px] font-mono tracking-widest text-cyan-400 font-black mb-1 uppercase">&#9670; SUBTITLES TIMELINE TRACE:</span>
                          <span className="text-white font-mono text-xs text-center uppercase tracking-wider font-extrabold px-4 text-glow-cyan">
                            {activeVideoObject.subtitleText || "[BACKGROUND LOOP COMPONENT - NO CAPTIONS REGISTERED]"}
                          </span>
                        </div>

                        {/* Live CSS Interactive Audio Level Visualizer bars */}
                        <div className="absolute bottom-2 left-4 right-4 flex items-center justify-center gap-0.5">
                          {[1,2,3,4,5,6,7,8,9,10,9,8,7,6,5,4,3,2,1].map((bar, bidx) => (
                            <div 
                              key={bidx} 
                              className="w-1.5 bg-gradient-to-t from-purple-500 to-cyan-400 rounded-t"
                              style={{ 
                                height: `${Math.floor(Math.random() * 20) + 10}px`,
                                animation: `pulse 0.8s ease-in-out infinite alternate ${bidx * 50}ms`
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center space-y-3 z-10 text-center px-6">
                        <div className="w-12 h-12 rounded-full border border-dashed border-slate-800 flex items-center justify-center text-slate-600 animate-spin">
                          <Loader2 className="w-6 h-6 animate-pulse" />
                        </div>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400 font-bold">AWAITING CORRESPONDENCE SELECT</p>
                        <p className="font-mono text-[8.5px] uppercase tracking-wider text-slate-500 max-w-sm">
                          Select any generated promo video from the Pipeline or click a Guide ID on the Sync tab to verify outputs directly.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* REAL-TIME LOGGING CONSOLE TRACE & GOOGLE DRIVE MATCHES */}
                <div className="bg-black/80 border border-slate-800 rounded-2xl p-5 relative font-mono text-[11px]">
                  <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-4">
                    <span className="font-bold text-white uppercase tracking-widest text-[10px] flex items-center gap-2">
                      <Cpu className="w-4 h-4 text-purple-400" /> SYSTEM DIAGNOSTICS & SYSTEM AUDIT TERM
                    </span>
                    <span className="text-[9px] text-emerald-400 flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> MIRRORED TO GOOGLE DRIVE (CLOUD_SYNC-02)
                    </span>
                  </div>

                  <div className="bg-black/90 rounded-xl p-4 border border-slate-850 h-40 overflow-y-auto space-y-1.5 text-slate-350">
                    <div>[INFO: 10:00:00] Initializing NexoraX Corporate Video Node OS (SC887095)</div>
                    <div>[INFO: 10:00:02] Loading 1% Levy global charity directives...</div>
                    <div>[INFO: 10:00:05] Establishing core handshakes on phone line Frequency 08000 622827</div>
                    {auditLogs.map((log, idx) => (
                      <div key={idx} className="flex gap-2">
                        <span className="text-slate-500">[{log.time}]</span>
                        <span className="text-purple-400">({log.user})</span>
                        <span className="text-white">{log.action}</span>
                        <span className={`${log.status === 'SUCCESS' ? 'text-emerald-400' : 'text-cyan-400'} ml-auto`}>[{log.status}]</span>
                      </div>
                    ))}
                    {consoleIsRunning && (
                      <div className="text-cyan-400 animate-pulse flex items-center gap-2 mt-2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> {consoleHeader} ({consoleProgress}%)
                      </div>
                    )}
                    {consoleLogs.map((clog, idx) => (
                      <div key={`c-${idx}`} className="text-cyan-300 font-mono text-[10px]">&#9658; {clog}</div>
                    ))}
                  </div>
                </div>

              </div>
              
              {/* RIGHT COLUMN: AUTO-GENERATION SCHEDULER */}
              <div className="flex flex-col space-y-6">
                
                {/* AUTOMATED RENDER QUEUE STATUS */}
                <div className="bg-gradient-to-b from-purple-950/20 to-black/60 border border-purple-500/20 rounded-2xl p-5 relative backdrop-blur-md">
                  <h4 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-black mb-4 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-purple-400" /> AUTOMATED SCHEDULER
                  </h4>
                  
                  <p className="text-[10px] text-slate-400 font-mono uppercase tracking-widest mb-4">
                    Unified trigger matrix for background automated video syndicates.
                  </p>

                  <div className="space-y-3.5">
                    {schedulers.map(sch => (
                      <div key={sch.id} className="p-3 bg-black/40 border border-slate-800 rounded-xl hover:border-purple-500/20 transition-all flex flex-col space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex flex-col">
                            <span className="text-white text-xs font-semibold">{sch.name}</span>
                            <span className="text-[9px] font-mono text-slate-400 mt-0.5">{sch.frequency}</span>
                          </div>
                          <button
                            onClick={() => {
                              // Toggle Active state
                              setSchedulers(schedulers.map(s => s.id === sch.id ? { ...s, active: !s.active } : s));
                              setAuditLogs([
                                {
                                  id: "log-" + Date.now(),
                                  time: new Date().toTimeString().split(' ')[0],
                                  user: "Lead Director",
                                  action: `Toggled Scheduler state for ${sch.name} to: ${!sch.active ? "ACTIVE" : "INACTIVE"}`,
                                  status: "SYNCED"
                                },
                                ...auditLogs
                              ]);
                            }}
                            className={`px-2 py-1 rounded text-[8px] font-mono tracking-widest uppercase border ${sch.active ? 'bg-cyan-500/10 border-cyan-500/40 text-cyan-400' : 'bg-slate-800/20 border-slate-700/40 text-slate-500'}`}
                          >
                            {sch.active ? 'ENABLED' : 'MUTED'}
                          </button>
                        </div>

                        <div className="grid grid-cols-2 gap-1 bg-[#020306] p-2 rounded-lg font-mono text-[8px] uppercase text-slate-500">
                          <span>LAST RUN: <strong className="text-slate-300">{sch.lastRun}</strong></span>
                          <span>NEXT RUN: <strong className="text-slate-300">{sch.nextRun}</strong></span>
                          <span>FORMAT: <strong className="text-slate-300">{sch.outputType}</strong></span>
                          <span>MODE: <strong className="text-slate-300">{sch.mode}</strong></span>
                        </div>
                        
                        <div className="flex gap-2 justify-end">
                          <button 
                            onClick={() => {
                              setActiveSection('VIDEO_FACTORY');
                              setFormVideoType(sch.outputType);
                              setFormVideoTitle(`${sch.name} Seed`);
                            }}
                            className="bg-purple-500/10 border border-purple-500/25 hover:bg-purple-900/30 text-purple-400 text-[8px] font-mono tracking-widest px-2.5 py-1 rounded transition-colors uppercase font-bold"
                          >
                            TRIGGER NOW
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-3.5 mt-4 text-[9px] font-mono flex items-start gap-2.5">
                    <Info className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <div className="text-slate-400 space-y-1">
                      <span className="font-extrabold text-amber-400 uppercase tracking-wider block">PROSPECT SCHEDULER CHECKLIST:</span>
                      <span>All scheduler pipelines are anchored to the standard Wednesday ledger deadlines automatically.</span>
                    </div>
                  </div>
                </div>

              </div>

            </div>
          )}

          {activeSection === 'BRAND_PROFILE' && (
            <div className="bg-black/40 border border-slate-800 rounded-2xl p-6 backdrop-blur-sm">
              <div className="flex items-center justify-between border-b border-slate-800 pb-4 mb-6">
                <div>
                  <h3 className="text-white text-md font-sans font-extrabold uppercase tracking-widest flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-cyan-400" /> CENTRAL BRAND PROFILE
                  </h3>
                  <p className="text-slate-400 text-xs mt-1">
                    Manage corporate assets injected into newly compile video overlays and direct-billed scripts.
                  </p>
                </div>
                <button
                  onClick={() => {
                    const newLog = {
                      id: "log-" + (auditLogs.length + 1),
                      time: new Date().toTimeString().split(' ')[0],
                      user: "Lead Director",
                      action: "Synced compliance brand database to main server assets",
                      status: "SUCCESS"
                    };
                    setAuditLogs([newLog, ...auditLogs]);
                    setDuplicateWarning("SUCCESS: Central brand configurations completely synced and cataloged in Google Workspace library.");
                  }}
                  className="bg-cyan-500 hover:bg-cyan-400 text-black px-4 py-2 rounded-lg font-mono text-xs font-bold tracking-widest transition-colors uppercase shadow-[0_0_15px_rgba(6,182,212,0.4)]"
                >
                  SAVE & SYNC PROFILE
                </button>
              </div>

              {duplicateWarning && (
                <div className={`p-3.5 rounded-xl border font-mono text-[10px] mb-6 ${duplicateWarning.includes('SUCCESS') ? 'bg-emerald-950/20 border-emerald-500/40 text-emerald-400' : 'bg-amber-950/20 border-amber-500/40 text-amber-400'}`}>
                  {duplicateWarning}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { label: "Company Directory Name", key: "company", placeholder: "e.g., NexoraX Digital" },
                  { label: "Corporate Tagline", key: "tagline", placeholder: "Build Smarter. Scale Faster." },
                  { label: "Compliance Hotline Voice", key: "phone", placeholder: "e.g., 08000 622827" },
                  { label: "General Mandate Email", key: "primaryEmail", placeholder: "info@nexoraxdigital.co.uk" },
                  { label: "Enterprise Support Box", key: "supportEmail", placeholder: "support@nexoraxdigital.co.uk" },
                  { label: "Central URL Domain", key: "websiteUrl", placeholder: "nexoraxdigital.co.uk" },
                  { label: "Social Handle Target", key: "socialHandles", placeholder: "@nexoraxdigital" },
                  { label: "Voiceover Accent Acoustic profile", key: "voiceoverTone", placeholder: "e.g., Sincere British Corporate Leader" }
                ].map(field => (
                  <div key={field.key} className="flex flex-col space-y-2">
                    <label className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-bold">{field.label}</label>
                    <input
                      type="text"
                      className="bg-[#020306] border border-slate-800 focus:border-cyan-500/50 rounded-lg py-2.5 px-3 font-mono text-xs text-white focus:outline-none transition-colors"
                      value={brandAssets[field.key as keyof typeof brandAssets] as string}
                      placeholder={field.placeholder}
                      onChange={(e) => {
                        setBrandAssets({ ...brandAssets, [field.key]: e.target.value });
                      }}
                    />
                  </div>
                ))}
                
                <div className="flex flex-col space-y-2 lg:col-span-1">
                  <label className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-bold">Theme Primary Aesthetic</label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { l: "Obsidian", c: brandAssets.themePrimary, k: "themePrimary" },
                      { l: "Cyan", c: brandAssets.themeAccent1, k: "themeAccent1" },
                      { l: "Purple", c: brandAssets.themeAccent2, k: "themeAccent2" },
                      { l: "Gold", c: brandAssets.themeHighlight, k: "themeHighlight" }
                    ].map((col, cidx) => (
                      <div key={cidx} className="flex flex-col items-center gap-1 bg-[#020306] border border-slate-800 p-1.5 rounded-lg">
                        <div className="w-5 h-5 rounded" style={{ backgroundColor: col.c }} />
                        <span className="text-[8px] font-mono text-slate-500 uppercase">{col.l}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col space-y-2 md:col-span-2">
                  <label className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-bold">Default Outro CTA Matrix</label>
                  <input
                    type="text"
                    className="bg-[#020306] border border-slate-800 focus:border-cyan-500/50 rounded-lg py-2.5 px-3 font-mono text-xs text-white focus:outline-none transition-colors"
                    value={brandAssets.ctaText}
                    onChange={(e) => setBrandAssets({ ...brandAssets, ctaText: e.target.value })}
                  />
                </div>

                <div className="flex flex-col space-y-2 md:col-span-3">
                  <label className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-bold">Central Corporate Footer Audits & Disclosures</label>
                  <textarea
                    rows={2}
                    className="bg-[#020306] border border-slate-800 focus:border-cyan-500/50 rounded-lg py-2 px-3 font-mono text-xs text-white focus:outline-none transition-colors resize-none"
                    value={brandAssets.footerDisclosure}
                    onChange={(e) => setBrandAssets({ ...brandAssets, footerDisclosure: e.target.value })}
                  />
                </div>

                <div className="flex flex-col space-y-2 md:col-span-3">
                  <label className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-bold">Default Brand intro Synopsis</label>
                  <textarea
                    rows={2}
                    className="bg-[#020306] border border-slate-800 focus:border-cyan-500/50 rounded-lg py-2 px-3 font-mono text-xs text-white focus:outline-none transition-colors resize-none"
                    value={brandAssets.brandIntro}
                    onChange={(e) => setBrandAssets({ ...brandAssets, brandIntro: e.target.value })}
                  />
                </div>
              </div>

              <div className="bg-purple-950/20 border border-purple-500/30 rounded-xl p-4 mt-6 text-[9px] font-mono flex items-start gap-3">
                <Shield className="w-5 h-5 text-purple-400 shrink-0 mt-0.5" />
                <div className="text-slate-400 space-y-1">
                  <span className="font-extrabold text-purple-400 uppercase tracking-wider block">FINANCIAL OVERSEER AUDIT PROTOCOL:</span>
                  <span>Brand assets configured here automatically inherit direct audit rules under Company Number SC887095. Any modification registers directly on main security logs.</span>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'VIDEO_FACTORY' && (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
              
              {/* COL 1: CLICKABLE REUSABLE AUTOMATION TEMPLATES */}
              <div className="flex flex-col space-y-4">
                <div className="p-3 border border-slate-800 bg-[#04060d]/60 rounded-xl">
                  <h4 className="text-white text-[11px] font-mono uppercase tracking-widest font-black mb-3 text-glow-cyan flex items-center gap-1.5">
                    <Layers className="w-3.5 h-3.5 text-cyan-400" /> TEMPLATES SELECT (10)
                  </h4>
                  <p className="text-[8.5px] font-mono text-slate-500 uppercase tracking-wider mb-3">
                    Click any preset template to populate the generator form parameters instantly.
                  </p>

                  <div className="space-y-2 h-[720px] overflow-y-auto pr-1">
                    {automationTemplates.map(tmp => (
                      <button
                        key={tmp.id}
                        onClick={() => {
                          setFormVideoType(tmp.name.replace(" Template", ""));
                          setFormAspectRatio(tmp.outputAspectRatio);
                          setFormDuration(tmp.sceneDuration);
                          setFormCta(tmp.ctaStyle.includes("No primary") ? "None" : "Book Consultation");
                          setFormScenePrompt(tmp.motionStyle + " + " + tmp.textOverlays);
                          setFormVideoTitle(`${brandAssets.company} ${tmp.name.replace(" Template", "")}`);
                          setFormNotes(`Rendered on ${tmp.sceneDuration} guideline structure. Best fit: ${tmp.recommendedUsage}`);
                          
                          setAuditLogs([
                            {
                              id: "log-" + Date.now(),
                              time: new Date().toTimeString().split(' ')[0],
                              user: "Private Sec",
                              action: `Loaded template specs for: ${tmp.name}`,
                              status: "DRAFT"
                            },
                            ...auditLogs
                          ]);
                        }}
                        className={`w-full text-left p-2.5 rounded-lg border text-[10px] transition-all flex flex-col space-y-1 bg-black/40 ${formVideoType === tmp.name.replace(" Template", "") ? 'border-cyan-500 text-white bg-cyan-950/20 shadow-[0_0_8px_rgba(6,182,212,0.15)]' : 'border-slate-800 text-slate-400 hover:border-slate-705/80 hover:text-white'}`}
                      >
                        <span className="font-sans font-bold text-slate-200">{tmp.name}</span>
                        <span className="font-mono text-[8px] text-cyan-500">FORMAT: {tmp.outputAspectRatio} | {tmp.sceneDuration}</span>
                        <span className="font-mono text-[7.5px] text-slate-500 italic mt-0.5 flex items-center gap-1 line-clamp-1">
                          &#9654; {tmp.recommendedUsage}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* COL 2 & 3: FORM FACTORY CONFIG SHEET (21 PARAMETERS) */}
              <div className="lg:col-span-2 bg-[#04060d]/50 border border-slate-805/80 rounded-2xl p-5 relative backdrop-blur-md">
                <h4 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-bold mb-4 flex items-center gap-2">
                  <Wand2 className="w-4 h-4 text-cyan-400" /> AUTOMATION INPUT CONFIG (21-FIELD FORM)
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Field 1 */}
                  <div className="flex flex-col space-y-1">
                    <label className="text-[9px] font-mono uppercase tracking-wider text-slate-400 font-bold">1. Video Naming Title *</label>
                    <input
                      type="text"
                      className="bg-black/40 border border-slate-800 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-500/50"
                      value={formVideoTitle}
                      onChange={(e) => setFormVideoTitle(e.target.value)}
                    />
                  </div>

                  {/* Field 2 */}
                  <div className="flex flex-col space-y-1">
                    <label className="text-[9px] font-mono uppercase tracking-wider text-slate-400 font-bold">2. Video Format Type *</label>
                    <select
                      className="bg-black/80 border border-slate-800 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-500/50"
                      value={formVideoType}
                      onChange={(e) => setFormVideoType(e.target.value)}
                    >
                      <option value="Homepage Hero Video">Homepage Hero Video</option>
                      <option value="15-second promo video">15-Second Promo Video</option>
                      <option value="30-second promo video">30-Second Promo Video</option>
                      <option value="Social Media Vertical Video">Social Media Vertical Video</option>
                      <option value="Transition / Bumper Clip">Transition / Bumper Clip</option>
                      <option value="Guides & Videos Walkthrough Clip">Guides Explainer Clip</option>
                      <option value="Product Promo Video">Product Promo Video</option>
                      <option value="Future Ad Creative">Future Ad Creative</option>
                      <option value="Branded Outro Handshake">Branded Outro Handshake</option>
                    </select>
                  </div>

                  {/* Field 3 */}
                  <div className="flex flex-col space-y-1">
                    <label className="text-[9px] font-mono uppercase tracking-wider text-slate-400 font-bold">3. Campaign Video Goal</label>
                    <input
                      type="text"
                      className="bg-black/40 border border-slate-800 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
                      value={formVideoGoal}
                      onChange={(e) => setFormVideoGoal(e.target.value)}
                    />
                  </div>

                  {/* Field 4 */}
                  <div className="flex flex-col space-y-1">
                    <label className="text-[9px] font-mono uppercase tracking-wider text-slate-400 font-bold">4. Core Target Audience</label>
                    <input
                      type="text"
                      className="bg-black/40 border border-slate-800 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
                      value={formTargetAudience}
                      onChange={(e) => setFormTargetAudience(e.target.value)}
                    />
                  </div>

                  {/* Field 5 */}
                  <div className="flex flex-col space-y-1">
                    <label className="text-[9px] font-mono uppercase tracking-wider text-slate-400 font-bold">5. Highlighted Service / Product</label>
                    <input
                      type="text"
                      className="bg-black/40 border border-slate-800 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
                      value={formServiceOrProduct}
                      onChange={(e) => setFormServiceOrProduct(e.target.value)}
                    />
                  </div>

                  {/* Field 6 & 7 */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col space-y-1">
                      <label className="text-[9px] font-mono uppercase tracking-wider text-slate-400 font-bold">6. Duration</label>
                      <input
                        type="text"
                        className="bg-black/40 border border-slate-800 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
                        value={formDuration}
                        onChange={(e) => setFormDuration(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col space-y-1">
                      <label className="text-[9px] font-mono uppercase tracking-wider text-slate-400 font-bold">7. Aspect Ratio</label>
                      <select
                        className="bg-black/80 border border-slate-800 rounded-lg px-2 py-2 text-xs text-white focus:outline-none"
                        value={formAspectRatio}
                        onChange={(e) => setFormAspectRatio(e.target.value)}
                      >
                        <option value="16:9">16:9 (Horizontal)</option>
                        <option value="9:16">9:16 (Vertical)</option>
                        <option value="1:1">1:1 (Square)</option>
                      </select>
                    </div>
                  </div>

                  {/* Field 8 & 9 */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col space-y-1">
                      <label className="text-[9px] font-mono uppercase tracking-wider text-slate-400 font-bold">8. Aesthetic Tone</label>
                      <input
                        type="text"
                        className="bg-black/40 border border-slate-800 rounded-lg px-2 py-2 text-xs text-white focus:outline-none"
                        value={formTone}
                        onChange={(e) => setFormTone(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col space-y-1">
                      <label className="text-[9px] font-mono uppercase tracking-wider text-slate-400 font-bold">9. Render CTA Outro</label>
                      <input
                        type="text"
                        className="bg-black/40 border border-slate-800 rounded-lg px-2 py-2 text-xs text-white focus:outline-none"
                        value={formCta}
                        onChange={(e) => setFormCta(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Field 10 to 16: BRAND TOGGLES LAYER */}
                  <div className="md:col-span-2 bg-[#020306] p-3 rounded-xl border border-slate-850 space-y-2.5">
                    <span className="font-mono text-[9px] text-cyan-400 uppercase tracking-widest font-black block">CORPORATE OVERLAY INTEGRATION (FIELDS 10-16)</span>
                    
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: "10. Embed Crown Watermark logo", state: formUseLogo, set: setFormUseLogo },
                        { label: `11. Contact Phone (${brandAssets.phone})`, state: formUsePhone, set: setFormUsePhone },
                        { label: `12. Corporate support Email`, state: formUseEmail, set: setFormUseEmail },
                        { label: `13. Central Website Link`, state: formUseWebsite, set: setFormUseWebsite },
                        { label: "14. Accent Voiceover audio Track", state: formUseVoiceover, set: setFormUseVoiceover },
                        { label: "15. Embedded font-mono Subtitles", state: formUseSubtitles, set: setFormUseSubtitles },
                        { label: "16. Companion Thumbnail card", state: formUseThumbnail, set: setFormUseThumbnail }
                      ].map((tgl, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-black/40 border border-slate-800/80 p-2 rounded-lg">
                          <span className="font-mono text-[8px] uppercase text-slate-300">{tgl.label}</span>
                          <input
                            type="checkbox"
                            checked={tgl.state}
                            onChange={(e) => tgl.set(e.target.checked)}
                            className="w-3.5 h-3.5 rounded border-slate-750 text-cyan-500 focus:ring-cyan-400 bg-black/40"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Field 17 */}
                  <div className="md:col-span-2 flex flex-col space-y-1">
                    <label className="text-[9px] font-mono uppercase tracking-wider text-slate-400 font-bold">17. Motion Style & Scene Prompt Prompt *</label>
                    <textarea
                      rows={2}
                      className="bg-black/40 border border-slate-800 rounded-lg p-2.5 text-xs text-white focus:outline-none focus:border-cyan-500/50 placeholder-slate-600 resize-none"
                      value={formScenePrompt}
                      onChange={(e) => setFormScenePrompt(e.target.value)}
                    />
                  </div>

                  {/* Field 18 */}
                  <div className="md:col-span-2 flex flex-col space-y-1">
                    <label className="text-[9px] font-mono uppercase tracking-wider text-slate-400 font-bold">18. Developer Special Instructions & Notes</label>
                    <textarea
                      rows={1}
                      className="bg-black/40 border border-slate-800 rounded-lg p-2.5 text-xs text-white focus:outline-none placeholder-slate-600 resize-none"
                      value={formNotes}
                      placeholder="e.g. Ensure compliance rules NH448796B are displayed safely"
                      onChange={(e) => setFormNotes(e.target.value)}
                    />
                  </div>

                  {/* Fields 19, 20 & 21 */}
                  <div className="md:col-span-2 grid grid-cols-3 gap-2">
                    <div className="flex flex-col space-y-1">
                      <label className="text-[8.5px] font-mono uppercase tracking-wider text-slate-400 font-bold">19. Run Priority</label>
                      <select
                        className="bg-black/8 w-full border border-slate-840 rounded px-2 py-1.5 text-[10px] text-white"
                        value={formPriority}
                        onChange={(e) => setFormPriority(e.target.value)}
                      >
                        <option value="Critical">Critical</option>
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </select>
                    </div>

                    <div className="flex flex-col space-y-1">
                      <label className="text-[8.5px] font-mono uppercase tracking-wider text-slate-400 font-bold">20. Default status</label>
                      <select
                        className="bg-black/8 w-full border border-slate-840 rounded px-2 py-1.5 text-[10px] text-white"
                        value={formStatus}
                        onChange={(e) => setFormStatus(e.target.value)}
                      >
                        <option value="Draft">Draft Only</option>
                        <option value="Needs Review">Needs Review</option>
                        <option value="Waiting for Render">Awaiting render</option>
                        <option value="Approved">Approved</option>
                      </select>
                    </div>

                    <div className="flex flex-col space-y-1">
                      <label className="text-[8.5px] font-mono uppercase tracking-wider text-slate-400 font-bold">21. Sitemap check</label>
                      <button
                        onClick={triggerVerifyDuplicate}
                        className="bg-slate-800/40 border border-slate-700 hover:bg-slate-700/60 text-slate-200 py-1.5 px-2 rounded font-mono text-[9px] tracking-wider uppercase font-bold"
                      >
                        RUN INTEGRITY SCAN
                      </button>
                    </div>
                  </div>

                </div>
              </div>

              {/* COL 4: ACTION CONTROLS & BUNDLE GENERATORS */}
              <div className="flex flex-col space-y-6">
                
                {/* 6 CORE POWER ACTION BUTTONS */}
                <div className="bg-[#04060d]/60 border border-slate-800 rounded-2xl p-4 flex flex-col space-y-3">
                  <h4 className="text-white text-[11px] font-mono uppercase tracking-widest font-black text-glow-cyan mb-1.5 flex items-center gap-1.5">
                    <Sliders className="w-4 h-4 text-cyan-400" /> ACTIONS SUITE
                  </h4>

                  {[
                    {
                      label: "1. Generate Scripts & VO",
                      sub: "Compile text overlay parameters and dialog sheets",
                      click: () => {
                        handleRunAutomationRender("Compiling Promo Audio Narrative Grid...", false);
                      },
                      theme: "hover:border-purple-500/50 hover:bg-purple-950/20 text-slate-200 border-slate-800/80"
                    },
                    {
                      label: "2. Create Other Deliverables",
                      sub: "Generate subtitles timelines SRT & thumbnail illustrations",
                      click: () => {
                        handleRunAutomationRender("Assembling timing cues and vector illustrations...", false);
                      },
                      theme: "hover:border-amber-500/50 hover:bg-amber-950/20 text-slate-250 border-slate-800/80"
                    },
                    {
                      label: "3. Queue for Render",
                      sub: "Lock parameter coordinates and route to render console",
                      click: () => {
                        handleRunAutomationRender("Routing coordinates and seeding to VEO matrix list...", false);
                      },
                      theme: "bg-cyan-500 hover:bg-cyan-400 text-black border-transparent font-black shadow-[0_0_12px_rgba(6,182,212,0.3)] animate-pulse"
                    },
                    {
                      label: "4. Multi-Video Suite Pack",
                      sub: "Bulk create 4 custom sizes using 1-click execution",
                      click: () => {
                        handleRunAutomationRender("Expanding layouts... multiplexing formats...", true);
                      },
                      theme: "border-purple-500/40 text-purple-300 hover:bg-purple-950/40 animate-pulse bg-purple-500/5"
                    },
                    {
                      label: "5. Clean Cache Fallback",
                      sub: "Flush temporary diagnostics memory stack",
                      click: () => {
                        setDuplicateWarning("SUCCESS: Temporary memory arrays and caching fallback indices flushed completely.");
                        setAuditLogs([
                          { id: "log-" + Date.now(), time: new Date().toTimeString().split(' ')[0], user: "Director Engine", action: "Triggered 'Clean Cache Fallback' on video database indexes", status: "SUCCESS" },
                          ...auditLogs
                        ]);
                      },
                      theme: "hover:border-slate-600 hover:bg-slate-800/20 text-slate-400 border-slate-800/80"
                    },
                    {
                      label: "6. Discard Unsaved Jobs",
                      sub: "Wipe inputs and empty draft states",
                      click: () => {
                        setFormVideoTitle("");
                        setFormScenePrompt("");
                        setDuplicateWarning("SUCCESS: Factory draft queues flushed to default states.");
                      },
                      theme: "hover:border-rose-500/40 text-rose-400 border-slate-800"
                    }
                  ].map((act, idx) => (
                    <button
                      key={idx}
                      disabled={consoleIsRunning}
                      onClick={act.click}
                      className={`text-left p-2.5 rounded-xl border transition-all flex flex-col space-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed ${act.theme}`}
                    >
                      <span className="text-[10px] font-sans font-bold flex items-center justify-between">
                        {act.label}
                      </span>
                      <span className="font-mono text-[7.5px] uppercase tracking-wide opacity-80 mt-0.5">
                        {act.sub}
                      </span>
                    </button>
                  ))}
                </div>

                {/* THE MULTI VIDEO CREATION PACK BUBBLE */}
                <div className="bg-gradient-to-r from-purple-950/20 to-black/40 border border-purple-500/20 rounded-2xl p-4 font-mono text-[10px]">
                  <h5 className="font-bold text-slate-200 mb-2 flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5 text-purple-400" /> ZERO-BUDGET CREATIVE PACK
                  </h5>
                  <p className="text-slate-400 text-[8px] uppercase tracking-wider mb-3">
                    Bypass standard administrative queues and package 4 vital placements matching zero-cost billing records:
                  </p>
                  <ul className="space-y-1 text-[7.5px] text-slate-500 list-disc list-inside">
                    <li>16:9 Looping Homepage Hero</li>
                    <li>15s High Conversion Promo cut</li>
                    <li>9:16 portrait Reel/Shorts</li>
                    <li>4-sec Transition Logo Bumper</li>
                  </ul>
                </div>

              </div>

            </div>
          )}

          {activeSection === 'SYNC_LIFECYCLE' && (
            <div className="flex flex-col space-y-6">
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* INTERACTIVE COMPANION GUIDES & VIDEOS SYNC */}
                <div className="bg-black/40 border border-slate-800 rounded-2xl p-5 backdrop-blur-md">
                  <h4 className="text-white text-xs font-mono uppercase tracking-widest font-black text-glow-cyan mb-3 flex items-center gap-2">
                    <Link className="w-4 h-4 text-cyan-400" /> GUIDES & VIDEOS CO-DIRECTOR
                  </h4>
                  <p className="text-[9px] font-mono text-slate-500 uppercase tracking-widest mb-4">
                    Assigned videos map directly to relevant tutorial guidelines inside Workspace:
                  </p>

                  <div className="space-y-3">
                    {[
                      { id: "welcome-to-nexorax", name: "Welcome to NexoraX", icon: Home, desc: "Primary operator orientation clip" },
                      { id: "nexus-search-guide", name: "Nexus Search indexing guide", icon: Play, desc: "Sitemap adjustments & competitor diagnostics" },
                      { id: "nexus-hosting-guide", name: "Nexus Hosting Command Portal", icon: Cpu, desc: "Dedicated bare-metal node operations instruction" }
                    ].map(gd => {
                      const GIcon = gd.icon;
                      const mappedVid = videosList.find(v => v.guideId === gd.id);
                      return (
                        <div key={gd.id} className={`p-3 rounded-xl border flex flex-col space-y-2 relative transition-all ${mappedVid ? 'border-purple-500/20 bg-purple-950/5' : 'border-slate-800 bg-[#020306]/40'}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              <div className="p-1.5 h-7 w-7 rounded bg-slate-800/50 flex items-center justify-center border border-slate-750">
                                <GIcon className="w-3.5 h-3.5 text-cyan-400 animate-pulse" />
                              </div>
                              <div className="flex flex-col">
                                <span className="text-white text-[11px] font-sans font-bold">{gd.name}</span>
                                <span className="text-[8px] font-mono text-slate-400 mt-0.5 font-black">{gd.id}</span>
                              </div>
                            </div>
                            {mappedVid && (
                              <span className="px-2 py-0.5 rounded border border-emerald-500/25 bg-emerald-500/10 text-[8px] font-mono text-emerald-400 font-extrabold uppercase">
                                {mappedVid.status}
                              </span>
                            )}
                          </div>
                          
                          <p className="font-mono text-[8px] text-slate-500 leading-relaxed uppercase">{gd.desc}</p>

                          <div className="flex items-center justify-between border-t border-slate-800/40 pt-2 mt-1">
                            <span className="font-mono text-[7px] text-slate-500 uppercase">
                              {mappedVid ? `MAP: ${mappedVid.id} (${mappedVid.duration})` : 'MAP: NOT CONFIGURED'}
                            </span>
                            
                            {mappedVid && (
                              <button
                                onClick={() => syncGuideVideoToPlayer(gd.id)}
                                className="bg-cyan-500 hover:bg-cyan-400 text-black text-[8px] font-mono font-bold tracking-widest px-2.5 py-1 rounded transition-colors uppercase"
                              >
                                LOAD & PLAY DIRECTLY
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* DYNAMIC DUPLICATE WATERMARK SCANNERS */}
                <div className="bg-black/40 border border-slate-805/85 rounded-2xl p-5 relative backdrop-blur-md">
                  <h4 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-bold mb-4 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-purple-400" /> CONTENT DUPLICATE CHECK
                  </h4>
                  
                  <p className="text-[9px] font-mono leading-relaxed text-slate-400 uppercase tracking-widest mb-4">
                    Scans sitemap datasets and title tags of previous builds to guarantee zero copy duplicates, protecting organic search presence.
                  </p>

                  <div className="bg-[#020306] border border-slate-800 p-4 rounded-xl flex flex-col space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[9px] text-slate-400 font-bold uppercase">&#9670; PROPOSED TITLE TAG:</span>
                      <span className="text-[8.5px] font-mono text-slate-500 uppercase font-black">HASH: SHA-256</span>
                    </div>
                    <div className="bg-black p-2.5 border border-slate-850 rounded font-mono text-xs text-white break-all">
                      {formVideoTitle || "[Awaiting title input parameters]"}
                    </div>

                    <button
                      onClick={triggerVerifyDuplicate}
                      className="bg-purple-500/10 border border-purple-500/30 hover:bg-purple-950/40 text-purple-400 py-2.5 rounded-lg text-xs font-mono font-bold uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
                    >
                      <Activity className="w-4 h-4 animate-pulse" /> INITIATE CATALOG INTEGRITY CHECK
                    </button>
                  </div>

                  {duplicateWarning && (
                    <div className={`p-4 rounded-xl border font-mono text-[9.5px] leading-relaxed mt-4 ${duplicateWarning.includes('SUCCESS') ? 'bg-emerald-950/25 border-emerald-500/40 text-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.1)]' : 'bg-amber-955/25 border-amber-500/40 text-amber-400 shadow-[0_0_10px_rgba(234,179,8,0.1)] animate-shake'}`}>
                      {duplicateWarning}
                    </div>
                  )}
                </div>

                {/* THE HUMAN AUDIT APPROVAL DECK */}
                <div className="bg-black/40 border border-slate-800 rounded-2xl p-5 backdrop-blur-md">
                  <h4 className="text-white text-xs font-mono uppercase tracking-widest font-black mb-3 text-glow-cyan flex items-center gap-2">
                    <CheckSquare className="w-4 h-4 text-emerald-400" /> HUMAN APPROVAL DECK
                  </h4>
                  <p className="text-[9.5px] font-mono leading-relaxed text-slate-400 uppercase tracking-widest mb-4">
                    Verification guidelines enforced strictly for "User" roles. Admin review is final before rendering outbound publishing.
                  </p>

                  <div className="border border-slate-800/80 bg-black/50 p-4 rounded-xl space-y-3 font-mono text-[9px]">
                    <div className="flex items-center gap-1.5 text-amber-400 font-extrabold uppercase">
                      <AlertCircle className="w-4 h-4" /> AUDIT CONDITION 01:
                    </div>
                    <p className="text-slate-400 leading-relaxed uppercase">
                      All published promo videos must integrate the corporate support phone number (<span className="text-white font-bold">{brandAssets.phone}</span>) and absolute zero duplicates.
                    </p>
                    <div className="border-t border-slate-800/60 pt-3 flex gap-2">
                      <button
                        onClick={() => {
                          setVideosList(videosList.map(v => v.status === 'Needs Review' ? { ...v, status: 'Approved' } : v));
                          setDuplicateWarning("SUCCESS: All pending and review jobs cleared of warnings and updated to APPROVED status code.");
                          setAuditLogs([
                            { id: "log-app-" + Date.now(), time: new Date().toTimeString().split(' ')[0], user: "Director Admin", action: "Bulk human review: Approved all review draft segments.", status: "SUCCESS" },
                            ...auditLogs
                          ]);
                        }}
                        className="bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-900/40 text-emerald-400 px-3 py-1.5 rounded uppercase font-bold text-[8.5px] tracking-widest"
                      >
                        APPROVE PENDING DRAFTS
                      </button>
                    </div>
                  </div>
                </div>

              </div>

              {/* UNIFIED STATUS BOARD & LIFECYCLE PIPELINE */}
              <div className="bg-[#04060d]/60 border border-slate-800 rounded-xl p-5">
                <h4 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-black mb-5 border-b border-slate-800 pb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-cyan-400 animate-pulse" /> LIFECYCLE PIPELINE (8 PIPELINE STAGES)
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-8 gap-3">
                  {[
                    { id: "Draft", label: "DRAFT SOURCE", col: "border-slate-800 text-slate-400 bg-slate-900/5" },
                    { id: "Script Ready", label: "SCRIPT COMPILE", col: "border-purple-900/40 text-purple-400 bg-purple-950/5" },
                    { id: "Waiting for Render", label: "AWAIT RENDER", col: "border-blue-900/40 text-blue-400 bg-blue-950/5" },
                    { id: "Rendering", label: "ACTIVE RENDERING", col: "border-cyan-500/30 text-cyan-400 bg-cyan-950/10 animate-pulse" },
                    { id: "Needs Review", label: "NEEDS MANAGER REVIEW", col: "border-amber-500/30 text-amber-400 bg-amber-500/5" },
                    { id: "Approved", label: "APPROVED STATE", col: "border-emerald-500/30 text-emerald-400 bg-emerald-500/5" },
                    { id: "Published", label: "PUBLISHED ON SITEMAP", col: "border-teal-500/40 text-teal-300 bg-teal-500/5 font-black shadow-[inset_0_0_10px_rgba(20,184,166,0.05)]" },
                    { id: "Failed", label: "FAILURE HALT", col: "border-rose-900/40 text-rose-400 bg-rose-950/5" }
                  ].map(step => {
                    const stepItems = videosList.filter(v => v.status === step.id);
                    return (
                      <div key={step.id} className={`p-2.5 rounded-lg border flex flex-col space-y-2 h-[260px] ${step.col}`}>
                        <div className="flex items-center justify-between border-b border-slate-800/40 pb-1.5">
                          <span className="font-mono text-[8px] font-extrabold uppercase tracking-wider truncate">{step.label}</span>
                          <span className="text-[10px] font-mono font-bold">({stepItems.length})</span>
                        </div>

                        <div className="flex-1 overflow-y-auto space-y-2 pr-0.5">
                          {stepItems.map(vi => (
                            <div 
                              key={vi.id} 
                              onClick={() => {
                                setActiveVideoObject(vi);
                                // Sync values to inputs
                                setFormVideoTitle(vi.title);
                                setFormVideoType(vi.type);
                                setFormScenePrompt(vi.scenePrompt);
                                setDuplicateWarning(null);
                              }}
                              className={`p-2 rounded border cursor-pointer hover:border-cyan-500/50 transition-colors bg-black/60 flex flex-col space-y-1 ${activeVideoObject?.id === vi.id ? 'border-cyan-500 ring-1 ring-cyan-500/20' : 'border-slate-850'}`}
                            >
                              <span className="font-sans font-bold text-[9px] text-white line-clamp-1 truncate">{vi.title}</span>
                              <span className="font-mono text-[7px] text-slate-400 select-all">{vi.id}</span>
                              
                              <div className="flex items-center justify-between mt-1 pt-1 border-t border-slate-800/40">
                                <span className="font-mono text-[7px] text-cyan-500 font-extrabold">{vi.duration}</span>
                                <span className="font-mono text-[6px] text-slate-500">{vi.aspectRatio}</span>
                              </div>

                              {/* Simple Status Toggles directly inside pipeline to easily inspect */}
                              <div className="flex gap-1.5 justify-end mt-1">
                                {vi.status !== 'Published' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setVideosList(videosList.map(v => v.id === vi.id ? { ...v, status: 'Published' } : v));
                                      setAuditLogs([{ id: "log-st-" + Date.now(), time: new Date().toTimeString().split(' ')[0], user: "Director Admin", action: `Outbound Published video: "${vi.title}" to standard sitemaps.`, status: "SUCCESS" }, ...auditLogs]);
                                    }}
                                    className="bg-teal-500 text-black text-[7px] font-mono font-extrabold px-1 rounded uppercase tracking-tighter"
                                  >
                                    PUBLISH
                                  </button>
                                )}
                                {vi.status === 'Needs Review' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setVideosList(videosList.map(v => v.id === vi.id ? { ...v, status: 'Approved' } : v));
                                      setAuditLogs([{ id: "log-ap-" + Date.now(), time: new Date().toTimeString().split(' ')[0], user: "Lead Director", action: `Approved video: "${vi.title}"`, status: "SUCCESS" }, ...auditLogs]);
                                    }}
                                    className="bg-emerald-500/20 text-emerald-400 text-[6.5px] font-mono px-1 rounded border border-emerald-500/30 uppercase"
                                  >
                                    APPROVE
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* DYNAMIC OUTPUT VIEWER (SCRIPT/SRT/THUMBNAILS DISPLAY) */}
              {activeVideoObject && (
                <div className="bg-black/60 border border-slate-800 rounded-2xl p-6 backdrop-blur-md">
                  <h4 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-black border-b border-slate-800 pb-3 mb-5 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-cyan-400" /> GENERATED OUTPUT DIRECTORY DETECTIVE: {activeVideoObject.title}
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Column 1: Dialogue Script structure */}
                    <div className="p-4 bg-[#020306] rounded-xl border border-slate-850 flex flex-col space-y-2">
                      <span className="font-mono text-[9px] text-cyan-400 uppercase tracking-widest font-black block border-b border-slate-800 pb-1.5">PROMO SCRIPT DOCUMENTATION</span>
                      {activeVideoObject.script ? (
                        <div className="space-y-2.5 font-mono text-[9px] text-slate-400">
                          <div><strong className="text-slate-200 uppercase">[HANGER HOOK]:</strong> {activeVideoObject.script.hook}</div>
                          <div><strong className="text-slate-200 uppercase">[SYSTEM PROBLEM]:</strong> {activeVideoObject.script.problem}</div>
                          <div><strong className="text-slate-200 uppercase">[AI RESOLUTION]:</strong> {activeVideoObject.script.solution}</div>
                          <div><strong className="text-slate-200 uppercase">[PROFIT BENEFIT]:</strong> {activeVideoObject.script.benefit}</div>
                          <div><strong className="text-slate-200 uppercase">[CALL DIRECT]:</strong> {activeVideoObject.script.cta}</div>
                        </div>
                      ) : (
                        <span className="font-mono text-[8px] text-slate-600">NO DIALOGUE REGISTERED FOR THE SELECTED COMPONENT.</span>
                      )}
                    </div>

                    {/* Column 2: Voiceover script details */}
                    <div className="p-4 bg-[#020306] rounded-xl border border-slate-850 flex flex-col space-y-2">
                      <span className="font-mono text-[9px] text-purple-400 uppercase tracking-widest font-black block border-b border-slate-800 pb-1.5">VOICEOVER DIALOG DIALOGUE ({brandAssets.voiceoverTone})</span>
                      <p className="font-mono text-[10px] text-white italic leading-relaxed">
                        {activeVideoObject.voiceoverScript || "[AMBIENT LOOP COMPONENT - ACOUSTIC VOICEOVER DISABLED]"}
                      </p>
                      {activeVideoObject.voiceoverScript && (
                        <div className="bg-slate-800/10 border border-slate-850 p-2 rounded text-[7.5px] font-mono text-slate-400 mt-auto uppercase">
                          &#9656; Synthesized dialog matching Wednesday billing clearance guidelines completely (SC887095)
                        </div>
                      )}
                    </div>

                    {/* Column 3: Subtitles and Thumbnail vectors */}
                    <div className="p-4 bg-[#020306] rounded-xl border border-slate-855 flex flex-col space-y-4">
                      <div className="flex flex-col space-y-1.5">
                        <span className="font-mono text-[9px] text-amber-400 uppercase tracking-widest font-black block border-b border-slate-805 pb-1.5">SUBTITLE SEGMENTS TEXT (SRT FORMAT)</span>
                        <p className="font-mono text-[9.5px] text-slate-300 whitespace-pre-line leading-loose">
                          {activeVideoObject.subtitleText ? `00:00:01,000 --> 00:00:03,500\n${activeVideoObject.title.toUpperCase()}\n\n00:00:04,100 --> 00:00:12,000\n${activeVideoObject.subtitleText}` : "[AMBIENT LOOP CAPTIONS PREEMPTED]"}
                        </p>
                      </div>

                      {activeVideoObject.thumbnailPrompt && (
                        <div className="flex flex-col space-y-1.5 pt-3 border-t border-slate-800/40">
                          <span className="font-mono text-[9px] text-cyan-400 uppercase tracking-widest font-black block pb-1 border-b border-slate-800">THUMBNAIL IMAGE PROMPT MATRIX</span>
                          <span className="text-[10px] font-sans font-bold text-slate-20/90 text-slate-200">LABEL: "{activeVideoObject.thumbnailText || 'N/A'}"</span>
                          <p className="font-mono text-[8px] text-slate-500 italic">
                            PROMPT: "{activeVideoObject.thumbnailPrompt}"
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            </div>
          )}

          {activeSection === 'DISTRIBUTION' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {[
                { id: "homepage-hero", title: "HOMEPAGE HERO HUB", desc: "Target placement: Looping central landing hero", icon: Home, badge: "text-cyan-400 bg-cyan-500/10" },
                { id: "company-promo", title: "PROMO ARCHIVE", desc: "Corporate introducing presentation logs", icon: Play, badge: "text-purple-400 bg-purple-500/10" },
                { id: "social-media", title: "SOCIAL MEDIA CHANNELS", desc: "Viral Tik-Tok / Reel vertical formats", icon: Share2, badge: "text-pink-400 bg-pink-500/10" },
                { id: "product-promo", title: "MARKETPLACE EXHIBIT", desc: "Marketplace products spotlight clips", icon: ShoppingBag, badge: "text-amber-400 bg-amber-500/10" },
                { id: "ad-creative", title: "CAMPAIGN AD LABS", desc: "Agressive direct response layouts", icon: Megaphone, badge: "text-rose-400 bg-rose-500/10" }
              ].map(dest => {
                const DestIcon = dest.icon;
                const destItems = videosList.filter(v => v.category === dest.id);
                return (
                  <div key={dest.id} className="bg-black/40 border border-slate-800 rounded-2xl p-4.5 flex flex-col space-y-3">
                    <span className={`px-2 py-1 rounded self-start font-mono text-[8px] font-black tracking-widest uppercase ${dest.badge}`}>
                      {dest.title}
                    </span>
                    <p className="font-mono text-[8px] text-slate-500 uppercase tracking-wide leading-relaxed">
                      {dest.desc}
                    </p>

                    <div className="border-t border-slate-800/60 pt-3 flex-1 overflow-y-auto space-y-2 h-[260px]">
                      {destItems.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-10 opacity-30 text-slate-600 font-mono text-[8px] uppercase">
                          <DestIcon className="w-8 h-8 mb-2" /> No catalog assets
                        </div>
                      ) : (
                        destItems.map(di => (
                          <div 
                            key={di.id} 
                            onClick={() => {
                              setActiveVideoObject(di);
                              setDuplicateWarning(null);
                            }}
                            className={`p-2 rounded border border-slate-850/80 bg-black/60 cursor-pointer hover:border-cyan-500/40 transition-colors flex flex-col space-y-1 ${activeVideoObject?.id === di.id ? 'border-cyan-500 ring-1 ring-cyan-500/20' : ''}`}
                          >
                            <span className="font-sans font-bold text-[9px] text-white line-clamp-1">{di.title}</span>
                            <div className="flex justify-between items-center text-[7px] font-mono text-slate-500 mt-1">
                              <span>VIEWS: {di.views}</span>
                              <span className="uppercase font-extrabold">{di.status}</span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'VAULT' && (
        <div className="bg-[#04060d] border border-slate-800/80 rounded-2xl p-8 relative overflow-hidden backdrop-blur-sm min-h-[600px]">
           <h3 className="text-white text-sm font-mono uppercase tracking-[0.3em] font-bold mb-8 border-b border-slate-800 pb-4">
             SECURE STORAGE // CINEMATIC VAULT
           </h3>
           
           {vault.length === 0 ? (
             <div className="flex flex-col items-center justify-center py-20 text-slate-600">
               <Archive className="w-16 h-16 mb-4 opacity-30" />
               <p className="font-mono text-xs uppercase tracking-widest">Vault is currently empty.</p>
               <p className="font-mono text-[10px] uppercase tracking-widest mt-2">Generate videos to store them here.</p>
             </div>
           ) : (
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
               {vault.map(item => (
                 <div key={item.id} className="bg-black/60 border border-slate-700/80 rounded-xl overflow-hidden hover:border-cyan-500/50 transition-colors flex flex-col group">
                   <div className="aspect-video bg-[#050914] relative group/video">
                     <video src={item.url} controls className="w-full h-full object-cover relative z-10" />
                     <div className="absolute top-2 left-2 z-20 pointer-events-none flex items-center space-x-2 bg-black/50 backdrop-blur-md px-1.5 py-1 rounded border border-white/10 opacity-100 shadow-lg scale-75 origin-top-left">
                       <img src="/logo.png" alt="Nexorax Logo" className="w-4 h-4 object-contain" />
                       <div className="flex flex-col">
                         <span className="text-white font-sans font-black tracking-[0.2em] text-[8px] uppercase leading-none drop-shadow-lg">NEXORAX</span>
                         <span className="text-cyan-400 font-sans font-bold tracking-[0.3em] text-[5px] uppercase leading-none drop-shadow-lg mt-[1px]">DIGITAL</span>
                       </div>
                     </div>
                   </div>
                   <div className="p-4 flex flex-col flex-1">
                     <p className="text-xs text-slate-400 font-mono uppercase tracking-widest mb-1 shadow-[0_0_20px_rgba(0,0,0,0.5)]">PROMPT LOG</p>
                     <p className="text-[10px] text-slate-300 font-mono leading-relaxed line-clamp-3 mb-4 flex-1">
                       {item.prompt}
                     </p>
                     <div className="flex items-center justify-between mt-auto border-t border-slate-800 pt-3">
                       <span className="text-[9px] text-slate-500 font-mono uppercase">{new Date(item.timestamp).toLocaleTimeString()}</span>
                       <div className="flex items-center space-x-3">
                         <button onClick={() => {
                            deleteVideoFromDB(item.id).catch(err => console.error("Error deleting video from DB:", err));
                            setVault(prev => {
                              const remaining = prev.filter(v => v.id !== item.id);
                              if (activeVideoObject?.id === item.id) {
                                if (remaining.length > 0) {
                                  setActiveVideoObject(remaining[0]);
                                  setVideoUrl(remaining[0].url);
                                } else {
                                  setActiveVideoObject(null);
                                  setVideoUrl(null);
                                }
                              }
                              return remaining;
                            });
                          }} className="text-[9px] text-rose-500 hover:text-rose-400 font-mono font-bold uppercase tracking-widest">
                           DELETE
                         </button>
                         <button onClick={() => {
                            const a = document.createElement('a');
                            a.href = item.url;
                            a.download = `render-${item.id}.mp4`;
                            a.click();
                         }} className="text-[9px] text-cyan-400 hover:text-cyan-300 font-mono font-bold uppercase tracking-widest">
                           DOWNLOAD
                         </button>
                       </div>
                     </div>
                   </div>
                 </div>
               ))}
             </div>
           )}
        </div>
      )}

       {activeTab === 'COMPARE' && (
         <div className="bg-[#04060d] border border-slate-800/80 rounded-2xl p-8 relative overflow-hidden backdrop-blur-sm min-h-[600px]">
           <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 border-b border-slate-800 pb-4">
             <div>
               <h2 className="text-white text-sm font-mono uppercase tracking-[0.3em] font-bold">
                 KINETIC COMPARISON MATRIX // SIDE-BY-SIDE
               </h2>
               <p className="text-[10px] text-slate-500 font-mono uppercase tracking-widest mt-1">
                 Analyze rendering presets, diffusion characteristics, and motion interpolation ease curves.
               </p>
             </div>
             {vault.length >= 2 && (
               <button
                 type="button"
                 onClick={() => {
                   const total = vault.length;
                   const indexA = Math.floor(Math.random() * total);
                   let indexB = Math.floor(Math.random() * total);
                   if (indexB === indexA && total > 1) {
                     indexB = (indexA + 1) % total;
                   }
                   setCompareVideoIdA(vault[indexA].id);
                   setCompareVideoIdB(vault[indexB].id);
                   setTuneStatusLog(`COMPARISON SIMULATOR SYNCHRONIZED: ${vault[indexA].id} VS ${vault[indexB].id}`);
                 }}
                 className="mt-3 md:mt-0 text-[10px] bg-slate-900 border border-slate-700/80 text-cyan-400 hover:text-cyan-300 font-mono font-bold uppercase tracking-widest px-3 py-1.5 rounded transition-all flex items-center gap-1.5"
               >
                 <RefreshCw className="w-3.5 h-3.5" /> SHUFFLE COMPARE
               </button>
             )}
           </div>

           {vault.length === 0 ? (
             <div className="flex flex-col items-center justify-center py-20 text-slate-600">
               <AlertTriangle className="w-16 h-16 mb-4 text-amber-500/80" />
               <p className="font-mono text-xs uppercase tracking-widest">History Log Empty</p>
               <p className="font-mono text-[10px] uppercase tracking-widest mt-2 max-w-md text-center">
                 Generate or render videos under the Craft tab first before running cross-comparison analysis.
               </p>
             </div>
           ) : (
             <div className="space-y-8">
               {/* Selection Dropdowns */}
               <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-black/40 border border-slate-800/80 p-5 rounded-xl">
                 {/* Video Selection A */}
                 <div className="flex flex-col space-y-2">
                   <label className="text-[10px] text-cyan-400 font-mono uppercase tracking-widest font-black">
                     RENDER SLOT A (LEFT ANCHOR)
                   </label>
                   <select
                     value={compareVideoIdA}
                     onChange={(e) => setCompareVideoIdA(e.target.value)}
                     className="bg-[#050914] border border-slate-800 text-slate-300 rounded font-mono text-xs p-2.5 outline-none focus:border-cyan-500/50 hover:border-slate-700"
                   >
                     <option value="" disabled>-- SELECT COMPARATIVE VIDEO --</option>
                     {vault.map(v => (
                       <option key={`sel-a-${v.id}`} value={v.id}>
                         [{v.id}] {v.prompt.substring(0, 50)}... ({new Date(v.timestamp).toLocaleTimeString()})
                       </option>
                     ))}
                   </select>
                 </div>

                 {/* Video Selection B */}
                 <div className="flex flex-col space-y-2">
                   <label className="text-[10px] text-cyan-400 font-mono uppercase tracking-widest font-black">
                     RENDER SLOT B (RIGHT ANCHOR)
                   </label>
                   <select
                     value={compareVideoIdB}
                     onChange={(e) => setCompareVideoIdB(e.target.value)}
                     className="bg-[#050914] border border-slate-800 text-slate-300 rounded font-mono text-xs p-2.5 outline-none focus:border-cyan-500/50 hover:border-slate-700"
                   >
                     <option value="" disabled>-- SELECT COMPARATIVE VIDEO --</option>
                     {vault.map(v => (
                       <option key={`sel-b-${v.id}`} value={v.id}>
                         [{v.id}] {v.prompt.substring(0, 50)}... ({new Date(v.timestamp).toLocaleTimeString()})
                       </option>
                     ))}
                   </select>
                 </div>
               </div>

               {/* Dual Video Players */}
               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 {/* Left Side Video Card */}
                 <div className="bg-[#050914] border border-slate-800 rounded-xl overflow-hidden flex flex-col">
                   <div className="bg-slate-900/60 border-b border-slate-800 p-3 flex justify-between items-center">
                     <span className="text-[10px] text-cyan-400 font-mono uppercase tracking-widest font-black">
                       NODE-A // {compareVideoIdA || "NOT SELECTED"}
                     </span>
                     <span className="text-[8px] text-slate-500 font-mono uppercase">
                       {compareVideoIdA ? new Date(vault.find(v => v.id === compareVideoIdA)?.timestamp || '').toLocaleString() : '-'}
                     </span>
                   </div>
                   <div className="aspect-video bg-black relative flex items-center justify-center">
                     {compareVideoIdA ? (
                       <video
                         id="compare-player-a"
                         src={vault.find(v => v.id === compareVideoIdA)?.url}
                         className="w-full h-full object-cover"
                         controls
                         preload="auto"
                       />
                     ) : (
                       <div className="text-slate-600 font-mono text-[10px] uppercase text-center p-8">
                         Select a video for Slot A above
                       </div>
                     )}
                   </div>
                   <div className="p-4 flex-1 flex flex-col justify-between">
                     <div>
                       <span className="text-[8px] text-slate-500 font-mono uppercase tracking-widest block mb-1">PROMPT MATRICES</span>
                       <p className="text-[10px] text-slate-350 font-mono leading-relaxed bg-black/30 border border-slate-900 p-2.5 rounded">
                         {vault.find(v => v.id === compareVideoIdA)?.prompt || 'No video selected.'}
                       </p>
                     </div>
                   </div>
                 </div>

                 {/* Right Side Video Card */}
                 <div className="bg-[#050914] border border-slate-800 rounded-xl overflow-hidden flex flex-col">
                   <div className="bg-slate-900/60 border-b border-slate-800 p-3 flex justify-between items-center">
                     <span className="text-[10px] text-cyan-400 font-mono uppercase tracking-widest font-black">
                       NODE-B // {compareVideoIdB || "NOT SELECTED"}
                     </span>
                     <span className="text-[8px] text-slate-500 font-mono uppercase">
                       {compareVideoIdB ? new Date(vault.find(v => v.id === compareVideoIdB)?.timestamp || '').toLocaleString() : '-'}
                     </span>
                   </div>
                   <div className="aspect-video bg-black relative flex items-center justify-center">
                     {compareVideoIdB ? (
                       <video
                         id="compare-player-b"
                         src={vault.find(v => v.id === compareVideoIdB)?.url}
                         className="w-full h-full object-cover"
                         controls
                         preload="auto"
                       />
                     ) : (
                       <div className="text-slate-600 font-mono text-[10px] uppercase text-center p-8">
                         Select a video for Slot B above
                       </div>
                     )}
                   </div>
                   <div className="p-4 flex-1 flex flex-col justify-between">
                     <div>
                       <span className="text-[8px] text-slate-500 font-mono uppercase tracking-widest block mb-1">PROMPT MATRICES</span>
                       <p className="text-[10px] text-slate-350 font-mono leading-relaxed bg-black/30 border border-slate-900 p-2.5 rounded">
                         {vault.find(v => v.id === compareVideoIdB)?.prompt || 'No video selected.'}
                       </p>
                     </div>
                   </div>
                 </div>
               </div>

               {/* Synchronize Playback Trigger */}
               {compareVideoIdA && compareVideoIdB && (
                 <div className="flex justify-center">
                   <button
                     type="button"
                     onClick={() => {
                       const playerA = document.getElementById('compare-player-a') as HTMLVideoElement;
                       const playerB = document.getElementById('compare-player-b') as HTMLVideoElement;
                       if (playerA && playerB) {
                         playerA.currentTime = 0;
                         playerB.currentTime = 0;
                         playerA.play().catch(() => {});
                         playerB.play().catch(() => {});
                         setTuneStatusLog("PLAYBACK INJECTOR: DUAL SYNC MODE SUCCESSFUL.");
                       }
                     }}
                     className="text-[10px] font-mono font-bold uppercase tracking-widest bg-cyan-500 hover:bg-cyan-400 text-black px-6 py-2.5 rounded-lg shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all flex items-center gap-2"
                   >
                     <Play className="w-4 h-4 fill-black text-black" style={{ animation: 'none' }} /> SYNC & PLAY BOTH VIDEOS
                   </button>
                 </div>
               )}

               {/* Configuration Metrics Comparison Grid */}
               <div className="bg-[#03050c] border border-slate-850 rounded-xl p-6">
                 <h4 className="text-xs text-white font-mono uppercase tracking-widest mb-6 font-bold pb-2 border-b border-slate-900">
                   NEURAL PRESET CONFIGURATION COMPARATIVE GRID
                 </h4>
                 
                 <div className="space-y-4">
                   {/* Parameter Row: Diffusion Steps */}
                   <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center border-b border-slate-900 pb-4">
                     <div className="flex flex-col">
                       <span className="text-[10px] text-slate-400 font-mono uppercase font-bold tracking-wider">DIFFUSION STEPS</span>
                       <span className="text-[8px] text-slate-600 font-mono uppercase">Render steps for image depth output</span>
                     </div>
                     <div className="text-xs font-mono text-cyan-400 text-left md:text-center">
                       <span className="text-slate-500 text-[9px] block md:hidden">SLOT A</span>
                       <span className="text-sm font-bold">
                         {compareVideoIdA ? (vault.find(v => v.id === compareVideoIdA)?.modelConfig?.diffusionSteps || 80) : '-'}
                       </span>
                     </div>
                     <div className="text-xs font-mono text-fuchsia-400 text-left md:text-right">
                       <span className="text-slate-500 text-[9px] block md:hidden">SLOT B</span>
                       <span className="text-sm font-bold">
                         {compareVideoIdB ? (vault.find(v => v.id === compareVideoIdB)?.modelConfig?.diffusionSteps || 80) : '-'}
                       </span>
                     </div>
                   </div>

                   {/* Parameter Row: Temporal Consistency */}
                   <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center border-b border-slate-900 pb-4">
                     <div className="flex flex-col">
                       <span className="text-[10px] text-slate-400 font-mono uppercase font-bold tracking-wider">TEMPORAL CONSISTENCY</span>
                       <span className="text-[8px] text-slate-600 font-mono uppercase">Inter-frame stability retention percentage</span>
                     </div>
                     <div className="text-xs font-mono text-cyan-400 text-left md:text-center">
                       <span className="text-slate-500 text-[9px] block md:hidden">SLOT A</span>
                       <span className="text-sm font-bold">
                         {compareVideoIdA ? `${vault.find(v => v.id === compareVideoIdA)?.modelConfig?.temporalConsistency || 90}%` : '-'}
                       </span>
                     </div>
                     <div className="text-xs font-mono text-fuchsia-400 text-left md:text-right">
                       <span className="text-slate-500 text-[9px] block md:hidden">SLOT B</span>
                       <span className="text-sm font-bold">
                         {compareVideoIdB ? `${vault.find(v => v.id === compareVideoIdB)?.modelConfig?.temporalConsistency || 90}%` : '-'}
                       </span>
                     </div>
                   </div>

                   {/* Parameter Row: Orbit Speed */}
                   <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center border-b border-slate-900 pb-4">
                     <div className="flex flex-col">
                       <span className="text-[10px] text-slate-400 font-mono uppercase font-bold tracking-wider">ORBIT SPEED</span>
                       <span className="text-[8px] text-slate-600 font-mono uppercase">Speed value for kinetic rotation acceleration</span>
                     </div>
                     <div className="text-xs font-mono text-cyan-400 text-left md:text-center">
                       <span className="text-slate-500 text-[9px] block md:hidden">SLOT A</span>
                       <span className="text-sm font-bold">
                         {compareVideoIdA ? `${vault.find(v => v.id === compareVideoIdA)?.modelConfig?.orbitSpeed || '3.0'}` : '-'}
                       </span>
                     </div>
                     <div className="text-xs font-mono text-fuchsia-400 text-left md:text-right">
                       <span className="text-slate-500 text-[9px] block md:hidden">SLOT B</span>
                       <span className="text-sm font-bold">
                         {compareVideoIdB ? `${vault.find(v => v.id === compareVideoIdB)?.modelConfig?.orbitSpeed || '3.0'}` : '-'}
                       </span>
                     </div>
                   </div>

                   {/* Parameter Row: Particle Density */}
                   <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center border-b border-slate-900 pb-4">
                     <div className="flex flex-col">
                       <span className="text-[10px] text-slate-400 font-mono uppercase font-bold tracking-wider">PARTICLE DENSITY</span>
                       <span className="text-[8px] text-slate-600 font-mono uppercase">Simulation matrix complexity count</span>
                     </div>
                     <div className="text-xs font-mono text-cyan-400 text-left md:text-center">
                       <span className="text-slate-500 text-[9px] block md:hidden">SLOT A</span>
                       <span className="text-sm font-bold">
                         {compareVideoIdA ? `${vault.find(v => v.id === compareVideoIdA)?.modelConfig?.particleDensity || 200}` : '-'}
                       </span>
                     </div>
                     <div className="text-xs font-mono text-fuchsia-400 text-left md:text-right">
                       <span className="text-slate-500 text-[9px] block md:hidden">SLOT B</span>
                       <span className="text-sm font-bold">
                         {compareVideoIdB ? `${vault.find(v => v.id === compareVideoIdB)?.modelConfig?.particleDensity || 200}` : '-'}
                       </span>
                     </div>
                   </div>

                   {/* Parameter Row: Matrix Accent Color */}
                   <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center border-b border-slate-900 pb-4">
                     <div className="flex flex-col">
                       <span className="text-[10px] text-slate-400 font-mono uppercase font-bold tracking-wider">MATRIX COLOR SPECIFICATION</span>
                       <span className="text-[8px] text-slate-600 font-mono uppercase">Visual matrix accent color mapping</span>
                     </div>
                     <div className="text-xs font-mono text-cyan-400 text-left md:text-center">
                       <span className="text-slate-500 text-[9px] block md:hidden">SLOT A</span>
                       <div className="flex items-center md:justify-center space-x-2">
                         {compareVideoIdA ? (
                           <>
                             <span className="text-xs font-bold uppercase">{vault.find(v => v.id === compareVideoIdA)?.modelConfig?.matrixColor || 'cyan'}</span>
                             <span className="w-2.5 h-2.5 rounded-full" style={{ 
                               backgroundColor: vault.find(v => v.id === compareVideoIdA)?.modelConfig?.matrixColor === 'rose' ? '#f43f5e' :
                                               vault.find(v => v.id === compareVideoIdA)?.modelConfig?.matrixColor === 'amber' ? '#f59e0b' :
                                               vault.find(v => v.id === compareVideoIdA)?.modelConfig?.matrixColor === 'cyan' ? '#06b6d4' : '#14b8a6'
                             }} />
                           </>
                         ) : '-'}
                       </div>
                     </div>
                     <div className="text-xs font-mono text-fuchsia-400 text-left md:text-right">
                       <span className="text-slate-500 text-[9px] block md:hidden">SLOT B</span>
                       <div className="flex items-center md:justify-end space-x-2">
                         {compareVideoIdB ? (
                           <>
                             <span className="text-xs font-bold uppercase">{vault.find(v => v.id === compareVideoIdB)?.modelConfig?.matrixColor || 'cyan'}</span>
                             <span className="w-2.5 h-2.5 rounded-full" style={{ 
                               backgroundColor: vault.find(v => v.id === compareVideoIdB)?.modelConfig?.matrixColor === 'rose' ? '#f43f5e' :
                                               vault.find(v => v.id === compareVideoIdB)?.modelConfig?.matrixColor === 'amber' ? '#f59e0b' :
                                               vault.find(v => v.id === compareVideoIdB)?.modelConfig?.matrixColor === 'cyan' ? '#06b6d4' : '#14b8a6'
                             }} />
                           </>
                         ) : '-'}
                       </div>
                     </div>
                   </div>

                   {/* Parameter Row: FRAME INTERPOLATION SETTING */}
                   <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                     <div className="flex flex-col">
                       <span className="text-[10px] text-slate-400 font-mono uppercase font-bold tracking-wider">FRAME INTERPOLATION (EASING)</span>
                       <span className="text-[8px] text-slate-600 font-mono uppercase">Velocity curve of in-between frames</span>
                     </div>
                     <div className="text-xs font-mono text-cyan-400 text-left md:text-center">
                       <span className="text-slate-500 text-[9px] block md:hidden">SLOT A</span>
                       <span className="text-xs font-black uppercase tracking-widest bg-cyan-500/10 border border-cyan-500/20 px-2 py-0.5 rounded">
                         {compareVideoIdA ? (vault.find(v => v.id === compareVideoIdA)?.modelConfig?.easeSetting?.replace('_', ' ') || 'LINEAR') : '-'}
                       </span>
                     </div>
                     <div className="text-xs font-mono text-fuchsia-400 text-left md:text-right">
                       <span className="text-slate-500 text-[9px] block md:hidden">SLOT B</span>
                       <span className="text-xs font-black uppercase tracking-widest bg-fuchsia-500/10 border border-fuchsia-500/20 px-2 py-0.5 rounded">
                         {compareVideoIdB ? (vault.find(v => v.id === compareVideoIdB)?.modelConfig?.easeSetting?.replace('_', ' ') || 'LINEAR') : '-'}
                       </span>
                     </div>
                   </div>
                 </div>
               </div>

               {/* Detailed Differential Analysis Alert */}
               {compareVideoIdA && compareVideoIdB && (
                 <div className="bg-slate-950/60 border border-slate-800 p-6 rounded-xl relative overflow-hidden">
                   <div className="absolute top-0 right-0 p-1 text-[7px] font-mono text-cyan-500 uppercase bg-cyan-500/10 border-b border-l border-cyan-500/20">
                     DIFFERENTIATION REPORT // CO-ENGINE MATE
                   </div>
                   <h5 className="text-[11px] text-white font-mono uppercase tracking-widest font-black mb-3">
                     INTERPOLATION FLOW INTERPRETATION & PHYSICS
                   </h5>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-[10px] text-slate-400 font-mono leading-relaxed">
                     <div className="bg-slate-900/30 p-3 rounded border border-slate-800/50">
                       <span className="text-cyan-400 font-bold block mb-1">SLOT A FLOW ANALYSIS:</span>
                       {vault.find(v => v.id === compareVideoIdA)?.modelConfig?.easeSetting === 'LINEAR' ? (
                         "Linear flow utilizes constant step interpolation between major keyframes. Perfect for flythrough digital coordinate tunnels, wireframes, and continuous infinite camera tracking shots where velocity must not change."
                       ) : vault.find(v => v.id === compareVideoIdA)?.modelConfig?.easeSetting === 'EASE_IN' ? (
                         "Ease In starting curve decelerates early frames and smoothly ramps kinetic acceleration as the video progresses. Maximizes focus on initiation effects."
                       ) : vault.find(v => v.id === compareVideoIdA)?.modelConfig?.easeSetting === 'EASE_OUT' ? (
                         "Ease Out exit curve injects energetic starting motion and decelerates smoothly at the end. Recommended for star collapses and nebula implosion shots."
                       ) : (
                         "Ease In Out is a symmetrical premium curve blending slow initiation, quick progression, and a smooth cinematic finish. Minimizes visual friction and sudden micro-shakes."
                       )}
                     </div>
                     <div className="bg-slate-900/30 p-3 rounded border border-slate-800/50">
                       <span className="text-fuchsia-400 font-bold block mb-1">SLOT B FLOW ANALYSIS:</span>
                       {vault.find(v => v.id === compareVideoIdB)?.modelConfig?.easeSetting === 'LINEAR' ? (
                         "Linear flow utilizes constant step interpolation between major keyframes. Perfect for flythrough digital coordinate tunnels, wireframes, and continuous infinite camera tracking shots where velocity must not change."
                       ) : vault.find(v => v.id === compareVideoIdB)?.modelConfig?.easeSetting === 'EASE_IN' ? (
                         "Ease In starting curve decelerates early frames and smoothly ramps kinetic acceleration as the video progresses. Maximizes focus on initiation effects."
                       ) : vault.find(v => v.id === compareVideoIdB)?.modelConfig?.easeSetting === 'EASE_OUT' ? (
                         "Ease Out exit curve injects energetic starting motion and decelerates smoothly at the end. Recommended for star collapses and nebula implosion shots."
                       ) : (
                         "Ease In Out is a symmetrical premium curve blending slow initiation, quick progression, and a smooth cinematic finish. Minimizes visual friction and sudden micro-shakes."
                       )}
                     </div>
                   </div>
                 </div>
               )}
             </div>
           )}
         </div>
       )}
    </div>
  );
}
